import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent as trackClarityEvent } from '../lib/clarity';
import {
  Sparkles, BookOpen, GraduationCap, Lightbulb, FileText, Clock, Home,
  Sun, MessageSquare, Theater, AlignLeft, Settings, ChevronDown, ChevronRight,
  Check, Layers, Target, Zap, Presentation, Library, Wand2, Plus, RefreshCw,
  ArrowDown, ArrowUp, Save, Send, Eye, Download, Filter, Users, User,
  CalendarDays, Calendar, Gauge, ShieldCheck, Brain, ListChecks, PlayCircle, FolderOpen,
  BarChart3, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Activity,
  LineChart, Repeat, ArrowUpRight, FileBarChart, Flame, Loader2, Lock, Crown, Eraser,
  Camera, Bot,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '../components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useApp } from '../contexts/AppContext';
import { toast } from 'sonner';
import { TASK_TYPE_CONFIG, TASK_TYPES_LIST } from '../config/taskTypeConfig';
import { useBoscoCaptureAccess } from '../features/bosco-capture/hooks/useBoscoCaptureAccess';
import BoscoCaptureUpsell from '../features/bosco-capture/BoscoCaptureUpsell';
import {
  BOARDS, TEACHING_GOALS, PLANNING_SCOPES, TEACHING_OBJECTIVES,
  TEACHING_STYLES, DURATIONS, getChaptersForBoard,
} from '../config/curriculumConfig';
import { cn } from '../lib/utils';
import { contentEngineApi } from '../services/contentEngineApi';
import { trackSubscriptionStarted } from '../lib/analytics';
import { FloatingAskBosco } from '../components/copilot/FloatingAskBosco';
import { peekReaderPrefill, takeReaderPrefill } from '../components/library/readerPrefill';
import { WorkspaceHero } from '../components/generator/WorkspaceHero';
import { PlanChangePreview, CheckoutResult } from '../components/billing/CheckoutStagePanel';
import {
  WizardStepper, WizardPanelHeading, WizardNav, ReviewSummary, Disclosure, SelectionCard,
} from '../components/generator/GeneratorWizard';

// ─────────────────────────────────────────────────────────────
// Static UI Data
// ─────────────────────────────────────────────────────────────
const CLASSES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const SUBJECTS = [
  'Mathematics', 'Science', 'English', 'Hindi', 'Social Studies',
  'Physics', 'Chemistry', 'Biology', 'History', 'Geography', 'Computer Science',
];
const QUESTION_TYPES = ['MCQ', 'Short Answer', 'Long Answer', 'Case Study', 'Fill in the Blanks', 'True/False'];
const ASSIGNMENT_TYPES = ['Classwork', 'Homework', 'Project', 'Assessment'];

const TASK_ICON_MAP = {
  FileText, Clock, GraduationCap, Home, Sun, MessageSquare, Theater,
  BookOpen, AlignLeft, Settings,
};

const GOAL_ICON_MAP = { BookOpen, GraduationCap, Lightbulb, Sparkles };

const STEPS = [
  { id: 1, key: 'plan', label: 'Plan', sub: 'Task & Goal', icon: Target },
  { id: 2, key: 'create', label: 'Create', sub: 'Curriculum Source', icon: Layers },
  { id: 3, key: 'deliver', label: 'Deliver', sub: 'Configure', icon: Settings },
  { id: 4, key: 'assign', label: 'Assign', sub: 'Review & Share', icon: Send },
  { id: 5, key: 'evaluate', label: 'Evaluate', sub: 'Outcomes', icon: BarChart3 },
  { id: 6, key: 'improve', label: 'Improve', sub: 'Iterate & Remediate', icon: Repeat },
];

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────
export default function TeacherAssignmentsPage() {
  const { currentUser, currentSchool } = useApp();
  const schoolId = currentSchool?.id || currentSchool?.schoolId || currentUser?.schoolId;

  // Mode
  const [mode, setMode] = useState(schoolId ? 'school' : 'individual');

  // Stepper
  const [currentStep, setCurrentStep] = useState(1);
  // Tracks the highest step the school user has stepped INTO via Next. Steps
  // beyond this remain locked/greyed; steps below this that aren't the current
  // one render in AMBER (visited-but-came-back state).
  const [maxVisitedStep, setMaxVisitedStep] = useState(1);
  // Reader hand-off: read the target generator during render (child sections consume the prefill in their effects first)
  const [generator, setGenerator] = useState(() => peekReaderPrefill()?.generator || 'quick');
  const readerGeneratorRef = useRef(peekReaderPrefill()?.generator || null);

  // Step 1
  const [selectedType, setSelectedType] = useState('');
  const [teachingGoal, setTeachingGoal] = useState('');
  const [planningScope, setPlanningScope] = useState('single_class');

  // Step 1 — Source + doc types
  const [sourceMode, setSourceMode] = useState('topic'); // 'library' | 'upload' | 'topic'
  const [libraryBooks, setLibraryBooks] = useState([]);
  // Library-panel local filters (decoupled from Step 2 curriculum)
  const [libBoard, setLibBoard] = useState('');
  const [libClass, setLibClass] = useState('');
  const [libSubject, setLibSubject] = useState('');
  const [librarySearch, setLibrarySearch] = useState('');
  const [selectedBookId, setSelectedBookId] = useState('');
  const [selectedChapterNumber, setSelectedChapterNumber] = useState('');
  const [uploadedBook, setUploadedBook] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docTypes, setDocTypes] = useState(['summary']);
  const [docTypeCaps, setDocTypeCaps] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  // Class + Section list for Assign step (school mode)
  const [classSections, setClassSections] = useState([]);

  // Step 2 — Curriculum
  const [board, setBoard] = useState('');
  const [classId, setClassId] = useState('');
  const [subject, setSubject] = useState('');
  const [chapter, setChapter] = useState('');
  const [syllabusVersion] = useState('2024-25'); // hidden by default

  // Step 3 — Config
  const [openSection, setOpenSection] = useState('objective');
  const [teachingObjective, setTeachingObjective] = useState('');
  const [difficulty, setDifficulty] = useState([30, 50, 20]); // easy, med, hard
  const [questionTypes, setQuestionTypes] = useState(['MCQ', 'Short Answer']);
  const [answerKey, setAnswerKey] = useState(true);
  const [variations, setVariations] = useState(false);
  const [lessonKitMode, setLessonKitMode] = useState(false);
  const [teachingStyle, setTeachingStyle] = useState('');
  const [duration, setDuration] = useState(45);
  // Iter 73 — Fix #6: explicit "No. of questions" + "Max marks" inputs.
  // Empty string = "let the backend pick the default for the difficulty bucket".
  const [numQuestions, setNumQuestions] = useState('');
  const [maxMarks, setMaxMarks] = useState('');
  // Iter 74 — Section B (assessment doctype) variants of the same inputs.
  const [sbNumQuestions, setSbNumQuestions] = useState('');
  const [sbMaxMarks, setSbMaxMarks] = useState('');

  // CASCADE: Task Type → auto-select Documents to Create
  useEffect(() => {
    if (!selectedType) return;
    const map = {
      'Assignment': ['assessment'],
      'Test': ['assessment'],
      'Exam': ['assessment'],
      'Homework': ['assessment'],
      'Holiday Homework': ['assessment'],
      'Summary': ['summary'],
      'Book / Chapter Task': ['summary', 'lesson_plan'],
      'Play / Skit': ['lesson_plan', 'teaching_flow'],
      'Debate': ['lesson_plan'],
      'Custom': [],
    };
    const next = map[selectedType];
    if (next && next.length && docTypes.length === 0) setDocTypes(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  // CASCADE: Teaching Goal → auto-tune difficulty distribution
  useEffect(() => {
    if (!teachingGoal) return;
    const map = {
      exam_preparation: [20, 40, 40],
      concept_reinforcement: [55, 35, 10],
      syllabus_completion: [30, 50, 20],
      activity_engagement: [45, 40, 15],
    };
    const next = map[teachingGoal];
    if (next) setDifficulty(next);
  }, [teachingGoal]);

  // CASCADE: Planning Scope → Full Lesson Kit cascade lives below (after `access` is declared).
  // Step 4 — Assignment
  const [assignmentType, setAssignmentType] = useState('Homework');
  const [dueDate, setDueDate] = useState('');
  const [assignTarget, setAssignTarget] = useState('');
  const [reviewTab, setReviewTab] = useState('content');

  // Step 5 — Evaluate
  const [evalTab, setEvalTab] = useState('overview');
  const [evaluation, setEvaluation] = useState(null);
  const [evaluationLoading, setEvaluationLoading] = useState(false);

  // Step 6 — Improve
  const [improveActions, setImproveActions] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);

  // Backend-wired state
  const [access, setAccess] = useState(null);
  const [remoteChapters, setRemoteChapters] = useState([]);
  const [generatedAsset, setGeneratedAsset] = useState(null);
  const [lessonKit, setLessonKit] = useState(null);
  const [assignmentId, setAssignmentId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isIterating, setIsIterating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  // Strip locked doc types whenever access changes (e.g., free user can't keep PPT selected)
  useEffect(() => {
    if (!access?.docTypeAccess) return;
    setDocTypes((prev) => prev.filter((d) => access.docTypeAccess[d] !== false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access?.tier]);

  // CASCADE: Planning Scope → Full Lesson Kit auto-selects the 4 non-PPT doc types
  // (respecting the user's tier — both `maxDocsPerBatch` and per-doc-type access).
  // PPT is intentionally EXCLUDED here — PPT Slides is a standalone scope (different
  // generation pipeline; can't be batched with docs).
  useEffect(() => {
    if (planningScope === 'lesson_kit' && docTypeCaps) {
      const nonPpt = ['summary', 'lesson_plan', 'assessment', 'teaching_flow'];
      // Filter by per-doc-type access first
      const allowedByTier = nonPpt.filter((d) => access?.docTypeAccess?.[d] !== false);
      // Then cap by the per-batch limit
      const allowed = allowedByTier.slice(0, docTypeCaps.maxDocsPerBatch || 4);
      setDocTypes(allowed);
      setLessonKitMode(true);
      const skipped = nonPpt.filter((d) => !allowed.includes(d));
      if (skipped.length > 0) {
        toast.info(
          `Lesson Kit pre-selected ${allowed.length} of 4 docs for your ${access?.tier?.toUpperCase() || 'current'} plan.`,
          { description: `${skipped.join(', ')} require a higher plan.`, duration: 5000 }
        );
      }
    } else if (planningScope === 'single_class') {
      setLessonKitMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planningScope, docTypeCaps, access?.tier]);

  // Fetch tier / access on mount + on global ce:access-refresh
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      contentEngineApi
        .getAccess()
        .then((a) => !cancelled && setAccess(a))
        .catch(() => {});
      contentEngineApi
        .listDocTypes()
        .then((d) => !cancelled && setDocTypeCaps(d))
        .catch(() => {});
    };
    load();
    const onRefresh = () => load();
    window.addEventListener('ce:access-refresh', onRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener('ce:access-refresh', onRefresh);
    };
  }, []);

  // Fetch library books — uses LIBRARY-LOCAL filters so the user can browse all 33+ titles
  // independently of the Step 2 curriculum selection.
  useEffect(() => {
    let cancelled = false;
    if (sourceMode !== 'library') return;
    contentEngineApi
      .listBooks(libBoard || undefined, libClass || undefined, libSubject || undefined)
      .then((r) => !cancelled && setLibraryBooks(r.books || []))
      .catch(() => !cancelled && setLibraryBooks([]));
    return () => { cancelled = true; };
  }, [sourceMode, libBoard, libClass, libSubject]);

  // Fetch class+section list for the school (Assign step)
  useEffect(() => {
    let cancelled = false;
    if (mode !== 'school' || !schoolId) {
      setClassSections([]);
      return;
    }
    contentEngineApi
      .listClassSections()
      .then((r) => !cancelled && setClassSections(r.items || []))
      .catch(() => !cancelled && setClassSections([]));
    return () => { cancelled = true; };
  }, [mode, schoolId]);

  // Fetch chapters from backend when board+class+subject set
  useEffect(() => {
    let cancelled = false;
    if (!board || !classId || !subject) {
      setRemoteChapters([]);
      return;
    }
    contentEngineApi
      .listChapters(board, classId, subject)
      .then((r) => !cancelled && setRemoteChapters(r.chapters || []))
      .catch(() => !cancelled && setRemoteChapters([]));
    return () => { cancelled = true; };
  }, [board, classId, subject]);

  // Auto-load evaluation + recommendations when Step 5/6 opens with an assignmentId
  useEffect(() => {
    if (currentStep === 5 && assignmentId) {
      setEvaluationLoading(true);
      contentEngineApi
        .getEvaluation(assignmentId)
        .then(setEvaluation)
        .catch((e) => toast.error(`Evaluation load failed: ${e.message}`))
        .finally(() => setEvaluationLoading(false));
    }
    if (currentStep === 6 && assignmentId) {
      setRecommendationsLoading(true);
      contentEngineApi
        .getRecommendations(assignmentId)
        .then((r) => setRecommendations(r.recommendations || []))
        .catch((e) => toast.error(`Recommendations failed: ${e.message}`))
        .finally(() => setRecommendationsLoading(false));
    }
  }, [currentStep, assignmentId]);

  // Derived
  const availableChapters = useMemo(() => {
    // Prefer backend-seeded chapters; fall back to local config
    if (remoteChapters.length > 0) return remoteChapters;
    return board && subject && classId ? getChaptersForBoard(board, subject, classId) : [];
  }, [remoteChapters, board, subject, classId]);

  const boardLabel = useMemo(
    () => BOARDS.find((b) => b.id === board)?.label || '',
    [board]
  );

  const filteredStyles = useMemo(() => {
    if (!board) return TEACHING_STYLES;
    return TEACHING_STYLES.filter(
      (s) => s.boards.length === 0 || s.boards.includes(board)
    );
  }, [board]);

  // Actions
  const canProceed = (step) => {
    // Individual users have access to Step 1 only — Steps 2-6 are gated
    if (mode === 'individual' && step >= 1) return false;
    // Step 1 → Step 2: always allowed in school mode (the two Step 1 generators are independent
    // and the user may want to set curriculum context in Step 2 directly)
    if (step === 1) return true;
    if (step === 2) return Boolean(board && classId && subject);
    if (step === 3) return Boolean(teachingObjective);
    return true;
  };

  const goNext = () => {
    if (!canProceed(currentStep)) {
      toast.error('Complete required fields to continue.');
      return;
    }
    setCurrentStep((s) => {
      const next = Math.min(STEPS.length, s + 1);
      setMaxVisitedStep((m) => Math.max(m, next));
      return next;
    });
  };
  const goBack = () => setCurrentStep((s) => Math.max(1, s - 1));

  // Build the CE API payload from current wizard state
  const buildGenerationPayload = () => ({
    taskType: selectedType,
    teachingGoal,
    planningScope,
    board,
    classId,
    subject,
    chapter: chapter || null,
    teachingObjective,
    difficultyDistribution: difficulty,
    questionTypes,
    answerKey,
    variations,
    teachingStyle: teachingStyle || null,
    duration,
    // Iter 73 — Fix #6: explicit count + marks (parsed to int when set).
    numQuestions: numQuestions ? parseInt(numQuestions, 10) || null : null,
    maxMarks: maxMarks ? parseInt(maxMarks, 10) || null : null,
  });

  const handleUpgradeRequired = (err) => {
    const detail = err?.detail || {};
    toast.error(detail.message || 'Upgrade required to use this feature.', {
      description: detail.suggestedTier
        ? `Suggested plan: ${detail.suggestedTier.toUpperCase()}`
        : undefined,
      action: {
        label: 'Upgrade',
        onClick: () => window.dispatchEvent(new CustomEvent('ce:upgrade-required', { detail })),
      },
    });
  };

  const handleUploadBook = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const book = await contentEngineApi.uploadBook(file, { board, classId, subject });
      setUploadedBook(book);
      setSelectedBookId(book.bookId);
      if (book.chapters?.length) setSelectedChapterNumber(String(book.chapters[0].number));
      toast.success(`Uploaded • ${book.chapters?.length || 0} chapter${book.chapters?.length === 1 ? '' : 's'} detected.`);
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleBatchGenerate = async () => {
    if (!docTypes.length) {
      toast.error('Select at least one document type.');
      return;
    }
    if (!board || !classId || !subject) {
      toast.error('Complete board, class, and subject before generating.');
      return;
    }
    if (sourceMode !== 'topic' && !selectedBookId) {
      toast.error('Pick a book first (or switch source to Topic).');
      return;
    }
    setIsGenerating(true);
    try {
      const source = {
        mode: sourceMode,
        bookId: sourceMode === 'topic' ? null : selectedBookId,
        chapterNumber: sourceMode === 'topic' ? null : (parseInt(selectedChapterNumber, 10) || null),
        topic: chapter || selectedType || subject,
        board, classId, subject, duration,
        teachingStyle: teachingStyle || null,
        // Iter 74 — Fix #2: pass question count + max marks into Section B config
        // so the backend `assessment` generator honours them strictly.
        config: {
          numQuestions: sbNumQuestions ? parseInt(sbNumQuestions, 10) || null : null,
          maxMarks: sbMaxMarks ? parseInt(sbMaxMarks, 10) || null : null,
        },
      };
      const batch = await contentEngineApi.generateMulti({ docTypes, source });
      setBatchResult(batch);
      // Wire primary asset for Step 5/6 flow — pass docType so preview can render correctly
      const primary = batch.results[0];
      if (primary) {
        setGeneratedAsset({
          assetId: primary.assetId,
          docType: primary.docType,
          title: primary.title,
          content: primary.content,
        });
      }
      toast.success(`Generated ${batch.results.length} document${batch.results.length === 1 ? '' : 's'}.`);
      // Iter 76 — Clarity events per docType produced.
      try {
        (batch.results || []).forEach((r) => {
          const dt = (r.docType || '').toLowerCase();
          const ev = dt.includes('lesson') ? 'lesson_plan_generated'
                   : dt.includes('worksheet') ? 'worksheet_generated'
                   : dt.includes('assessment') ? 'question_paper_generated'
                   : dt.includes('summary') ? 'summary_generated'
                   : dt.includes('ppt') ? 'ppt_generated'
                   : 'section_b_generated';
          trackClarityEvent(ev, { docType: r.docType });
        });
      } catch { /* noop */ }
      // Auto-jump to Step 4 (Assign) only for school users — individuals stay on Step 1
      if (mode === 'school') setCurrentStep(4);
      contentEngineApi.getAccess().then(setAccess).catch(() => {});
    } catch (err) {
      if (err.code === 'UPGRADE_REQUIRED') handleUpgradeRequired(err);
      else toast.error(`Batch generation failed: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerate = async () => {
    if (!canProceed(1) || !canProceed(2) || !canProceed(3)) {
      toast.error('Complete all steps before generating.');
      return;
    }
    // Library/Upload OR multi-doc selection → use batch generator
    if (sourceMode !== 'topic' || docTypes.length > 1 || (docTypes.length === 1 && !['Assignment', 'Test', 'Homework'].includes(selectedType))) {
      await handleBatchGenerate();
      return;
    }
    setIsGenerating(true);
    try {
      if (lessonKitMode || planningScope === 'lesson_kit') {
        const kit = await contentEngineApi.generateLessonKit(buildGenerationPayload());
        setLessonKit(kit);
        setGeneratedAsset({
          assetId: kit.components?.contentAssetId,
          title: kit.title,
          content: { _lessonKit: true },
        });
        trackClarityEvent('lesson_plan_generated', { taskType: selectedType });
        toast.success('Lesson kit generated.');
      } else {
        const asset = await contentEngineApi.generate(buildGenerationPayload());
        setGeneratedAsset(asset);
        // Map taskType → Clarity event (kept narrow so SA dashboard chart is meaningful)
        const t = (selectedType || '').toLowerCase();
        const ev = t.includes('lesson') ? 'lesson_plan_generated'
                 : t.includes('worksheet') ? 'worksheet_generated'
                 : (t.includes('test') || t.includes('exam') || t.includes('question')) ? 'question_paper_generated'
                 : 'section_a_generated';
        trackClarityEvent(ev, { taskType: selectedType });
        toast.success('Content generated.');
      }
      contentEngineApi.getAccess().then(setAccess).catch(() => {});
    } catch (err) {
      if (err.code === 'UPGRADE_REQUIRED') handleUpgradeRequired(err);
      else toast.error(`Generation failed: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAssign = async () => {
    if (!generatedAsset && !lessonKit) {
      toast.error('Generate content before assigning.');
      return;
    }
    if (!assignTarget) {
      toast.error('Please enter a class or group to assign to.');
      return;
    }
    setIsAssigning(true);
    try {
      const payload = {
        assetId: generatedAsset?.assetId || null,
        kitId: lessonKit?.kitId || null,
        assignTo: assignTarget,
        assignmentType,
        dueDate: dueDate || null,
      };
      const a = await contentEngineApi.createAssignment(payload);
      setAssignmentId(a.assignmentId);
      const count = a.studentCount || 0;
      toast.success(
        count > 0
          ? `Assigned to ${count} student${count === 1 ? '' : 's'} in ${a.assignTo}.`
          : 'Assignment created.'
      );
    } catch (err) {
      toast.error(`Assignment failed: ${err.message}`);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleMicroAction = async (id) => {
    if (!generatedAsset?.assetId) {
      toast.error('Generate content first to iterate on it.');
      return;
    }
    const actionMap = {
      simplify: 'simplify',
      complexity: 'increase_complexity',
      'add-questions': 'add_questions',
      'convert-format': 'convert_format',
    };
    const action = actionMap[id] || id;
    setIsIterating(true);
    try {
      const res = await contentEngineApi.iterate({
        assetId: generatedAsset.assetId,
        action,
        extraCount: action === 'add_questions' ? 5 : undefined,
        targetFormat: action === 'convert_format' ? 'MCQ' : undefined,
      });
      const newAsset = {
        assetId: res.newAssetId,
        docType: res.docType || generatedAsset.docType,
        title: res.title || `${generatedAsset.title} (${action.replace('_', ' ')})`,
        content: res.content,
      };
      setGeneratedAsset(newAsset);
      // Also patch the active row inside batchResult so the Step 4 preview swaps to the iterated version
      setBatchResult((prev) => {
        if (!prev?.results?.length) return prev;
        const idx = prev.results.findIndex((r) => r.assetId === generatedAsset.assetId);
        if (idx === -1) return prev;
        const next = { ...prev, results: prev.results.slice() };
        next.results[idx] = {
          ...next.results[idx],
          assetId: newAsset.assetId,
          title: newAsset.title,
          content: newAsset.content,
        };
        return next;
      });
      toast.success(`Applied: ${action.replace('_', ' ')}`);
    } catch (err) {
      if (err.code === 'UPGRADE_REQUIRED') handleUpgradeRequired(err);
      else toast.error(`Iteration failed: ${err.message}`);
    } finally {
      setIsIterating(false);
    }
  };

  const handleApplyImprovements = async () => {
    if (!assignmentId) {
      toast.info('No assignment selected. Closing loop in preview mode.');
      return;
    }
    setIsApplying(true);
    try {
      await contentEngineApi.applyImprovements({
        assignmentId,
        actionIds: improveActions,
        feedbackToggles: improveActions.filter((x) =>
          ['auto-tune-difficulty', 'update-curriculum-pace', 'save-as-template'].includes(x)
        ),
      });
      toast.success(
        improveActions.length > 0
          ? `${improveActions.length} improvement${improveActions.length > 1 ? 's' : ''} applied.`
          : 'Loop closed.'
      );
    } catch (err) {
      toast.error(`Apply failed: ${err.message}`);
    } finally {
      setIsApplying(false);
    }
  };

  const openGenerator = (g) => {
    setGenerator(g);
    setCurrentStep(1);
    setTimeout(() => document.querySelector('[data-testid="generator-mode-switch"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  // Library reader hand-off → open the matching generator.
  useEffect(() => {
    if (readerGeneratorRef.current) openGenerator(readerGeneratorRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleImprove = (id) => {
    setImproveActions((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div data-testid="content-engine-page" className="space-y-6 pb-24">
      {/* Workspace hero — greeting, plan/mode controls & quick actions */}
      <WorkspaceHero
        name={currentUser?.name}
        actions={<div className="contents" data-testid="engine-header-actions">
          <TierBadge access={access} />
          <ModeToggle mode={mode} setMode={setMode} schoolId={schoolId} />
          <TeachingAssetsMenu mode={mode} />
        </div>}
        quickActions={[
          { id: 'quick-task', label: 'Quick task', hint: 'Worksheet, test, homework…', icon: Zap, onClick: () => openGenerator('quick') },
          { id: 'lesson-kit', label: 'Plan a lesson kit', hint: 'Goal-based multi-document plan', icon: Target, onClick: () => openGenerator('plan') },
          { id: 'ask-bosco', label: 'Ask BOSCO AI', hint: 'Your teaching co-pilot', icon: Bot, onClick: () => document.querySelector('[data-testid="ask-bosco-fab"]')?.click() },
        ]}
      />
      <span className="sr-only" data-testid="engine-page-title">AI Teacher Content Engine</span>

      {/* Stepper */}
      <Stepper
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        canProceed={canProceed}
        mode={mode}
        maxVisitedStep={maxVisitedStep}
        tier={access?.tier}
      />

      {/* Main grid */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6">
        {/* Left — Step Content */}
        <div className="space-y-6 min-w-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              {currentStep === 1 && (
                <Step1Sections
                  access={access}
                  docTypeCaps={docTypeCaps}
                  mode={mode}
                  isGenerating={isGenerating}
                  setIsGenerating={setIsGenerating}
                  setBatchResult={setBatchResult}
                  setGeneratedAsset={setGeneratedAsset}
                  setCurrentStep={setCurrentStep}
                  refreshAccess={() => contentEngineApi.getAccess().then(setAccess).catch(() => {})}
                  generator={generator}
                  setGenerator={setGenerator}
                />
              )}
              {currentStep === 2 && (
                <Step2Create
                  mode={mode}
                  board={board}
                  setBoard={setBoard}
                  classId={classId}
                  setClassId={setClassId}
                  subject={subject}
                  setSubject={setSubject}
                  chapter={chapter}
                  setChapter={setChapter}
                  availableChapters={availableChapters}
                  boardLabel={boardLabel}
                />
              )}
              {currentStep === 3 && (
                <Step3Deliver
                  openSection={openSection}
                  setOpenSection={setOpenSection}
                  board={board}
                  boardLabel={boardLabel}
                  classId={classId}
                  subject={subject}
                  chapter={chapter}
                  syllabusVersion={syllabusVersion}
                  teachingObjective={teachingObjective}
                  setTeachingObjective={setTeachingObjective}
                  difficulty={difficulty}
                  setDifficulty={setDifficulty}
                  questionTypes={questionTypes}
                  setQuestionTypes={setQuestionTypes}
                  answerKey={answerKey}
                  setAnswerKey={setAnswerKey}
                  variations={variations}
                  setVariations={setVariations}
                  lessonKitMode={lessonKitMode}
                  setLessonKitMode={setLessonKitMode}
                  teachingStyle={teachingStyle}
                  setTeachingStyle={setTeachingStyle}
                  filteredStyles={filteredStyles}
                  duration={duration}
                  setDuration={setDuration}
                  numQuestions={numQuestions}
                  setNumQuestions={setNumQuestions}
                  maxMarks={maxMarks}
                  setMaxMarks={setMaxMarks}
                  selectedType={selectedType}
                />
              )}
              {currentStep === 4 && (
                <Step4Assign
                  lessonKitMode={lessonKitMode}
                  reviewTab={reviewTab}
                  setReviewTab={setReviewTab}
                  selectedType={selectedType}
                  boardLabel={boardLabel}
                  classId={classId}
                  subject={subject}
                  chapter={chapter}
                  assignmentType={assignmentType}
                  setAssignmentType={setAssignmentType}
                  dueDate={dueDate}
                  setDueDate={setDueDate}
                  assignTarget={assignTarget}
                  setAssignTarget={setAssignTarget}
                  classSections={classSections}
                  mode={mode}
                  schoolId={schoolId}
                  handleMicroAction={handleMicroAction}
                  generatedAsset={generatedAsset}
                  lessonKit={lessonKit}
                  assignmentId={assignmentId}
                  isGenerating={isGenerating}
                  isAssigning={isAssigning}
                  isIterating={isIterating}
                  onAssign={handleAssign}
                  batchResult={batchResult}
                />
              )}
              {currentStep === 5 && (
                <Step5Evaluate
                  evalTab={evalTab}
                  setEvalTab={setEvalTab}
                  boardLabel={boardLabel}
                  classId={classId}
                  subject={subject}
                  chapter={chapter}
                  selectedType={selectedType}
                  evaluation={evaluation}
                  evaluationLoading={evaluationLoading}
                  assignmentId={assignmentId}
                />
              )}
              {currentStep === 6 && (
                <Step6Improve
                  improveActions={improveActions}
                  toggleImprove={toggleImprove}
                  boardLabel={boardLabel}
                  classId={classId}
                  subject={subject}
                  chapter={chapter}
                  recommendations={recommendations}
                  recommendationsLoading={recommendationsLoading}
                  assignmentId={assignmentId}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Right — Smart Insight Panel + Teaching Co-Pilot */}
        <div className="space-y-4 min-w-0">
          <SmartInsightPanel
            boardLabel={boardLabel}
            difficulty={difficulty}
            duration={duration}
            teachingObjective={teachingObjective}
            lessonKitMode={lessonKitMode}
            questionTypes={questionTypes}
            mode={mode}
          />
          <FloatingAskBosco
            access={access}
            context={{
              classId,
              subject,
              curriculum: boardLabel,
              topic: chapter,
            }}
          />
        </div>
      </div>

      {/* Bottom Action Bar — school workflow only (individual mode uses the in-card wizard) */}
      {mode !== 'individual' && <ActionBar
        currentStep={currentStep}
        totalSteps={STEPS.length}
        goBack={goBack}
        goNext={goNext}
        handleGenerate={handleGenerate}
        handleApplyImprovements={handleApplyImprovements}
        improveCount={improveActions.length}
        isGenerating={isGenerating}
        isApplying={isApplying}
        hasGenerated={Boolean(generatedAsset || lessonKit)}
        mode={mode}
      />}

      {/* Global upgrade dialog — opens on 402 UPGRADE_REQUIRED */}
      <UpgradeDialog />
      <ManageSubscriptionDialog />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Mode Toggle
// ─────────────────────────────────────────────────────────────
function ModeToggle({ mode, setMode, schoolId }) {
  // v96 bug-fix — School users must NOT be able to switch to Individual
  // mode; the Individual button is disabled when the user belongs to a
  // school. Individual users still see the School button disabled by
  // its own `!schoolId` guard.
  const options = [
    { id: 'school', label: 'School Mode', icon: Users, disabled: !schoolId },
    { id: 'individual', label: 'Individual Mode', icon: User, disabled: Boolean(schoolId) },
  ];
  return (
    <div
      data-testid="mode-toggle"
      className="inline-flex p-1 rounded-xl border border-white/10 bg-white/[0.06] backdrop-blur"
    >
      {options.map((opt) => {
        const Icon = opt.icon;
        const active = mode === opt.id;
        return (
          <button
            key={opt.id}
            data-testid={`mode-toggle-${opt.id}`}
            onClick={() => !opt.disabled && setMode(opt.id)}
            disabled={opt.disabled}
            className={cn(
              'relative px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-[background-color,color] duration-200 flex items-center gap-2',
              active
                ? 'bg-white text-ae-navy shadow-sm'
                : 'text-slate-300 hover:text-white',
              opt.disabled && 'opacity-40 cursor-not-allowed'
            )}
          >
            <Icon className="h-4 w-4" />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Teaching Assets Menu (popover)
// ─────────────────────────────────────────────────────────────
function TeachingAssetsMenu({ mode = 'school' }) {
  const [counts, setCounts] = useState({ saved: 0, kits: 0, templates: 0, schedules: 0 });
  const [open, setOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null); // 'saved' | 'kits' | 'templates' | 'schedules'

  useEffect(() => {
    if (!open) return;
    contentEngineApi
      .popoverCounts()
      .then(setCounts)
      .catch(() => {});
    if (mode === 'school') {
      contentEngineApi
        .listSchedules()
        .then((r) => setCounts((c) => ({ ...c, schedules: (r?.items || []).length })))
        .catch(() => {});
    }
  }, [open, mode]);

  const groups = [
    { id: 'saved', label: 'Saved Content', icon: Save, count: counts.saved },
    { id: 'kits', label: 'Lesson Kits', icon: Layers, count: counts.kits },
    { id: 'templates', label: 'Templates', icon: FolderOpen, count: counts.templates },
    ...(mode === 'school'
      ? [{ id: 'schedules', label: 'My Schedules', icon: Calendar, count: counts.schedules }]
      : []),
  ];
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="gap-2 bg-white/[0.06] dark:bg-white/[0.06] border-white/15 text-white hover:bg-white/15 hover:text-white hover:border-ae-cyan/40"
            data-testid="teaching-assets-trigger"
          >
            <Library className="h-4 w-4" />
            Teaching Assets
            <ChevronDown className="h-4 w-4 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-2" data-testid="teaching-assets-popover">
          <div className="px-2 py-1.5 text-xs uppercase tracking-wide text-muted-foreground">
            Quick Access
          </div>
          <div className="space-y-1">
            {groups.map((g) => {
              const Icon = g.icon;
              return (
                <button
                  key={g.id}
                  data-testid={`asset-${g.id}`}
                  onClick={() => { setDrawerType(g.id); setOpen(false); }}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-sm transition-colors"
                >
                  <span className="flex items-center gap-2.5">
                    <Icon className="h-4 w-4 text-slate-500" />
                    {g.label}
                  </span>
                  <Badge variant="secondary" className="text-[11px]">
                    {g.count}
                  </Badge>
                </button>
              );
            })}
            {mode !== 'school' && (
              <a
                href="/library"
                data-testid="asset-library"
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-sm transition-colors"
              >
                <span className="flex items-center gap-2.5">
                  <BookOpen className="h-4 w-4 text-slate-500" />
                  NCERT &amp; Classic Books Library
                </span>
              </a>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <TeacherAssetsDrawer
        type={drawerType}
        onClose={() => setDrawerType(null)}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// ScheduleCard — single row in the "My Schedules" drawer
// Shows status, next run, runCount, runHistory sparkline, and
// pause/resume/delete controls.
// ─────────────────────────────────────────────────────────────
function ScheduleCard({ schedule, onChange }) {
  const [busy, setBusy] = useState(false);
  const isActive = schedule.status === 'active';
  const day = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][schedule.dayOfWeek] || '—';

  const fmtNext = (() => {
    if (!schedule.nextRunAt) return '—';
    try {
      return new Date(schedule.nextRunAt).toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return schedule.nextRunAt; }
  })();

  const togglePause = async () => {
    setBusy(true);
    try {
      if (isActive) await contentEngineApi.pauseSchedule(schedule.scheduleId);
      else await contentEngineApi.resumeSchedule(schedule.scheduleId);
      toast.success(isActive ? 'Schedule paused' : 'Schedule resumed');
      onChange && (await onChange());
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${schedule.label}" autopilot? Future runs will stop.`)) return;
    setBusy(true);
    try {
      await contentEngineApi.deleteSchedule(schedule.scheduleId);
      toast.success('Schedule deleted');
      onChange && (await onChange());
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`schedule-row-${schedule.scheduleId}`}
      className="p-3 rounded-lg border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
            <span className="text-sm font-semibold truncate">{schedule.label}</span>
          </div>
          <div className="text-[11px] text-muted-foreground truncate mt-0.5">
            {schedule.topic || '—'} · {schedule.assignTo}
          </div>
        </div>
        <Badge
          className={cn(
            'text-[9px] capitalize border-0',
            isActive
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
          )}
        >
          {schedule.status}
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px] mb-2">
        <div>
          <div className="text-muted-foreground">Cadence</div>
          <div className="font-medium">Every {day} · {String(schedule.hour).padStart(2, '0')}:00</div>
        </div>
        <div>
          <div className="text-muted-foreground">Next run</div>
          <div className="font-medium truncate" title={schedule.nextRunAt}>{fmtNext}</div>
        </div>
      </div>

      {/* Sparkline + run count */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <Sparkline history={schedule.runHistory || []} />
        <div className="text-right flex-shrink-0">
          <div
            data-testid={`schedule-runcount-${schedule.scheduleId}`}
            className="text-base font-bold leading-none"
          >
            {schedule.runCount || 0}
          </div>
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">
            {schedule.runCount === 1 ? 'auto-run' : 'auto-runs'}
          </div>
        </div>
      </div>

      {schedule.runCount > 0 && (
        <div className="text-[10px] text-emerald-700 dark:text-emerald-400 mb-2 italic">
          {schedule.runCount} assignment{schedule.runCount === 1 ? '' : 's'} auto-delivered — 0 effort.
        </div>
      )}
      {schedule.lastError && (
        <div className="text-[10px] text-red-700 dark:text-red-400 mb-2 truncate" title={schedule.lastError}>
          Last error: {schedule.lastError}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          data-testid={`schedule-toggle-${schedule.scheduleId}`}
          variant="outline" size="sm"
          onClick={togglePause}
          disabled={busy}
          className="flex-1 h-8 text-[11px]"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : (isActive ? 'Pause' : 'Resume')}
        </Button>
        <Button
          data-testid={`schedule-delete-${schedule.scheduleId}`}
          variant="ghost" size="sm"
          onClick={handleDelete}
          disabled={busy}
          className="h-8 text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
        >
          Delete
        </Button>
      </div>
    </div>
  );
}

// Tiny inline sparkline (no extra deps)
function Sparkline({ history }) {
  const w = 100;
  const h = 24;
  const pts = (history || []).slice(-20);
  if (pts.length === 0) {
    return (
      <div className="text-[10px] text-muted-foreground italic">
        No runs yet — first auto-run on the next scheduled day.
      </div>
    );
  }
  const barW = w / Math.max(pts.length, 1);
  return (
    <svg
      data-testid="schedule-sparkline"
      width={w} height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="flex-1"
      preserveAspectRatio="none"
    >
      {pts.map((p, i) => {
        const success = p.success !== false;
        const barH = success ? h - 4 : Math.max(4, (h - 4) * 0.35);
        const y = h - barH;
        return (
          <rect
            key={i}
            x={i * barW + 1}
            y={y}
            width={Math.max(2, barW - 2)}
            height={barH}
            rx={1}
            className={success ? 'fill-emerald-500' : 'fill-red-400'}
          />
        );
      })}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// Teacher Assets Drawer — opens a side sheet with real items
// ─────────────────────────────────────────────────────────────
function TeacherAssetsDrawer({ type, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const titleMap = {
    saved: { title: 'Saved Content', desc: 'Documents you have generated — view & download.' },
    kits: { title: 'Lesson Kits', desc: 'Bundles combining content, PPT, teaching flow & assessment.' },
    templates: { title: 'Templates', desc: 'Blank starter templates per document type.' },
    schedules: { title: 'My Schedules', desc: 'Active autopilots — recurring weekly fan-outs to your classes.' },
  };

  useEffect(() => {
    if (!type) return;
    let cancelled = false;
    setLoading(true);
    setItems([]);
    const promise =
      type === 'saved' ? contentEngineApi.listAssets()
      : type === 'kits' ? contentEngineApi.listLessonKits()
      : type === 'schedules' ? contentEngineApi.listSchedules()
      : contentEngineApi.listTemplates();
    promise
      .then((d) => {
        if (cancelled) return;
        const list = d.assets || d.kits || d.templates || d.items || [];
        setItems(list);
      })
      .catch((e) => !cancelled && toast.error(e.message || 'Failed to load.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [type]);

  const refreshSchedules = async () => {
    try {
      const d = await contentEngineApi.listSchedules();
      setItems(d.items || []);
    } catch {/* ignore */}
  };

  const renderItem = (item) => {
    if (type === 'schedules') {
      return (
        <ScheduleCard
          key={item.scheduleId}
          schedule={item}
          onChange={refreshSchedules}
        />
      );
    }
    if (type === 'saved') {
      const docType = item.docType || item.type || 'document';
      const formats = docType === 'assessment' ? ['docx', 'pdf']
        : docType === 'ppt' ? ['pptx']
        : ['docx'];
      return (
        <div
          key={item.assetId}
          data-testid={`drawer-item-${item.assetId}`}
          className="p-3 rounded-lg border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 transition-colors"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{item.title || 'Untitled'}</div>
              <div className="text-[11px] text-muted-foreground capitalize">
                {String(docType).replace('_', ' ')} • {item.subject || 'General'}
                {item.classId ? ` • Class ${item.classId}` : ''}
              </div>
            </div>
            <Badge variant="secondary" className="text-[9px] capitalize">{docType}</Badge>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {formats.map((fmt) => (
              <button
                key={fmt}
                data-testid={`drawer-download-${item.assetId}-${fmt}`}
                onClick={async () => {
                  try {
                    await contentEngineApi.downloadAsset(fmt, item.assetId, `${item.title || docType}.${fmt}`);
                  } catch (err) {
                    toast.error(err.message || 'Download failed');
                  }
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-slate-900 text-white hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200 transition-colors"
              >
                <Download className="h-3 w-3" /> .{fmt}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (type === 'kits') {
      return (
        <div
          key={item.kitId}
          data-testid={`drawer-kit-${item.kitId}`}
          className="p-3 rounded-lg border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900"
        >
          <div className="text-sm font-semibold truncate">{item.title || 'Lesson Kit'}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {item.subject || ''}{item.classId ? ` • Class ${item.classId}` : ''}
            {item.components?.slideCount ? ` • ${item.components.slideCount} slides` : ''}
          </div>
        </div>
      );
    }
    // templates
    const tpl = typeof item === 'string' ? item : (item.docType || item.id);
    return (
      <div
        key={tpl}
        data-testid={`drawer-template-${tpl}`}
        className="flex items-center justify-between p-3 rounded-lg border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900"
      >
        <div className="text-sm font-semibold capitalize">{String(tpl).replace('_', ' ')}</div>
        <button
          data-testid={`drawer-template-download-${tpl}`}
          onClick={async () => {
            try {
              await contentEngineApi.downloadTemplate(tpl);
              toast.success('Template downloaded');
            } catch (err) {
              toast.error(err.message || 'Download failed');
            }
          }}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-slate-900 text-white hover:bg-slate-700 dark:bg-white dark:text-slate-900 transition-colors"
        >
          <Download className="h-3 w-3" /> .docx
        </button>
      </div>
    );
  };

  const meta = titleMap[type] || { title: 'Assets', desc: '' };

  return (
    <Sheet open={!!type} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="teacher-assets-drawer"
      >
        <SheetHeader>
          <SheetTitle data-testid="drawer-title">{meta.title}</SheetTitle>
          <SheetDescription>{meta.desc}</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-2.5">
          {loading ? (
            <div className="text-center py-12 text-sm text-muted-foreground flex flex-col items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div
              data-testid="drawer-empty"
              className="text-center py-12 text-sm text-muted-foreground border border-dashed rounded-lg"
            >
              {type === 'saved' && 'No saved content yet. Generate a document to see it here.'}
              {type === 'kits' && 'No lesson kits generated yet.'}
              {type === 'templates' && 'No templates available.'}
            </div>
          ) : (
            items.map((item, idx) => (
              <div key={`${item.assetId || item.kitId || item.docType || item.id || 'item'}-${idx}`}>
                {renderItem(item)}
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────
// Stepper
// ─────────────────────────────────────────────────────────────
function Stepper({ currentStep, setCurrentStep, canProceed, mode, maxVisitedStep = 1, tier }) {
  const isIndividual = mode === 'individual';
  // Individual users only ever interact with Step 1 — render it solo, plus a
  // compact upsell hint, so the page fits on a single screen without the
  // horizontal scroll caused by the other 5 locked steps. School mode
  // is intentionally untouched.
  const visibleSteps = isIndividual ? STEPS.slice(0, 1) : STEPS;
  return (
    <div
      data-testid="engine-stepper"
      className="ae-surface px-4 sm:px-6 py-3.5"
    >
      <div className={cn(
        "flex items-center gap-2 sm:gap-4",
        // Only the school-mode stepper needs horizontal scroll; individual
        // mode renders just one card so it never overflows.
        !isIndividual && "overflow-x-auto"
      )}>
        {visibleSteps.map((step, idx) => {
          const Icon = step.icon;
          const active = currentStep === step.id;
          const completed = currentStep > step.id;
          // School mode: a step is reachable only AFTER the user has stepped INTO it via Next
          // (tracked by maxVisitedStep). Individual mode keeps the single-step gate.
          const reachable = isIndividual
            ? step.id === 1
            : step.id <= maxVisitedStep;
          // Visited-but-came-back state — render in AMBER so the school user can tell at a
          // glance that they've already configured it but are currently on an earlier step.
          const visitedAhead = !isIndividual && !active && step.id > currentStep && step.id <= maxVisitedStep;
          const locked = (isIndividual && step.id !== 1) || (!isIndividual && step.id > maxVisitedStep);
          return (
            <div key={step.id} className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <button
                data-testid={`step-${step.key}`}
                onClick={() => reachable && setCurrentStep(step.id)}
                disabled={!reachable}
                title={locked ? (isIndividual ? 'Available in School Mode' : 'Click Next to unlock this step') : undefined}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-xl transition-[background-color,color,box-shadow] duration-200',
                  active && 'bg-ae-navy dark:bg-white text-white dark:text-ae-navy shadow-soft',
                  !active && completed && 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400',
                  !active && visitedAhead && 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 ring-1 ring-amber-300/60',
                  !active && !completed && !visitedAhead && 'hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-300',
                  !reachable && 'opacity-40 cursor-not-allowed'
                )}
              >
                <div
                  className={cn(
                    'h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0',
                    active && 'bg-ae-blue text-white',
                    !active && completed && 'bg-emerald-100 dark:bg-emerald-900/40',
                    !active && visitedAhead && 'bg-amber-100 dark:bg-amber-900/40',
                    !active && !completed && !visitedAhead && 'bg-slate-100 dark:bg-slate-800'
                  )}
                >
                  {completed ? (
                    <Check className="h-4 w-4" />
                  ) : locked ? (
                    <Lock className="h-3.5 w-3.5" />
                  ) : (
                    <Icon className="h-4 w-4" />
                  )}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-[11px] uppercase tracking-wide opacity-70 leading-none">
                    Step {step.id}
                  </div>
                  <div className="text-sm font-semibold leading-tight mt-0.5">{step.label}</div>
                </div>
              </button>
              {idx < visibleSteps.length - 1 && (
                <div
                  className={cn(
                    'hidden sm:block h-px w-8 lg:w-16 flex-shrink-0 transition-colors',
                    completed
                      ? 'bg-emerald-300 dark:bg-emerald-700'
                      : visitedAhead
                      ? 'bg-amber-300 dark:bg-amber-700'
                      : 'bg-slate-200 dark:bg-slate-800'
                  )}
                />
              )}
            </div>
          );
        })}
        {/* Individual-mode upsell: replaces the 5 hidden steps with a clean,
            non-scrolling hint chip. Sits right next to the Step 1 card. */}
        {isIndividual && (
          <div data-testid="individual-mode-banner" className="flex items-start gap-2 pl-3 sm:pl-4 border-l border-slate-200/80 dark:border-slate-800 text-[11px] leading-relaxed text-ae-slate dark:text-slate-400 min-w-0">
            <Lock className="h-3 w-3 mt-0.5 shrink-0" />
            <span>
              <strong className="text-ae-ink dark:text-slate-200">Individual Mode</strong> — generate documents here.
              Steps 2–6 (Create · Deliver · Assign · Evaluate · Improve) unlock in <strong className="text-ae-ink dark:text-slate-200">School Mode</strong>.
              Document access &amp; monthly counts follow your <span data-testid="individual-mode-tier">{(tier || 'free').toUpperCase()}</span> plan.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 1 — Plan (Task + Teaching Goal + Planning Scope)
// ─────────────────────────────────────────────────────────────
function Step1Plan({
  mode,
  selectedType, setSelectedType, teachingGoal, setTeachingGoal,
  planningScope, setPlanningScope,
  sourceMode, setSourceMode, libraryBooks,
  libBoard, setLibBoard, libClass, setLibClass, libSubject, setLibSubject,
  librarySearch, setLibrarySearch,
  selectedBookId, setSelectedBookId,
  selectedChapterNumber, setSelectedChapterNumber, uploadedBook, onUploadBook, uploading,
  docTypes, setDocTypes, docTypeCaps, access, board, classId, subject,
  // NEW props for inline generate + cascade
  setBoard, setClassId, setSubject, setChapter, isGenerating, onInlineGenerate, batchResult,
  // Section B question-count/max-marks controls (lifted state from parent)
  sbNumQuestions, setSbNumQuestions, sbMaxMarks, setSbMaxMarks,
}) {
  const { t } = useTranslation();
  // BOSCO Capture access (Phase 1) — controls whether the 4th source card
  // navigates or opens the upsell modal.
  const captureAccess = useBoscoCaptureAccess({ enabled: true });
  const [captureUpsellOpen, setCaptureUpsellOpen] = useState(false);
  const selectedBook = useMemo(() => {
    if (uploadedBook && selectedBookId === uploadedBook.bookId) return uploadedBook;
    return libraryBooks.find((b) => b.bookId === selectedBookId) || null;
  }, [selectedBookId, libraryBooks, uploadedBook]);

  // Filtered library list (search across title/author/publisher)
  const filteredLibraryBooks = useMemo(() => {
    const needle = (librarySearch || '').trim().toLowerCase();
    if (!needle) return libraryBooks;
    return libraryBooks.filter((b) =>
      [b.title, b.author, b.publisher, b.subject, b.board]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [libraryBooks, librarySearch]);

  // Auto-cascade book → board/class/subject when a library book is picked
  useEffect(() => {
    if (!selectedBook || sourceMode !== 'library') return;
    if (selectedBook.board) setBoard(selectedBook.board);
    if (selectedBook.classId) setClassId(selectedBook.classId);
    if (selectedBook.subject) setSubject(selectedBook.subject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBook]);

  // When chapter selected, cascade into global chapter state for Step 2 coherence
  useEffect(() => {
    if (!selectedBook || !selectedChapterNumber) return;
    const ch = (selectedBook.chapters || []).find((c) => String(c.number) === String(selectedChapterNumber));
    if (ch) setChapter(`Chapter ${ch.number} — ${ch.title}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChapterNumber]);

  const chapters = selectedBook?.chapters || [];
  const maxDocs = docTypeCaps?.maxDocsPerBatch || 1;
  const docTypeAccess = access?.docTypeAccess || {};
  const tierName = (access?.tier || docTypeCaps?.tier || 'free').toUpperCase();
  const toggleDocType = (dt) => {
    if (docTypeAccess[dt] === false) {
      toast.error(`PPT/${dt.replace('_', ' ')} not included on ${tierName}.`, {
        description: 'Upgrade your plan to unlock this document type.',
        action: {
          label: 'Upgrade',
          onClick: () => window.dispatchEvent(new CustomEvent('ce:upgrade-required', {
            detail: { code: 'DOC_TYPE_LOCKED', requiredTier: 'starter', currentTier: (access?.tier || 'free') },
          })),
        },
      });
      return;
    }
    setDocTypes((prev) => {
      // Mutual exclusivity — PPT Slides is standalone (different generation pipeline).
      const isPpt = dt === 'ppt';
      const hasPpt = prev.includes('ppt');
      const hasNonPpt = prev.some((x) => x !== 'ppt');
      if (prev.includes(dt)) return prev.filter((x) => x !== dt);
      if (isPpt && hasNonPpt) {
        toast.info('PPT Slides works standalone — switched selection to PPT only.');
        return ['ppt'];
      }
      if (!isPpt && hasPpt) {
        toast.info('PPT Slides works standalone — switched selection to docs only.');
        return [dt];
      }
      if (prev.length >= maxDocs) {
        toast.error(`Your ${tierName} plan allows ${maxDocs} doc${maxDocs === 1 ? '' : 's'} per batch.`);
        return prev;
      }
      return [...prev, dt];
    });
  };

  // Derived readiness for inline Generate CTA
  const sourceOk = (sourceMode === 'topic' && board && classId && subject)
    || (sourceMode === 'library' && selectedBook && (selectedChapterNumber || !chapters.length))
    || (sourceMode === 'upload' && uploadedBook && selectedChapterNumber);
  const canGenerateInline = Boolean(selectedType && teachingGoal && docTypes.length > 0 && sourceOk);

  // Summary sentence for the context strip
  const summaryBits = useMemo(() => {
    const bits = [];
    if (selectedType) bits.push(selectedType);
    if (teachingGoal) bits.push(teachingGoal.replace(/_/g, ' '));
    if (planningScope === 'lesson_kit') bits.push('Full Lesson Kit');
    if (sourceMode === 'library' && selectedBook) {
      bits.push(selectedBook.title);
      if (selectedChapterNumber) bits.push(`Ch ${selectedChapterNumber}`);
    } else if (sourceMode === 'upload' && uploadedBook) {
      bits.push(uploadedBook.title);
      if (selectedChapterNumber) bits.push(`Ch ${selectedChapterNumber}`);
    } else if (sourceMode === 'topic') {
      if (board) bits.push(String(board).toUpperCase());
      if (classId) bits.push(`Class ${classId}`);
      if (subject) bits.push(subject);
    }
    if (docTypes.length > 0) bits.push(`→ ${docTypes.length} doc${docTypes.length === 1 ? '' : 's'}`);
    return bits;
  }, [selectedType, teachingGoal, planningScope, sourceMode, selectedBook, selectedChapterNumber, uploadedBook, board, classId, subject, docTypes]);

  return (
    <div className="space-y-6" data-testid="step-1-plan">
      {/* Live Context Strip */}
      <div
        data-testid="step1-context-strip"
        className="flex items-center flex-wrap gap-2 px-4 py-3 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-900/50"
      >
        <Activity className="h-4 w-4 text-emerald-500" />
        <span className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
          Your plan:
        </span>
        {summaryBits.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">
            Pick a task, goal, source, and document types — preview here
          </span>
        ) : (
          summaryBits.map((b, i) => (
            <span key={i} className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900">
              {b}
            </span>
          ))
        )}
      </div>
      <SectionCard
        title="Select Task Type"
        subtitle="Pick a format — we'll auto-pre-select the matching document types below"
        icon={FileText}
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {TASK_TYPES_LIST.map((type) => {
            const cfg = TASK_TYPE_CONFIG[type];
            const Icon = TASK_ICON_MAP[cfg.icon] || FileText;
            const active = selectedType === type;
            return (
              <button
                key={type}
                data-testid={`task-type-${type.toLowerCase().replace(/[\s/]+/g, '-')}`}
                onClick={() => setSelectedType(type)}
                className={cn(
                  'group relative rounded-xl border p-4 text-left transition-all',
                  active
                    ? 'border-slate-900 dark:border-white bg-slate-50 dark:bg-slate-800 shadow-sm'
                    : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 hover:shadow-sm bg-white dark:bg-slate-900'
                )}
              >
                <div
                  className={cn(
                    'h-9 w-9 rounded-lg flex items-center justify-center mb-3',
                    active ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                  )}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-sm font-semibold">{t(`teacher.taskTypes.${type}`, { defaultValue: type })}</div>
                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                  {cfg.purpose}
                </div>
                {active && (
                  <div className="absolute top-3 right-3">
                    <Check className="h-4 w-4 text-emerald-600" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard title="Teaching Goal" subtitle="Tunes the difficulty mix automatically" icon={Target}>
          <div className="grid grid-cols-2 gap-2.5" data-testid="teaching-goals">
            {TEACHING_GOALS.map((g) => {
              const Icon = GOAL_ICON_MAP[g.icon] || Sparkles;
              const active = teachingGoal === g.id;
              return (
                <button
                  key={g.id}
                  data-testid={`teaching-goal-${g.id}`}
                  onClick={() => setTeachingGoal(g.id)}
                  className={cn(
                    'flex items-center gap-2.5 p-3 rounded-lg border text-left text-sm transition-all',
                    active
                      ? 'border-slate-900 dark:border-white bg-slate-50 dark:bg-slate-800'
                      : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 bg-white dark:bg-slate-900'
                  )}
                >
                  <Icon className={cn('h-4 w-4', active ? 'text-slate-900 dark:text-white' : 'text-slate-500')} />
                  <span className="font-medium">{g.label}</span>
                </button>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard title="Planning Scope" subtitle="One class's worth of content, or the full bundle (PPT + Plan + Homework + Assessment)" icon={Layers}>
          <div className="space-y-2" data-testid="planning-scope">
            {PLANNING_SCOPES.map((p) => {
              const active = planningScope === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={`planning-scope-${p.id}`}
                  onClick={() => setPlanningScope(p.id)}
                  className={cn(
                    'w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all',
                    active
                      ? 'border-slate-900 dark:border-white bg-slate-50 dark:bg-slate-800'
                      : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 bg-white dark:bg-slate-900'
                  )}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className={cn(
                        'h-4 w-4 rounded-full border-2 flex items-center justify-center',
                        active ? 'border-slate-900 dark:border-white' : 'border-slate-300 dark:border-slate-600'
                      )}
                    >
                      {active && <div className="h-1.5 w-1.5 rounded-full bg-slate-900 dark:bg-white" />}
                    </div>
                    <span className="text-sm font-medium">{p.label}</span>
                  </div>
                  {p.id === 'lesson_kit' && (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-0 text-[10px]">
                      Full Kit
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
        </SectionCard>
      </div>

      {/* Source selector — Library / Upload / Topic */}
      <SectionCard
        title="Content Source"
        subtitle="Pick a book, upload a PDF, or generate from the topic directly"
        icon={Library}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4" data-testid="source-modes">
          {[
            { id: 'library', label: 'Library Book', icon: BookOpen, hint: 'Pick a platform book' },
            { id: 'upload', label: 'Upload PDF', icon: ArrowUp, hint: 'Extract chapters from your PDF' },
            { id: 'topic', label: 'Topic Only', icon: Wand2, hint: 'Just the chapter/topic string' },
            { id: 'capture', label: 'BOSCO Capture™', icon: Camera, hint: 'Use your phone camera · Elite only', isCapture: true },
          ].map((m) => {
            const Icon = m.icon;
            const active = sourceMode === m.id;

            // ─── BOSCO Capture (4th source) — Phase 1 ───
            if (m.isCapture) {
              const enabled = captureAccess?.data?.enabled;
              const navigate = (url) => { if (typeof window !== 'undefined') window.location.assign(url); };
              return (
                <button
                  key={m.id}
                  data-testid="source-capture"
                  onClick={() => {
                    if (captureAccess.loading) return;
                    if (enabled) {
                      trackClarityEvent?.('bosco_capture_open_from_source', { from: 'source-card' });
                      navigate('/bosco-capture');
                    } else {
                      trackClarityEvent?.('bosco_capture_upsell_open', { tier: captureAccess?.data?.tier });
                      setCaptureUpsellOpen(true);
                    }
                  }}
                  className={cn(
                    'relative flex items-start gap-3 p-3 rounded-lg border text-left transition-all overflow-hidden',
                    'border-emerald-300/70 dark:border-emerald-700/50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/30 dark:to-slate-900',
                    'hover:border-emerald-500 hover:shadow-md hover:shadow-emerald-200/40 dark:hover:shadow-emerald-900/20',
                  )}
                >
                  {/* Beta crown */}
                  <span
                    data-testid="source-capture-beta-crown"
                    className="absolute -top-1 -right-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-bl-md rounded-tr-md bg-amber-400 text-amber-950 text-[9px] font-bold uppercase tracking-wider"
                    title="Beta — early access"
                  >
                    <Crown className="h-2.5 w-2.5" /> Beta
                  </span>
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-emerald-600 text-white shadow shadow-emerald-600/30">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-1.5">
                      {m.label}
                      {!enabled && !captureAccess.loading && (
                        <Lock className="h-3 w-3 text-slate-400" aria-label="Locked — Elite required" />
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {captureAccess.loading
                        ? 'Checking access…'
                        : (enabled ? 'Phone camera → instant teaching kit' : m.hint)}
                    </div>
                  </div>
                </button>
              );
            }

            return (
              <button
                key={m.id}
                data-testid={`source-${m.id}`}
                onClick={() => setSourceMode(m.id)}
                className={cn(
                  'flex items-start gap-3 p-3 rounded-lg border text-left transition-all',
                  active
                    ? 'border-slate-900 dark:border-white bg-slate-50 dark:bg-slate-800'
                    : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 bg-white dark:bg-slate-900'
                )}
              >
                <div className={cn('h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0',
                  active ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-800')}>
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-semibold">{m.label}</div>
                  <div className="text-[11px] text-muted-foreground">{m.hint}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* BOSCO Capture Upsell Modal (Phase 1) */}
        <BoscoCaptureUpsell
          open={captureUpsellOpen}
          onClose={() => setCaptureUpsellOpen(false)}
          tier={captureAccess?.data?.tier}
          isIndividual={captureAccess?.data?.isIndividual ?? true}
        />

        {sourceMode === 'library' && (
          <div className="space-y-3" data-testid="source-library-panel">
            {/* Category quick-filter (NCERT vs International) — sets libBoard for the user */}
            <div className="flex items-center gap-1.5" data-testid="lib-category-tabs">
              {[
                { id: 'all',           label: 'All Books',     match: () => true },
                { id: 'ncert',         label: 'NCERT (CBSE)',  match: (b) => b.board === 'cbse' },
                { id: 'international', label: 'International', match: (b) => ['icse', 'igcse', 'ib'].includes(b.board) },
              ].map((cat) => {
                const count = libraryBooks.filter(cat.match).length;
                const isActive =
                  cat.id === 'all' ? libBoard === '' :
                  cat.id === 'ncert' ? libBoard === 'cbse' :
                  ['icse', 'igcse', 'ib'].includes(libBoard);
                return (
                  <button
                    key={cat.id}
                    data-testid={`lib-cat-${cat.id}`}
                    onClick={() => {
                      if (cat.id === 'all') setLibBoard('');
                      else if (cat.id === 'ncert') setLibBoard('cbse');
                      else setLibBoard('igcse'); // default International → IGCSE; user can refine in dropdown
                    }}
                    className={cn(
                      'h-8 px-3 rounded-full text-[11px] font-semibold border transition-colors',
                      isActive
                        ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'
                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-400 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300'
                    )}
                  >
                    {cat.label} <span className="ml-1 opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>

            {/* Library-local filters — independent from Step 2 curriculum */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Select value={libBoard || 'all'} onValueChange={(v) => setLibBoard(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="lib-board-filter" className="h-9 text-xs">
                  <SelectValue placeholder="Board" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All boards</SelectItem>
                  {BOARDS.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={libClass || 'all'} onValueChange={(v) => setLibClass(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="lib-class-filter" className="h-9 text-xs">
                  <SelectValue placeholder="Class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  {CLASSES.map((c) => (
                    <SelectItem key={c} value={c}>Class {c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={libSubject || 'all'} onValueChange={(v) => setLibSubject(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="lib-subject-filter" className="h-9 text-xs">
                  <SelectValue placeholder="Subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subjects</SelectItem>
                  {SUBJECTS.concat(['English Literature']).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                data-testid="lib-search"
                placeholder="Search title / author…"
                value={librarySearch}
                onChange={(e) => setLibrarySearch(e.target.value)}
                className="h-9 text-xs"
              />
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {filteredLibraryBooks.length} of {libraryBooks.length} book{libraryBooks.length === 1 ? '' : 's'}
                {(libBoard || libClass || libSubject || librarySearch) && (
                  <button
                    data-testid="lib-clear-filters"
                    onClick={() => {
                      setLibBoard(''); setLibClass(''); setLibSubject(''); setLibrarySearch('');
                    }}
                    className="ml-2 underline hover:text-foreground"
                  >
                    Clear filters
                  </button>
                )}
              </span>
            </div>

            {/* Book grid — card view */}
            <div
              data-testid="library-book-grid"
              className="max-h-80 overflow-y-auto pr-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
            >
              {filteredLibraryBooks.length === 0 ? (
                <div className="col-span-full text-xs text-muted-foreground italic p-6 text-center border border-dashed rounded-lg">
                  No books match — try clearing filters.
                </div>
              ) : (
                filteredLibraryBooks.map((b) => {
                  const active = b.bookId === selectedBookId;
                  return (
                    <button
                      key={b.bookId}
                      data-testid={`lib-book-${b.bookId}`}
                      onClick={() => { setSelectedBookId(b.bookId); setSelectedChapterNumber(''); }}
                      className={cn(
                        'text-left p-3 rounded-lg border transition-all',
                        active
                          ? 'border-emerald-500 bg-emerald-50/40 dark:bg-emerald-900/20 ring-2 ring-emerald-500/30'
                          : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 bg-white dark:bg-slate-900'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="text-xs font-semibold line-clamp-2 leading-snug">
                          {b.title}
                        </div>
                        {active && <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{b.author || b.publisher || '—'}</div>
                      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                        <Badge variant="secondary" className="text-[9px] px-1.5 py-0">
                          {b.board?.toUpperCase()}
                        </Badge>
                        <Badge variant="secondary" className="text-[9px] px-1.5 py-0">
                          Class {b.classId}
                        </Badge>
                        {b.source_type === 'Gutenberg' && (
                          <Badge className="bg-amber-100 text-amber-800 text-[9px] px-1.5 py-0 border-0">
                            Classic
                          </Badge>
                        )}
                        {b.source_type === 'NCERT' && (
                          <Badge className="bg-emerald-100 text-emerald-800 text-[9px] px-1.5 py-0 border-0">
                            NCERT
                          </Badge>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {(b.chapters || []).length} chapter{(b.chapters || []).length === 1 ? '' : 's'} • {b.subject}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {selectedBook && chapters.length > 0 && (
              <Select value={selectedChapterNumber} onValueChange={setSelectedChapterNumber}>
                <SelectTrigger data-testid="book-chapter-select">
                  <SelectValue placeholder="Choose chapter number…" />
                </SelectTrigger>
                <SelectContent className="max-h-96">
                  {chapters.map((ch) => (
                    <SelectItem key={ch.number} value={String(ch.number)}>
                      Chapter {ch.number} — {ch.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {selectedBook && (
              <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                <BookOpen className="h-3 w-3" />
                Selected: <strong className="text-foreground">{selectedBook.title}</strong>
                {selectedChapterNumber && <span>· Chapter {selectedChapterNumber}</span>}
              </div>
            )}
          </div>
        )}

        {sourceMode === 'upload' && (
          <div className="space-y-3" data-testid="source-upload-panel">
            <label className="flex flex-col items-center justify-center gap-2 p-8 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-slate-400 cursor-pointer transition-colors">
              <input
                data-testid="upload-pdf-input"
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && onUploadBook(e.target.files[0])}
                disabled={uploading}
              />
              {uploading ? (
                <><Loader2 className="h-5 w-5 animate-spin" /><span className="text-sm">Extracting chapters…</span></>
              ) : uploadedBook ? (
                <><CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm font-semibold">{uploadedBook.title}</span>
                  <span className="text-xs text-muted-foreground">{uploadedBook.chapters?.length || 0} chapter(s) detected{uploadedBook.pageCount ? ` • ${uploadedBook.pageCount} pages` : ''}</span>
                  <span className="text-[11px] text-slate-500 underline">Click to replace</span></>
              ) : (
                <><ArrowUp className="h-5 w-5 text-slate-500" />
                  <span className="text-sm font-semibold">Upload PDF</span>
                  <span className="text-xs text-muted-foreground">Max 25 MB. Chapter detection is heuristic — manual edit coming soon.</span>
                  <span className="text-[11px] text-amber-700 dark:text-amber-400 font-medium">💡 For best results, upload one chapter at a time.</span></>
              )}
            </label>
            {uploadedBook && uploadedBook.chapters?.length > 0 && (
              <Select value={selectedChapterNumber} onValueChange={setSelectedChapterNumber}>
                <SelectTrigger data-testid="upload-chapter-select">
                  <SelectValue placeholder="Choose chapter…" />
                </SelectTrigger>
                <SelectContent>
                  {uploadedBook.chapters.map((ch) => (
                    <SelectItem key={ch.number} value={String(ch.number)}>
                      Chapter {ch.number} — {ch.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {sourceMode === 'topic' && (
          <div className="text-xs text-muted-foreground p-3 rounded-lg bg-slate-50 dark:bg-slate-900/60 border border-dashed border-slate-200 dark:border-slate-800" data-testid="source-topic-panel">
            Uses the <strong>chapter field</strong> on Step 2 as the topic (or subject if no chapter set). Perfect for quick one-off generation.
          </div>
        )}
      </SectionCard>

      {/* Documents to create */}
      <SectionCard
        title="Documents to Create"
        subtitle="Multi-select per your plan"
        icon={FileText}
        actions={
          docTypeCaps && (
            <Badge variant="secondary" className="text-[10px]" data-testid="doc-type-cap">
              {docTypes.length} / {maxDocs} on {docTypeCaps.tier?.toUpperCase()}
            </Badge>
          )
        }
      >
        <div
          data-testid="doctype-standalone-note"
          className="mb-3 rounded-md border border-amber-200/70 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-900/10 px-3 py-2 text-[11px] leading-relaxed text-amber-900 dark:text-amber-200"
        >
          <strong>PPT Slides works standalone.</strong> Pick PPT on its own (rendered as a designed slide deck) <em>or</em> any combination of Lesson Plan / Assessment / Teaching Flow (rendered as .docx&nbsp;+&nbsp;.pdf). Cross-type cards stay greyed out until you change selection.
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="doc-type-picker">
          {[
            { id: 'lesson_plan', icon: BookOpen, format: '.docx + .pdf' },
            { id: 'assessment', icon: ListChecks, format: '.docx + .pdf' },
            { id: 'teaching_flow', icon: PlayCircle, format: '.docx + .pdf' },
            { id: 'ppt', icon: Presentation, format: '.pptx' },
          ].map((dt) => {
            const Icon = dt.icon;
            const selected = docTypes.includes(dt.id);
            const locked = docTypeAccess[dt.id] === false;
            const atCap = !selected && !locked && docTypes.length >= maxDocs;
            // Mutual exclusivity — PPT Slides is standalone. Grey out cross-type cards.
            const pptSelected = docTypes.includes('ppt');
            const nonPptSelected = docTypes.some((x) => x !== 'ppt');
            const crossExcluded = !selected && (
              (dt.id === 'ppt' && nonPptSelected) ||
              (dt.id !== 'ppt' && pptSelected)
            );
            const disabled = locked || atCap || crossExcluded;
            return (
              <button
                key={dt.id}
                data-testid={`doctype-${dt.id}`}
                onClick={() => toggleDocType(dt.id)}
                disabled={disabled && !locked && !crossExcluded}
                title={crossExcluded
                  ? (dt.id === 'ppt'
                      ? 'PPT Slides works standalone — deselect the other docs first.'
                      : 'PPT Slides is selected — deselect it to mix with other docs.')
                  : undefined}
                className={cn(
                  'relative flex flex-col items-start gap-1.5 p-3 rounded-lg border text-left transition-all',
                  selected
                    ? 'border-emerald-500 bg-emerald-50/40 dark:bg-emerald-900/20 ring-2 ring-emerald-500/30'
                    : locked
                    ? 'border-slate-200 bg-slate-50 dark:bg-slate-900/40 cursor-not-allowed opacity-70'
                    : crossExcluded
                    ? 'border-slate-200 bg-slate-100/60 dark:bg-slate-900/40 opacity-40 cursor-pointer'
                    : atCap
                    ? 'border-slate-200 bg-slate-50 dark:bg-slate-900 opacity-50 cursor-not-allowed'
                    : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 bg-white dark:bg-slate-900'
                )}
              >
                <div className="flex items-center justify-between w-full">
                  <Icon className={cn('h-4 w-4',
                    selected ? 'text-emerald-600' : locked ? 'text-slate-400' : 'text-slate-500'
                  )} />
                  {selected ? (
                    <Check className="h-4 w-4 text-emerald-600" />
                  ) : locked ? (
                    <Lock className="h-3.5 w-3.5 text-slate-400" />
                  ) : null}
                </div>
                <div className={cn('text-sm font-semibold', locked && 'text-slate-500')}>
                  {t(`teacher.docTypes.${dt.id}`)}
                </div>
                <div className="text-[10px] text-muted-foreground font-mono">{dt.format}</div>
                {locked && (
                  <div className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1">
                    <Crown className="h-2.5 w-2.5" /> Upgrade required
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Iter 74 — Fix #2: Question-count + Max-marks UI for Assessment doctype */}
        {docTypes.includes('assessment') && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 rounded-xl border border-indigo-200/60 dark:border-indigo-900/40 bg-indigo-50/40 dark:bg-indigo-950/20 p-4">
            <div>
              <label htmlFor="sb-num-questions" className="text-xs uppercase tracking-wide font-semibold text-indigo-700 dark:text-indigo-300">
                No. of Questions
                <span className="ml-1 text-[10px] text-slate-500 normal-case font-normal">(optional)</span>
              </label>
              <input
                id="sb-num-questions"
                type="number"
                min={1}
                max={100}
                placeholder="auto (10–25)"
                value={sbNumQuestions}
                onChange={(e) => setSbNumQuestions(e.target.value)}
                className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                data-testid="sb-input-num-questions"
              />
              <p className="text-[10px] text-slate-500 mt-1">Leave blank to use difficulty-based default.</p>
            </div>
            <div>
              <label htmlFor="sb-max-marks" className="text-xs uppercase tracking-wide font-semibold text-indigo-700 dark:text-indigo-300">
                Max Marks
                <span className="ml-1 text-[10px] text-slate-500 normal-case font-normal">(optional)</span>
              </label>
              <input
                id="sb-max-marks"
                type="number"
                min={1}
                max={500}
                placeholder="e.g. 80, 100"
                value={sbMaxMarks}
                onChange={(e) => setSbMaxMarks(e.target.value)}
                className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                data-testid="sb-input-max-marks"
              />
              <p className="text-[10px] text-slate-500 mt-1">Marks will sum to exactly this total.</p>
            </div>
          </div>
        )}

        {/* Template download row */}
        <div className="mt-4 pt-4 border-t border-slate-200/60 dark:border-slate-800">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
            Download blank templates
          </div>
          <div className="flex flex-wrap gap-2" data-testid="template-downloads">
            {['summary', 'lesson_plan', 'assessment', 'teaching_flow', 'ppt'].map((dt) => (
              <button
                key={dt}
                data-testid={`template-${dt}`}
                onClick={async () => {
                  try {
                    await contentEngineApi.downloadTemplate(dt);
                    toast.success(`${dt.replace('_', ' ')} template downloaded`);
                  } catch (err) {
                    toast.error(err.message || 'Template download failed');
                  }
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-full border border-slate-200 dark:border-slate-700 hover:border-slate-400 bg-white dark:bg-slate-900 transition-colors"
              >
                <Download className="h-3 w-3" /> {dt.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* Inline Generate CTA — brings Step 4 Generate right here */}
      <div
        data-testid="step1-generate-cta"
        className="rounded-2xl border-2 border-dashed border-amber-200 dark:border-amber-900/40 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 p-5"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center flex-shrink-0">
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold">Ready to generate?</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {canGenerateInline
                  ? `Tap below to create ${docTypes.length} document${docTypes.length === 1 ? '' : 's'} now — skip the rest of the wizard.`
                  : 'Pick a task type, teaching goal, content source & at least one doc type to enable.'}
              </div>
            </div>
          </div>
          <Button
            data-testid="step1-generate-btn"
            onClick={onInlineGenerate}
            disabled={!canGenerateInline || isGenerating}
            className="gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-soft"
          >
            {isGenerating ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
            ) : (
              <><Sparkles className="h-4 w-4" /> Generate Now</>
            )}
          </Button>
        </div>

        {/* Batch results inline once available */}
        {batchResult && batchResult.results?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 pt-4 border-t border-amber-200/60 dark:border-amber-900/30"
          >
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              {batchResult.results.length} document{batchResult.results.length === 1 ? '' : 's'} ready
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="step1-batch-downloads">
              {batchResult.results.map((r) => (
                <div
                  key={r.assetId}
                  className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold truncate capitalize">{r.docType.replace('_', ' ')}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{r.title}</div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {r.downloadFormats.map((fmt) => (
                      <button
                        key={fmt}
                        data-testid={`step1-download-${r.docType}-${fmt}`}
                        onClick={async () => {
                          try {
                            await contentEngineApi.downloadAsset(fmt, r.assetId, `${r.docType}.${fmt}`);
                          } catch (err) {
                            toast.error(err.message || 'Download failed');
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-900 text-white hover:bg-slate-700 dark:bg-white dark:text-slate-900 transition-colors"
                      >
                        <Download className="h-2.5 w-2.5" /> .{fmt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
function Step2Create({
  mode, board, setBoard, classId, setClassId, subject, setSubject,
  chapter, setChapter, availableChapters, boardLabel,
}) {
  const lockedBoard = mode === 'school' && Boolean(board);
  return (
    <div className="space-y-6" data-testid="step-2-create">
      <SectionCard
        title="Curriculum Source"
        subtitle="Context for the content engine — drives prompts, difficulty & tone"
        icon={BookOpen}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Board" required>
            <Select value={board} onValueChange={setBoard} disabled={lockedBoard}>
              <SelectTrigger data-testid="board-select">
                <SelectValue placeholder="Select board" />
              </SelectTrigger>
              <SelectContent>
                {BOARDS.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    <span className="flex items-center gap-2">
                      {b.label}
                      <span className="text-[10px] text-muted-foreground">• {b.country}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {lockedBoard && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Auto-set from school settings
              </p>
            )}
          </Field>

          <Field label="Class" required>
            <Select value={classId} onValueChange={setClassId}>
              <SelectTrigger data-testid="class-select">
                <SelectValue placeholder="Select class" />
              </SelectTrigger>
              <SelectContent>
                {CLASSES.map((c) => (
                  <SelectItem key={c} value={c}>
                    Class {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Subject" required>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger data-testid="subject-select">
                <SelectValue placeholder="Select subject" />
              </SelectTrigger>
              <SelectContent>
                {SUBJECTS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Chapter" hint={availableChapters.length === 0 ? 'Select board + class + subject first' : `${availableChapters.length} chapters`}>
            <Select value={chapter} onValueChange={setChapter} disabled={availableChapters.length === 0}>
              <SelectTrigger data-testid="chapter-select">
                <SelectValue placeholder="Select chapter (optional)" />
              </SelectTrigger>
              <SelectContent>
                {availableChapters.map((ch) => (
                  <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        {board && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>Aligned with {boardLabel} curriculum</span>
          </motion.div>
        )}
      </SectionCard>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 3 — Deliver (Configure) — collapsible sections
// ─────────────────────────────────────────────────────────────
function Step3Deliver({
  openSection, setOpenSection, board, boardLabel, classId, subject, chapter,
  syllabusVersion, teachingObjective, setTeachingObjective, difficulty, setDifficulty,
  questionTypes, setQuestionTypes, answerKey, setAnswerKey, variations, setVariations,
  lessonKitMode, setLessonKitMode, teachingStyle, setTeachingStyle, filteredStyles,
  duration, setDuration,
  // Iter 73 — Fix #6
  numQuestions, setNumQuestions, maxMarks, setMaxMarks, selectedType,
}) {
  const toggleQType = (qt) => {
    setQuestionTypes((prev) =>
      prev.includes(qt) ? prev.filter((q) => q !== qt) : [...prev, qt]
    );
  };

  return (
    <div className="space-y-3" data-testid="step-3-deliver">
      {/* A. Curriculum Context */}
      <ConfigSection
        id="curriculum"
        open={openSection === 'curriculum'}
        setOpen={setOpenSection}
        title="Curriculum Context"
        subtitle="Board, class, subject, chapter — mandatory core"
        icon={BookOpen}
        badge="Required"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ReadOnlyField label="Board" value={boardLabel || '—'} />
          <ReadOnlyField label="Class" value={classId ? `Class ${classId}` : '—'} />
          <ReadOnlyField label="Subject" value={subject || '—'} />
          <ReadOnlyField label="Chapter" value={chapter || 'All chapters'} />
        </div>
        <details className="mt-3 group">
          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground select-none inline-flex items-center gap-1">
            <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
            Syllabus version
          </summary>
          <div className="mt-2 text-xs text-muted-foreground pl-4">
            <span className="font-mono">{syllabusVersion}</span> — auto-selected
          </div>
        </details>
      </ConfigSection>

      {/* B. Teaching Objective */}
      <ConfigSection
        id="objective"
        open={openSection === 'objective'}
        setOpen={setOpenSection}
        title="Teaching Objective"
        subtitle="What should learners gain?"
        icon={Target}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="teaching-objectives">
          {TEACHING_OBJECTIVES.map((o) => {
            const active = teachingObjective === o.id;
            return (
              <button
                key={o.id}
                data-testid={`objective-${o.id}`}
                onClick={() => setTeachingObjective(o.id)}
                className={cn(
                  'p-3 rounded-lg border text-sm font-medium transition-all',
                  active
                    ? 'border-slate-900 dark:border-white bg-slate-50 dark:bg-slate-800'
                    : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 bg-white dark:bg-slate-900'
                )}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </ConfigSection>

      {/* C. Generation Controls */}
      <ConfigSection
        id="generation"
        open={openSection === 'generation'}
        setOpen={setOpenSection}
        title="Generation Controls"
        subtitle="Difficulty mix, question types, answer keys & variations"
        icon={Gauge}
      >
        <div className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Difficulty Distribution
            </Label>
            <div className="mt-3 grid grid-cols-3 gap-4">
              {['Easy', 'Medium', 'Hard'].map((lvl, idx) => (
                <div key={lvl}>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="font-medium">{lvl}</span>
                    <span className="text-muted-foreground">{difficulty[idx]}%</span>
                  </div>
                  <Slider
                    data-testid={`difficulty-${lvl.toLowerCase()}`}
                    value={[difficulty[idx]]}
                    max={100}
                    step={5}
                    onValueChange={(v) => {
                      const newDiff = [...difficulty];
                      newDiff[idx] = v[0];
                      setDifficulty(newDiff);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Question Types
            </Label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="question-types">
              {QUESTION_TYPES.map((qt) => {
                const active = questionTypes.includes(qt);
                return (
                  <button
                    key={qt}
                    data-testid={`qtype-${qt.toLowerCase().replace(/[\s/]+/g, '-')}`}
                    onClick={() => toggleQType(qt)}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                      active
                        ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'
                        : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                    )}
                  >
                    {qt}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Iter 73 — Fix #6: explicit "No. of questions" + "Max marks" */}
          {['Assignment', 'Test', 'Exam', 'Homework', 'Holiday Homework'].includes(selectedType) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="num-questions-input" className="text-xs uppercase tracking-wide text-muted-foreground">
                  No. of Questions <span className="text-slate-400 normal-case">(optional)</span>
                </Label>
                <input
                  id="num-questions-input"
                  type="number"
                  min={1}
                  max={100}
                  placeholder="auto (10–20 by difficulty)"
                  value={numQuestions}
                  onChange={(e) => setNumQuestions(e.target.value)}
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  data-testid="input-num-questions"
                />
                <p className="text-[11px] text-slate-500 mt-1">Leave blank to use difficulty-based default.</p>
              </div>
              <div>
                <Label htmlFor="max-marks-input" className="text-xs uppercase tracking-wide text-muted-foreground">
                  Max Marks <span className="text-slate-400 normal-case">(optional)</span>
                </Label>
                <input
                  id="max-marks-input"
                  type="number"
                  min={1}
                  max={500}
                  placeholder="e.g. 80, 100"
                  value={maxMarks}
                  onChange={(e) => setMaxMarks(e.target.value)}
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  data-testid="input-max-marks"
                />
                <p className="text-[11px] text-slate-500 mt-1">Marks will sum to exactly this total.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ToggleRow
              testId="toggle-answer-key"
              label="Include Answer Key"
              description="Generate solutions alongside questions"
              checked={answerKey}
              onChange={setAnswerKey}
              icon={Check}
            />
            <ToggleRow
              testId="toggle-variations"
              label="Create Variations"
              description="Multiple sets for anti-cheating"
              checked={variations}
              onChange={setVariations}
              icon={RefreshCw}
            />
          </div>
        </div>
      </ConfigSection>

      {/* D. Lesson Kit Mode */}
      <ConfigSection
        id="lessonkit"
        open={openSection === 'lessonkit'}
        setOpen={setOpenSection}
        title="Lesson Kit Mode"
        subtitle="One-shot bundle: PPT + teaching flow + questions + homework + assessment"
        icon={Layers}
        badge={lessonKitMode ? 'ON' : 'OFF'}
        badgeTone={lessonKitMode ? 'emerald' : 'neutral'}
      >
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-800">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-semibold">Enable Full Lesson Kit</div>
              <div className="text-xs text-muted-foreground">
                Auto-bundles PPT slides, teaching flow, in-class questions, homework, and assessment.
              </div>
            </div>
          </div>
          <Switch
            data-testid="toggle-lesson-kit"
            checked={lessonKitMode}
            onCheckedChange={setLessonKitMode}
          />
        </div>
        <AnimatePresence>
          {lessonKitMode && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  { label: 'PPT Slides', icon: Presentation, hint: 'Presenton AI' },
                  { label: 'Teaching Flow', icon: PlayCircle, hint: 'Minute-by-minute' },
                  { label: 'Class Questions', icon: MessageSquare, hint: 'Discussion prompts' },
                  { label: 'Homework', icon: Home, hint: 'Reinforcement' },
                  { label: 'Assessment', icon: ListChecks, hint: 'Evaluation' },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      className="p-2.5 rounded-lg border border-emerald-200/70 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-900/10"
                    >
                      <div className="flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-400" />
                        <span className="text-xs font-semibold text-emerald-900 dark:text-emerald-300">
                          {item.label}
                        </span>
                      </div>
                      <div className="text-[10px] text-emerald-700/70 dark:text-emerald-500/70 mt-0.5">
                        {item.hint}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 text-[11px] text-muted-foreground italic">
                PPT generation will use Presenton AI (self-hosted) when backend is wired.
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </ConfigSection>

      {/* E. Teaching Delivery Controls */}
      <ConfigSection
        id="delivery"
        open={openSection === 'delivery'}
        setOpen={setOpenSection}
        title="Teaching Delivery Controls"
        subtitle="Style & duration tailored to your board"
        icon={PlayCircle}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Teaching Style
            </Label>
            <div className="mt-2 space-y-1.5" data-testid="teaching-styles">
              {filteredStyles.map((s) => {
                const active = teachingStyle === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`style-${s.id}`}
                    onClick={() => setTeachingStyle(s.id)}
                    className={cn(
                      'w-full flex items-center gap-3 p-2.5 rounded-lg border text-left text-sm transition-colors',
                      active
                        ? 'border-slate-900 dark:border-white bg-slate-50 dark:bg-slate-800'
                        : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 bg-white dark:bg-slate-900'
                    )}
                  >
                    <div
                      className={cn(
                        'h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                        active ? 'border-slate-900 dark:border-white' : 'border-slate-300 dark:border-slate-600'
                      )}
                    >
                      {active && <div className="h-1.5 w-1.5 rounded-full bg-slate-900 dark:bg-white" />}
                    </div>
                    <span className="font-medium">{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Duration
            </Label>
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="durations">
              {DURATIONS.map((d) => {
                const active = duration === d;
                return (
                  <button
                    key={d}
                    data-testid={`duration-${d}`}
                    onClick={() => setDuration(d)}
                    className={cn(
                      'p-3 rounded-lg border text-sm font-semibold transition-colors',
                      active
                        ? 'border-slate-900 dark:border-white bg-slate-50 dark:bg-slate-800'
                        : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600 bg-white dark:bg-slate-900'
                    )}
                  >
                    {d} <span className="text-xs font-normal text-muted-foreground">min</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 text-[11px] text-muted-foreground flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              Maps to lesson flow segmentation
            </div>
          </div>
        </div>
      </ConfigSection>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 4 — Review + Assign + Micro Actions
// ─────────────────────────────────────────────────────────────
function Step4Assign({
  lessonKitMode, reviewTab, setReviewTab, selectedType, boardLabel, classId,
  subject, chapter, assignmentType, setAssignmentType, dueDate, setDueDate,
  assignTarget, setAssignTarget, classSections, mode, schoolId, handleMicroAction,
  generatedAsset, lessonKit, assignmentId, isGenerating, isAssigning, isIterating,
  onAssign, batchResult,
}) {
  const tabs = lessonKitMode
    ? [
        { id: 'content', label: 'Content', icon: FileText },
        { id: 'ppt', label: 'PPT Slides', icon: Presentation },
        { id: 'flow', label: 'Teaching Flow', icon: PlayCircle },
        { id: 'assessment', label: 'Assessment', icon: ListChecks },
      ]
    : [{ id: 'content', label: 'Content', icon: FileText }];

  // Active preview asset — defaults to first batch result if no individual asset is set
  const [activeAssetId, setActiveAssetId] = useState(null);
  const activeAsset = useMemo(() => {
    if (batchResult?.results?.length) {
      const r = batchResult.results.find((x) => x.assetId === activeAssetId)
        || batchResult.results[0];
      return { assetId: r.assetId, docType: r.docType, title: r.title, content: r.content };
    }
    return generatedAsset;
  }, [batchResult, activeAssetId, generatedAsset]);

  return (
    <div className="space-y-6" data-testid="step-4-assign">
      {batchResult && batchResult.results && batchResult.results.length > 0 && (
        <SectionCard
          title={`Batch Generated — ${batchResult.results.length} document${batchResult.results.length === 1 ? '' : 's'}`}
          subtitle={`Topic: ${batchResult.topic} • ${batchResult.board?.toUpperCase()}`}
          icon={Download}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5" data-testid="batch-downloads">
            {batchResult.results.map((r) => {
              const isActive = (activeAssetId || batchResult.results[0]?.assetId) === r.assetId;
              return (
                <div
                  key={r.assetId}
                  data-testid={`batch-item-${r.docType}`}
                  className={cn(
                    'flex items-center justify-between p-3 rounded-lg border bg-white dark:bg-slate-900 transition-all',
                    isActive
                      ? 'border-emerald-500 ring-2 ring-emerald-500/20'
                      : 'border-slate-200/70 dark:border-slate-800'
                  )}
                >
                  <button
                    onClick={() => setActiveAssetId(r.assetId)}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`preview-${r.docType}`}
                  >
                    <div className="text-sm font-semibold truncate">{r.title}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 capitalize">
                      {r.docType.replace('_', ' ')} • {r.downloadFormats.join(' + ')} • Click to preview
                    </div>
                  </button>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {r.downloadFormats.map((fmt) => (
                      <button
                        key={fmt}
                        data-testid={`download-${r.docType}-${fmt}`}
                        onClick={async () => {
                          try {
                            await contentEngineApi.downloadAsset(fmt, r.assetId, `${r.docType}.${fmt}`);
                          } catch (err) {
                            toast.error(err.message || 'Download failed');
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-slate-900 text-white hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200 transition-colors"
                      >
                        <Download className="h-3 w-3" /> .{fmt}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}
      {/* Review */}
      <SectionCard
        title="Review Generated Content"
        subtitle={
          activeAsset
            ? `${activeAsset.docType ? activeAsset.docType.replace('_', ' ').replace(/\b\w/g, (m) => m.toUpperCase()) + ' • ' : ''}${activeAsset.title || ''}`
            : `${selectedType || 'Content'} • ${boardLabel} • Class ${classId} • ${subject}${chapter ? ` • ${chapter}` : ''}`
        }
        icon={Eye}
        actions={
          activeAsset ? (
            <Badge variant="secondary" className="text-[10px] capitalize" data-testid="active-doctype-badge">
              {activeAsset.docType || 'content'}
            </Badge>
          ) : null
        }
      >
        <Tabs value={reviewTab} onValueChange={setReviewTab}>
          <TabsList className="bg-slate-100 dark:bg-slate-800 mb-4">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  data-testid={`review-tab-${t.id}`}
                  className="gap-1.5"
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {tabs.map((t) => (
            <TabsContent key={t.id} value={t.id}>
              <ReviewTabContent
                tabId={t.id}
                label={t.label}
                generatedAsset={activeAsset}
                lessonKit={lessonKit}
                isGenerating={isGenerating}
              />
            </TabsContent>
          ))}
        </Tabs>
      </SectionCard>

      {/* Micro AI Actions */}
      <SectionCard
        title="Micro AI Actions"
        subtitle="Iterate quickly — refine the generated content"
        icon={Wand2}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="micro-actions">
          {[
            { id: 'simplify', label: 'Simplify', icon: ArrowDown, tone: 'blue' },
            { id: 'complexity', label: 'Increase Complexity', icon: Brain, tone: 'purple' },
            { id: 'add-questions', label: 'Add Questions', icon: Plus, tone: 'emerald' },
            { id: 'convert-format', label: 'Convert Format', icon: RefreshCw, tone: 'amber' },
          ].map((a) => {
            const Icon = a.icon;
            const toneMap = {
              blue: 'hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20',
              purple: 'hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20',
              emerald: 'hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
              amber: 'hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20',
            };
            return (
              <button
                key={a.id}
                data-testid={`micro-action-${a.id}`}
                onClick={() => handleMicroAction(a.id, a.label)}
                disabled={!generatedAsset || isIterating}
                className={cn(
                  'flex items-center gap-2.5 p-3 rounded-lg border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 transition-colors text-left',
                  toneMap[a.tone],
                  (!generatedAsset || isIterating) && 'opacity-50 cursor-not-allowed'
                )}
              >
                <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  {isIterating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
                </div>
                <div className="text-sm font-medium">{a.label}</div>
              </button>
            );
          })}
        </div>
      </SectionCard>

      {/* Assign Inline */}
      {mode === 'school' && schoolId ? (
        <SectionCard
          title="Assignment Layer"
          subtitle="Push this content to all students in the chosen class"
          icon={Send}
          actions={
            <Badge variant="secondary" className="text-[10px] font-mono">
              schoolId: {String(schoolId).slice(0, 8)}…
            </Badge>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Assign To" hint={
              classSections?.length
                ? `${classSections.length} class${classSections.length === 1 ? '' : 'es'} available`
                : 'No active classes found'
            }>
              <Select value={assignTarget} onValueChange={setAssignTarget}>
                <SelectTrigger data-testid="assign-target">
                  <SelectValue placeholder="Pick class & section" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {classSections.map((cs) => (
                    <SelectItem
                      key={cs.label}
                      value={cs.label}
                      data-testid={`assign-target-option-${cs.class}-${cs.section || 'all'}`}
                    >
                      <span className="flex items-center gap-2">
                        <Users className="h-3.5 w-3.5 text-slate-500" />
                        {cs.label}
                        <Badge variant="secondary" className="text-[9px]">
                          {cs.studentCount} student{cs.studentCount === 1 ? '' : 's'}
                        </Badge>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Assignment Type">
              <Select value={assignmentType} onValueChange={setAssignmentType}>
                <SelectTrigger data-testid="assignment-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSIGNMENT_TYPES.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Due Date">
              <Input
                data-testid="due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </Field>
          </div>
          {assignTarget && (
            <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 text-emerald-600" />
              {(() => {
                const sel = classSections.find((c) => c.label === assignTarget);
                return sel
                  ? `Will reach ${sel.studentCount} student${sel.studentCount === 1 ? '' : 's'} in ${sel.label}.`
                  : `Target: ${assignTarget}`;
              })()}
            </div>
          )}
          <div className="mt-4 flex items-center gap-2">
            <Button
              data-testid="assign-now-btn"
              onClick={onAssign}
              disabled={isAssigning || !assignTarget || (!generatedAsset && !lessonKit)}
            >
              {isAssigning ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Assigning…</>
              ) : (
                <><Send className="h-4 w-4 mr-1.5" /> {assignmentId ? 'Re-assign' : 'Assign Now'}</>
              )}
            </Button>
            <Button variant="outline" onClick={() => toast.info('Saved as draft.')}>
              <Save className="h-4 w-4 mr-1.5" /> Save Draft
            </Button>
            {assignmentId && (
              <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]" data-testid="assigned-badge">
                Assigned • {String(assignmentId).slice(0, 10)}…
              </Badge>
            )}
          </div>
        </SectionCard>
      ) : (
        <div className="p-4 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/40 text-sm text-muted-foreground flex items-center gap-2">
          <User className="h-4 w-4" />
          Assignment layer is available in <strong className="text-foreground">School Mode</strong>.
          Switch modes from the top-right to assign to a class.
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Smart Insight Demo Dialog — 30-second auto-playing soft-sell
// for individual users, showing the panel populating with real
// metrics for a sample assignment.
// ─────────────────────────────────────────────────────────────
const _DEMO_STAGES = [
  {
    label: 'Step 1 — picking a task',
    caption: 'Teacher selects "Test" for Class 9 Quadratic Equations.',
    diff: [50, 40, 10],
    time: 30, kit: false,
    skills: ['Recall', 'Understanding'],
    qtypes: ['MCQ'],
    questions: 8, marks: 30,
  },
  {
    label: 'Step 2 — adding curriculum',
    caption: 'CBSE · Class 9 · Mathematics · Chapter 4 selected.',
    diff: [40, 45, 15],
    time: 35, kit: false,
    skills: ['Recall', 'Understanding', 'Application'],
    qtypes: ['MCQ', 'Short Answer'],
    questions: 10, marks: 40,
  },
  {
    label: 'Step 3 — tuning difficulty',
    caption: 'Difficulty rebalanced to a board-aligned 30/50/20 split.',
    diff: [30, 50, 20],
    time: 45, kit: false,
    skills: ['Application', 'Fluency', 'Analysis'],
    qtypes: ['MCQ', 'Short Answer', 'Long Answer'],
    questions: 10, marks: 50,
  },
  {
    label: 'Step 4 — turning on Lesson Kit',
    caption: 'Lesson Kit auto-bundles PPT + flow + assessment + homework.',
    diff: [25, 50, 25],
    time: 60, kit: true,
    skills: ['Application', 'Analysis', 'Evaluation', 'Problem Solving'],
    qtypes: ['MCQ', 'Short Answer', 'Long Answer', 'Case'],
    questions: 12, marks: 60,
  },
  {
    label: 'Step 5 — ready to fan out',
    caption: 'Smart Insights confirm the assignment hits CBSE-9 norms.',
    diff: [20, 55, 25],
    time: 60, kit: true,
    skills: ['Analysis', 'Evaluation', 'Problem Solving', 'Creativity'],
    qtypes: ['MCQ', 'Short Answer', 'Long Answer', 'Case'],
    questions: 12, marks: 60,
  },
];
const _DEMO_TOTAL_MS = 30000; // 30s
const _DEMO_STAGE_MS = _DEMO_TOTAL_MS / _DEMO_STAGES.length;

function SmartInsightDemoDialog({ onClose }) {
  const [stageIdx, setStageIdx] = useState(0);
  const [progress, setProgress] = useState(0); // 0..100 across the whole demo
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const start = Date.now();
    const startProgress = progress;
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      const overall = Math.min(100, startProgress + (elapsed / _DEMO_TOTAL_MS) * 100);
      setProgress(overall);
      const idx = Math.min(_DEMO_STAGES.length - 1, Math.floor((overall / 100) * _DEMO_STAGES.length));
      setStageIdx(idx);
      if (overall >= 100) clearInterval(id);
    }, 200);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  const stage = _DEMO_STAGES[stageIdx];
  const totalDiff = stage.diff.reduce((a, b) => a + b, 0) || 1;
  const easy = Math.round((stage.diff[0] / totalDiff) * 100);
  const medium = Math.round((stage.diff[1] / totalDiff) * 100);
  const hard = 100 - easy - medium;
  const finished = progress >= 100;

  return (
    <div
      data-testid="smart-insight-demo-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl border border-slate-700 bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/60 bg-slate-950/40">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-300" />
            <div>
              <div className="text-sm font-semibold">Smart Insights — School Mode preview</div>
              <div className="text-[10px] text-white/60">
                {finished ? 'Replay or close' : `Auto-playing · ${Math.max(0, Math.ceil((100 - progress) * _DEMO_TOTAL_MS / 100000))}s left`}
              </div>
            </div>
          </div>
          <button
            data-testid="smart-insight-demo-close"
            onClick={onClose}
            className="text-white/60 hover:text-white text-xs"
          >
            ✕
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-slate-800">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-amber-300 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-0">
          {/* Left — narrative */}
          <div className="p-5 space-y-3 border-r border-slate-700/60">
            <div className="text-[10px] uppercase tracking-wide text-amber-300 font-semibold">
              {stage.label}
            </div>
            <div
              key={stageIdx}
              className="text-sm text-white/90 leading-relaxed animate-in fade-in"
            >
              {stage.caption}
            </div>

            <div className="rounded-lg bg-slate-950/50 p-3 border border-slate-700/60">
              <div className="text-[10px] uppercase tracking-wide text-white/60 mb-2">Sample assignment</div>
              <div className="text-xs font-medium">Test — Quadratic Equations</div>
              <div className="text-[10px] text-white/60">CBSE · Class 9 · Mathematics</div>
              <div className="flex items-center gap-2 mt-2 text-[10px]">
                <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-mono">
                  {stage.questions} Q
                </span>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-mono">
                  {stage.marks} marks
                </span>
                <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/70 font-mono">
                  {stage.time} min
                </span>
              </div>
            </div>

            {/* Stage dots */}
            <div className="flex items-center gap-1.5 pt-2">
              {_DEMO_STAGES.map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'h-1.5 rounded-full transition-all',
                    i === stageIdx ? 'w-6 bg-amber-300' : i < stageIdx ? 'w-3 bg-amber-300/60' : 'w-3 bg-white/15'
                  )}
                />
              ))}
            </div>
          </div>

          {/* Right — live-populating panel */}
          <div className="p-4 bg-slate-950/30">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-7 w-7 rounded-lg bg-white/10 flex items-center justify-center">
                <Sparkles className="h-3.5 w-3.5 text-amber-300" />
              </div>
              <div>
                <div className="text-xs font-semibold">Smart Insights</div>
                <div className="text-[9px] text-white/60">Live preview</div>
              </div>
            </div>

            <div>
              <div className="text-[9px] uppercase tracking-wide text-white/60 mb-1">Difficulty</div>
              <div className="flex h-1.5 rounded-full overflow-hidden bg-white/10">
                <div className="bg-emerald-400 transition-all duration-700" style={{ width: `${easy}%` }} />
                <div className="bg-amber-400 transition-all duration-700" style={{ width: `${medium}%` }} />
                <div className="bg-rose-400 transition-all duration-700" style={{ width: `${hard}%` }} />
              </div>
              <div className="mt-1 flex justify-between text-[9px] text-white/70">
                <span>E {easy}%</span><span>M {medium}%</span><span>H {hard}%</span>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between text-[10px]">
              <span className="text-white/60">Estimated time</span>
              <span className="font-semibold text-white">~{stage.time} min</span>
            </div>
            <div className="mt-1.5 flex items-center justify-between text-[10px]">
              <span className="text-white/60">Lesson Kit</span>
              <span className={stage.kit ? 'text-emerald-300 font-semibold' : 'text-white/40'}>
                {stage.kit ? 'Enabled' : 'Disabled'}
              </span>
            </div>

            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="text-[9px] uppercase tracking-wide text-white/60 mb-1.5">Skills covered</div>
              <div className="flex flex-wrap gap-1">
                {stage.skills.map((s) => (
                  <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
                    {s}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="text-[9px] uppercase tracking-wide text-white/60 mb-1.5">Question types</div>
              <div className="flex flex-wrap gap-1">
                {stage.qtypes.map((q) => (
                  <span key={q} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/80">
                    {q}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA footer */}
        <div className="px-5 py-3 border-t border-slate-700/60 bg-slate-950/40 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-white/70">
            <strong className="text-white">School Mode</strong> unlocks live difficulty / time / skill insights and 6-step assignment workflow.
          </div>
          <div className="flex items-center gap-2">
            {!finished ? (
              <button
                data-testid="smart-insight-demo-pause"
                onClick={() => setPaused((p) => !p)}
                className="text-[11px] text-white/70 hover:text-white px-2 py-1"
              >
                {paused ? 'Resume' : 'Pause'}
              </button>
            ) : (
              <button
                data-testid="smart-insight-demo-replay"
                onClick={() => { setProgress(0); setStageIdx(0); }}
                className="text-[11px] text-white/70 hover:text-white px-2 py-1"
              >
                Replay
              </button>
            )}
            <button
              data-testid="smart-insight-demo-upgrade"
              onClick={() => {
                // v96 — instead of firing an internal upgrade-required
                // event, open a WhatsApp chat with the Actanon sales team.
                const msg = encodeURIComponent(
                  "Hi Actanon team! I saw the Smart Insights demo in Actanon Edu and would like to know more about School Mode."
                );
                window.open(`https://wa.me/918796869006?text=${msg}`, '_blank', 'noopener,noreferrer');
                onClose();
              }}
              className="text-[11px] font-semibold bg-amber-400 hover:bg-amber-300 text-slate-900 px-3 py-1.5 rounded-md transition-colors"
            >
              Talk to us about School Mode
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Smart Insight Panel
// ─────────────────────────────────────────────────────────────
function SmartInsightPanel({
  boardLabel, difficulty, duration, teachingObjective, lessonKitMode, questionTypes, mode = 'school',
}) {
  const totalDiff = difficulty.reduce((a, b) => a + b, 0) || 1;
  const easy = Math.round((difficulty[0] / totalDiff) * 100);
  const medium = Math.round((difficulty[1] / totalDiff) * 100);
  const hard = 100 - easy - medium;

  const skills = useMemo(() => {
    const map = {
      concept_clarity: ['Understanding', 'Recall'],
      practice: ['Application', 'Fluency'],
      assessment: ['Analysis', 'Evaluation'],
      skill_building: ['Problem Solving', 'Creativity'],
    };
    return map[teachingObjective] || ['Engagement'];
  }, [teachingObjective]);

  const isIndividual = mode === 'individual';
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <motion.aside
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className="h-fit space-y-4"
      data-testid="smart-insight-panel"
    >
      <div className="relative rounded-2xl border border-slate-200/60 dark:border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 text-white p-5 shadow-soft-lg">
        {/* Greyed-out school-only overlay for individual users */}
        {isIndividual && (
          <div
            data-testid="smart-insight-school-overlay"
            className="absolute inset-0 z-10 rounded-2xl bg-slate-950/75 backdrop-blur-[2px] flex flex-col items-center justify-center text-center p-5"
          >
            <div className="h-9 w-9 rounded-full bg-amber-500/20 flex items-center justify-center mb-2">
              <Lock className="h-4 w-4 text-amber-300" />
            </div>
            <div className="text-sm font-semibold text-white">Smart Insights</div>
            <div className="text-[11px] text-white/70 mt-1 max-w-[230px]">
              Live previews of difficulty, time and skill coverage are part of <strong className="text-white">School Mode</strong>.
              Individual accounts can still generate documents from Section A or B.
            </div>
            <button
              data-testid="smart-insight-see-school-demo"
              onClick={() => {
                // v96 — "See it in School Mode" also opens WhatsApp so a
                // sales rep can walk the teacher through it live.
                const msg = encodeURIComponent(
                  "Hi Actanon team! I'd like to see Smart Insights in School Mode live. Can you show me a demo?"
                );
                window.open(`https://wa.me/918796869006?text=${msg}`, '_blank', 'noopener,noreferrer');
              }}
              className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300 hover:text-amber-200 transition-colors"
            >
              See it in School Mode <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>
        )}
        {isIndividual && demoOpen && (
          <SmartInsightDemoDialog onClose={() => setDemoOpen(false)} />
        )}
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-amber-300" />
          </div>
          <div>
            <div className="text-sm font-semibold">Smart Insights</div>
            <div className="text-[11px] text-white/60">Live preview</div>
          </div>
        </div>

        {/* Difficulty */}
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-wide text-white/60 mb-2">
            Difficulty Breakdown
          </div>
          <div className="flex h-2 rounded-full overflow-hidden bg-white/10">
            <div className="bg-emerald-400" style={{ width: `${easy}%` }} />
            <div className="bg-amber-400" style={{ width: `${medium}%` }} />
            <div className="bg-rose-400" style={{ width: `${hard}%` }} />
          </div>
          <div className="mt-2 grid grid-cols-3 text-[11px] text-white/80">
            <span>E {easy}%</span>
            <span className="text-center">M {medium}%</span>
            <span className="text-right">H {hard}%</span>
          </div>
        </div>

        {/* Time */}
        <InsightRow label="Estimated Time" value={`~${duration} min`} icon={Clock} />
        <InsightRow
          label="Lesson Kit"
          value={lessonKitMode ? 'Enabled' : 'Disabled'}
          icon={Layers}
          valueClass={lessonKitMode ? 'text-emerald-300' : 'text-white/50'}
        />

        {/* Skills */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="text-[11px] uppercase tracking-wide text-white/60 mb-2">
            Skills Covered
          </div>
          <div className="flex flex-wrap gap-1.5">
            {skills.map((s) => (
              <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 border border-white/10">
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Question types */}
        {questionTypes.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="text-[11px] uppercase tracking-wide text-white/60 mb-2">
              Question Types
            </div>
            <div className="flex flex-wrap gap-1.5">
              {questionTypes.map((q) => (
                <span key={q} className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 border border-white/10">
                  {q}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Board compliance */}
        {boardLabel && (
          <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-300" />
            <div className="text-xs">
              <span className="text-white/60">Board compliance</span>
              <div className="font-semibold">{boardLabel} Aligned</div>
            </div>
          </div>
        )}
      </div>

      {/* Quick tips */}
      <div className="rounded-2xl border border-slate-200/60 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-soft">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="h-4 w-4 text-amber-500" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Tip
          </span>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Enable <strong className="text-foreground">Lesson Kit Mode</strong> to auto-generate PPT,
          teaching flow, class questions, homework and assessment in one click.
          {mode !== 'school' && <>{' '}<span className="text-amber-700 dark:text-amber-300 font-semibold">Only available for school mode.</span></>}
        </p>
      </div>
    </motion.aside>
  );
}

// ─────────────────────────────────────────────────────────────
// Shared UI primitives
// ─────────────────────────────────────────────────────────────
function SectionCard({ title, subtitle, icon: Icon, actions, children, tone = 'neutral', sectionLetter }) {
  // Task E / Enhanced segregation — Tone applies a thick accent bar,
  // tinted header, ring, AND a giant ghost letter watermark so Section A
  // vs Section B reads instantly even on a quick mobile scroll.
  const tones = {
    neutral: {
      ring: '',
      bar:   'bg-slate-100 dark:bg-slate-800',
      headerBg: 'bg-white dark:bg-slate-900',
      headerBorder: 'border-slate-200/60 dark:border-slate-800',
      iconBg: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
      cardBg: 'bg-white dark:bg-slate-900',
      border: 'border-slate-200/60 dark:border-slate-800',
      watermark: 'text-slate-100 dark:text-slate-800/60',
      letterChip: '',
    },
    amber: {
      ring: 'ring-2 ring-amber-300/50 dark:ring-amber-900/40',
      bar:   'h-1.5 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-500',
      headerBg: 'bg-gradient-to-br from-amber-50 via-orange-50/40 to-amber-50/60 dark:from-amber-950/30 dark:via-orange-950/20 dark:to-amber-950/20',
      headerBorder: 'border-amber-200/70 dark:border-amber-900/40',
      iconBg: 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/20',
      cardBg: 'bg-amber-50/30 dark:bg-amber-950/10',
      border: 'border-amber-200/60 dark:border-amber-900/30',
      watermark: 'text-amber-200/30 dark:text-amber-900/15',
      letterChip: 'bg-amber-500 text-white shadow-md shadow-amber-500/30',
    },
    emerald: {
      ring: 'ring-2 ring-emerald-300/50 dark:ring-emerald-900/40',
      bar:   'h-1.5 bg-gradient-to-r from-emerald-400 via-teal-500 to-emerald-500',
      headerBg: 'bg-gradient-to-br from-emerald-50 via-teal-50/40 to-emerald-50/60 dark:from-emerald-950/30 dark:via-teal-950/20 dark:to-emerald-950/20',
      headerBorder: 'border-emerald-200/70 dark:border-emerald-900/40',
      iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/20',
      cardBg: 'bg-emerald-50/30 dark:bg-emerald-950/10',
      border: 'border-emerald-200/60 dark:border-emerald-900/30',
      watermark: 'text-emerald-200/30 dark:text-emerald-900/15',
      letterChip: 'bg-emerald-500 text-white shadow-md shadow-emerald-500/30',
    },
  };
  tones.blue = {
    ring: '',
    bar: 'h-1 bg-gradient-to-r from-ae-blue via-ae-blue to-ae-cyan',
    headerBg: 'bg-white dark:bg-slate-900',
    headerBorder: 'border-slate-200/80 dark:border-slate-800',
    iconBg: 'bg-ae-blue text-white shadow-blue',
    cardBg: 'bg-white dark:bg-slate-900',
    border: 'border-slate-200/80 dark:border-slate-800',
    watermark: 'text-ae-blue/[0.05]',
    letterChip: 'bg-ae-navy text-ae-cyan',
  };
  tones.cyan = {
    ...tones.blue,
    bar: 'h-1 bg-gradient-to-r from-ae-cyan via-ae-cyan to-ae-blue',
    iconBg: 'bg-ae-navy text-ae-cyan',
    watermark: 'text-ae-cyan/[0.08]',
    letterChip: 'bg-ae-cyan text-ae-navy',
  };
  const tk = tones[tone] || tones.neutral;
  const barHeight = tone === 'neutral' ? 'h-1' : '';
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'relative rounded-2xl border shadow-soft overflow-hidden',
        tk.cardBg, tk.border, tk.ring
      )}
    >
      {/* Giant ghost letter watermark — invisible on neutral; appears for A/B sections. */}
      {sectionLetter && (
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none select-none absolute -top-4 -right-2 sm:-right-4 font-black leading-none',
            'text-[120px] sm:text-[180px] tracking-tighter',
            tk.watermark
          )}
        >
          {sectionLetter}
        </div>
      )}
      {/* Accent bar */}
      <div className={cn('w-full', tk.bar, barHeight)} />
      <div className={cn(
        'relative flex items-center justify-between px-5 sm:px-6 py-4 border-b',
        tk.headerBg, tk.headerBorder
      )}>
        <div className="flex items-center gap-3 min-w-0">
          {/* Section letter chip — instantly identifiable on mobile */}
          {sectionLetter && (
            <div className={cn(
              'h-9 w-9 rounded-lg flex items-center justify-center text-base font-black shrink-0',
              tk.letterChip
            )} data-testid={`section-letter-${sectionLetter.toLowerCase()}`}>
              {sectionLetter}
            </div>
          )}
          {Icon && (
            <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center shrink-0', tk.iconBg)}>
              <Icon className="h-4 w-4" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-heading font-bold sm:truncate">{title}</div>
            {subtitle && <div className="text-xs text-muted-foreground sm:truncate">{subtitle}</div>}
          </div>
        </div>
        <div className="shrink-0">{actions}</div>
      </div>
      <div className="relative p-5 sm:p-6">{children}</div>
    </motion.div>
  );
}

function ConfigSection({ id, open, setOpen, title, subtitle, icon: Icon, badge, badgeTone = 'neutral', children }) {
  const toneClass = {
    neutral: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  }[badgeTone];
  return (
    <Collapsible
      open={open}
      onOpenChange={(v) => setOpen(v ? id : '')}
      className="rounded-2xl border border-slate-200/60 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-soft overflow-hidden"
      data-testid={`config-section-${id}`}
    >
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between px-5 sm:px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
          <div className="flex items-center gap-3">
            {Icon && (
              <div className="h-9 w-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-slate-300">
                <Icon className="h-4 w-4" />
              </div>
            )}
            <div className="text-left">
              <div className="text-sm font-semibold flex items-center gap-2">
                {title}
                {badge && (
                  <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', toneClass)}>
                    {badge}
                  </span>
                )}
              </div>
              {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
            </div>
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              open && 'rotate-180'
            )}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-5 sm:px-6 pb-5 sm:pb-6 border-t border-slate-200/60 dark:border-slate-800 pt-5">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        {label}
        {required && <span className="text-rose-500">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-800">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold mt-0.5 truncate">{value}</div>
    </div>
  );
}

function ToggleRow({ testId, label, description, checked, onChange, icon: Icon }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-start gap-2.5">
        {Icon && (
          <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-slate-300">
            <Icon className="h-4 w-4" />
          </div>
        )}
        <div>
          <div className="text-sm font-medium">{label}</div>
          {description && <div className="text-xs text-muted-foreground">{description}</div>}
        </div>
      </div>
      <Switch data-testid={testId} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function InsightRow({ label, value, icon: Icon, valueClass }) {
  return (
    <div className="mt-3 flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 text-white/70">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </span>
      <span className={cn('font-semibold', valueClass || 'text-white')}>{value}</span>
    </div>
  );
}

function PreviewPlaceholder({ label }) {
  return (
    <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-10 text-center bg-slate-50/40 dark:bg-slate-900/40">
      <div className="mx-auto h-12 w-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
        <Eye className="h-5 w-5 text-slate-500" />
      </div>
      <div className="mt-3 text-sm font-semibold">{label} preview</div>
      <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
        Generated output will appear here once you click <strong>Generate</strong>.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ReviewTabContent — renders real generated content per tab + per docType
// ─────────────────────────────────────────────────────────────
function ReviewTabContent({ tabId, label, generatedAsset, lessonKit, isGenerating }) {
  if (isGenerating) {
    return (
      <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-10 text-center bg-slate-50/40 dark:bg-slate-900/40">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-500" />
        <div className="mt-3 text-sm font-semibold">Generating {label.toLowerCase()}…</div>
        <div className="text-xs text-muted-foreground mt-1">GPT-4o is curating curriculum-aligned content.</div>
      </div>
    );
  }

  const content = generatedAsset?.content;
  const docType = generatedAsset?.docType;

  // Content tab — branch by docType for proper rendering
  if (tabId === 'content') {
    if (!content || content._lessonKit) {
      if (lessonKit) {
        return (
          <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 p-5 bg-white dark:bg-slate-900" data-testid="content-preview">
            <div className="text-sm font-semibold mb-1">{lessonKit.title}</div>
            <div className="text-xs text-muted-foreground">
              Lesson Kit generated. Switch to <strong>PPT Slides</strong> / <strong>Teaching Flow</strong> / <strong>Assessment</strong> tabs to review components.
            </div>
          </div>
        );
      }
      return <PreviewPlaceholder label={label} />;
    }

    // ── Summary preview ──────────────────────────────────────
    if (docType === 'summary') {
      return (
        <div
          className="rounded-xl border border-slate-200/70 dark:border-slate-800 p-5 bg-white dark:bg-slate-900 space-y-4"
          data-testid="content-preview-summary"
        >
          {content.title && <div className="text-lg font-heading font-semibold">{content.title}</div>}
          {content.overview && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Overview</div>
              <p className="text-sm leading-relaxed">{content.overview}</p>
            </div>
          )}
          {Array.isArray(content.keyConcepts) && content.keyConcepts.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Key Concepts</div>
              <ul className="space-y-2">
                {content.keyConcepts.map((kc, i) => (
                  <li key={i} className="text-sm">
                    <strong>{kc.concept}:</strong>{' '}
                    <span className="text-muted-foreground">{kc.explanation}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(content.importantFormulas) && content.importantFormulas.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Important Formulas</div>
              <ul className="space-y-1 list-disc list-inside text-sm font-mono">
                {content.importantFormulas.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(content.quickNotes) && content.quickNotes.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Quick Notes</div>
              <ul className="space-y-1 list-disc list-inside text-sm">
                {content.quickNotes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(content.examTips) && content.examTips.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Exam Tips</div>
              <ul className="space-y-1 list-disc list-inside text-sm text-amber-800 dark:text-amber-300">
                {content.examTips.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </div>
          )}
        </div>
      );
    }

    // ── Lesson Plan preview ──────────────────────────────────
    if (docType === 'lesson_plan') {
      return (
        <div
          className="rounded-xl border border-slate-200/70 dark:border-slate-800 p-5 bg-white dark:bg-slate-900 space-y-4"
          data-testid="content-preview-lesson-plan"
        >
          {content.title && <div className="text-lg font-heading font-semibold">{content.title}</div>}
          {Array.isArray(content.learningObjectives) && content.learningObjectives.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Learning Objectives</div>
              <ul className="space-y-1 list-disc list-inside text-sm">
                {content.learningObjectives.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(content.materials) && content.materials.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Materials</div>
              <div className="text-sm">{content.materials.join(' · ')}</div>
            </div>
          )}
          {Array.isArray(content.priorKnowledge) && content.priorKnowledge.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Prior Knowledge</div>
              <ul className="space-y-1 list-disc list-inside text-sm">
                {content.priorKnowledge.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(content.activities) && content.activities.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Activities</div>
              <div className="space-y-2">
                {content.activities.map((a, i) => (
                  <div key={i} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="text-sm font-semibold flex items-center gap-2">
                      {a.phase}
                      {a.duration ? <Badge variant="secondary" className="text-[9px]">{a.duration} min</Badge> : null}
                    </div>
                    <div className="text-sm mt-1">{a.description}</div>
                    {Array.isArray(a.questions) && a.questions.length > 0 && (
                      <ul className="mt-2 space-y-0.5 list-disc list-inside text-xs text-muted-foreground">
                        {a.questions.map((q, k) => <li key={k}>{q}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {content.differentiation && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {content.differentiation.support && (
                <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-sm">
                  <div className="text-[10px] uppercase tracking-wide text-blue-700 dark:text-blue-300 mb-0.5">Support</div>
                  {content.differentiation.support}
                </div>
              )}
              {content.differentiation.extension && (
                <div className="p-2.5 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-sm">
                  <div className="text-[10px] uppercase tracking-wide text-purple-700 dark:text-purple-300 mb-0.5">Extension</div>
                  {content.differentiation.extension}
                </div>
              )}
            </div>
          )}
          {content.homework && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Homework</div>
              <div className="text-sm">{content.homework}</div>
            </div>
          )}
          {content.reflection && (
            <div className="text-xs italic text-muted-foreground border-t pt-2">
              Reflection: {content.reflection}
            </div>
          )}
          {/* ── Day-wise Schedule (additive — only when feature-flagged engine produced it) ── */}
          {Array.isArray(content.dayWisePlan) && content.dayWisePlan.length > 0 && (
            <div className="pt-3 mt-3 border-t-2 border-dashed border-emerald-300 dark:border-emerald-800" data-testid="content-preview-daywise">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                <div className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-400 font-semibold">
                  Day-wise Schedule ({content.dayWisePlan.length} day{content.dayWisePlan.length === 1 ? '' : 's'})
                </div>
              </div>
              <div className="space-y-3">
                {content.dayWisePlan.map((d, idx) => (
                  <details
                    key={idx}
                    open={idx === 0}
                    data-testid={`daywise-day-${d?.dayNumber || idx + 1}`}
                    className="rounded-lg border border-emerald-200/70 dark:border-emerald-900/50 bg-emerald-50/30 dark:bg-emerald-900/10 group"
                  >
                    <summary className="cursor-pointer list-none p-3 flex items-center justify-between gap-2 hover:bg-emerald-50/60 dark:hover:bg-emerald-900/20 rounded-lg">
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge className="bg-emerald-600 text-white text-[10px] shrink-0">Day {d?.dayNumber || idx + 1}</Badge>
                        <div className="text-sm font-semibold truncate">{d?.title || d?.topic || 'Lesson'}</div>
                      </div>
                      <span className="text-[10px] text-emerald-700 dark:text-emerald-400 shrink-0 group-open:rotate-180 transition-transform">▾</span>
                    </summary>
                    <div className="px-3 pb-3 pt-1 space-y-2 text-sm">
                      {d?.topic && d?.topic !== d?.title && (
                        <div className="text-xs text-muted-foreground italic">{d.topic}</div>
                      )}
                      {Array.isArray(d?.objectives) && d.objectives.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Objectives</div>
                          <ul className="list-disc list-inside space-y-0.5">{d.objectives.map((x, k) => <li key={k} className="text-xs">{x}</li>)}</ul>
                        </div>
                      )}
                      {d?.methodology && (
                        <div className="text-xs"><span className="text-muted-foreground">Methodology: </span>{d.methodology}</div>
                      )}
                      {Array.isArray(d?.activities) && d.activities.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Activities</div>
                          <ul className="space-y-1">
                            {d.activities.map((a, k) => (
                              <li key={k} className="text-xs">
                                <strong>{a?.name}</strong>
                                {a?.durationMinutes ? <span className="ml-1 text-muted-foreground">({a.durationMinutes} min)</span> : null}
                                {a?.description ? <span className="ml-1">— {a.description}</span> : null}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {Array.isArray(d?.classwork) && d.classwork.length > 0 && (
                        <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 p-2">
                          <div className="text-[10px] uppercase tracking-wide text-blue-700 dark:text-blue-300 mb-0.5">Classwork</div>
                          <ul className="list-disc list-inside space-y-0.5 text-xs">{d.classwork.map((x, k) => <li key={k}>{x}</li>)}</ul>
                        </div>
                      )}
                      {Array.isArray(d?.homework) && d.homework.length > 0 && (
                        <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 p-2">
                          <div className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-0.5">Homework</div>
                          <ul className="list-disc list-inside space-y-0.5 text-xs">{d.homework.map((x, k) => <li key={k}>{x}</li>)}</ul>
                        </div>
                      )}
                      {d?.labActivity && typeof d.labActivity === 'object' && (
                        <div className="rounded-md bg-violet-50 dark:bg-violet-900/20 p-2 border border-violet-200 dark:border-violet-800/50">
                          <div className="text-[10px] uppercase tracking-wide text-violet-700 dark:text-violet-300 mb-1 font-semibold">Laboratory Activity</div>
                          {d.labActivity.title && <div className="text-xs font-medium">{d.labActivity.title}</div>}
                          {d.labActivity.objective && <div className="text-xs italic text-muted-foreground">{d.labActivity.objective}</div>}
                          {Array.isArray(d.labActivity.procedure) && d.labActivity.procedure.length > 0 && (
                            <ol className="list-decimal list-inside space-y-0.5 text-xs mt-1">{d.labActivity.procedure.map((x, k) => <li key={k}>{x}</li>)}</ol>
                          )}
                          {d.labActivity.observation && <div className="text-xs mt-1"><strong>Expected: </strong>{d.labActivity.observation}</div>}
                        </div>
                      )}
                      {d?.assessment && (
                        <div className="text-xs text-emerald-800 dark:text-emerald-300 border-l-2 border-emerald-400 pl-2">
                          <strong>Checkpoint:</strong> {d.assessment}
                        </div>
                      )}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    // ── Teaching Flow preview ────────────────────────────────
    if (docType === 'teaching_flow') {
      return (
        <div
          className="rounded-xl border border-slate-200/70 dark:border-slate-800 p-5 bg-white dark:bg-slate-900 space-y-3"
          data-testid="content-preview-teaching-flow"
        >
          {content.title && <div className="text-lg font-heading font-semibold">{content.title}</div>}
          {content.totalDuration && (
            <div className="text-xs text-muted-foreground">
              Total duration: <strong>{content.totalDuration} min</strong>
            </div>
          )}
          {Array.isArray(content.phases) && content.phases.length > 0 && (
            <div className="space-y-2">
              {content.phases.map((p, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <div className="text-xs font-mono text-slate-500 w-16 flex-shrink-0">
                    {p.from}–{p.to}m
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{p.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.activity}</div>
                    {p.teacherDoes && <div className="text-[11px] mt-1">👩‍🏫 {p.teacherDoes}</div>}
                    {p.studentsDo && <div className="text-[11px]">🎒 {p.studentsDo}</div>}
                    {p.checkForUnderstanding && (
                      <div className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-1">
                        ✓ {p.checkForUnderstanding}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {Array.isArray(content.materials) && content.materials.length > 0 && (
            <div className="text-xs text-muted-foreground pt-2 border-t">
              Materials: {content.materials.join(', ')}
            </div>
          )}
        </div>
      );
    }

    // ── PPT preview (slides) ─────────────────────────────────
    if (docType === 'ppt') {
      const slides = Array.isArray(content.slides) ? content.slides : [];
      if (!slides.length) return <PreviewPlaceholder label={label} />;
      return (
        <div
          className="rounded-xl border border-slate-200/70 dark:border-slate-800 p-5 bg-white dark:bg-slate-900"
          data-testid="content-preview-ppt"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">
              {content.title || 'Presentation'} • {slides.length} slide{slides.length === 1 ? '' : 's'}
            </div>
            <Badge variant="secondary" className="text-[10px]">
              {content.provider === 'presenton' ? 'Presenton AI' : 'Internal Fallback'}
            </Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {slides.map((s, i) => (
              <div
                key={i}
                className="aspect-video rounded-lg border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 p-3 flex flex-col"
              >
                <div className="text-[11px] font-semibold line-clamp-1">{s.title || `Slide ${i + 1}`}</div>
                <div className="text-[10px] text-muted-foreground mt-1 line-clamp-3">
                  {Array.isArray(s.content)
                    ? s.content.slice(0, 3).map((c) => `• ${c}`).join('\n')
                    : String(s.content || '')}
                </div>
                <div className="mt-auto text-[9px] text-muted-foreground">#{i + 1}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // ── Assessment preview (default — sections + items) ──────
    if (docType === 'assessment' || (content.sections && content.sections.length > 0)) {
      return (
        <div
          className="rounded-xl border border-slate-200/70 dark:border-slate-800 p-5 bg-white dark:bg-slate-900 space-y-4"
          data-testid="content-preview-assessment"
        >
          {content.title && <div className="text-lg font-heading font-semibold">{content.title}</div>}
          {content.instructions && <div className="text-sm text-muted-foreground italic">{content.instructions}</div>}
          {(content.sections || []).map((section, idx) => (
            <div key={idx} className="border-l-4 border-slate-200 dark:border-slate-700 pl-4">
              <div className="text-sm font-semibold mb-2">
                {section.name} {section.marks ? <span className="text-xs text-muted-foreground">• {section.marks} marks</span> : null}
              </div>
              <ol className="space-y-2 list-decimal list-inside">
                {(section.items || []).map((item, i) => (
                  <li key={i} className="text-sm">
                    <span>{item.q}</span>
                    {item.marks ? <span className="ml-2 text-xs text-muted-foreground">[{item.marks}]</span> : null}
                    {item.difficulty && (
                      <Badge variant="secondary" className="ml-2 text-[9px]">{item.difficulty}</Badge>
                    )}
                    {item.type === 'MCQ' && item.options && (
                      <div className="ml-5 mt-1 text-xs text-muted-foreground">
                        {item.options.map((o, k) => <div key={k}>• {o}</div>)}
                      </div>
                    )}
                    {item.answer && (
                      <div className="ml-5 mt-1 text-xs text-emerald-700 dark:text-emerald-400">
                        <strong>Ans:</strong> {typeof item.answer === 'string' ? item.answer : JSON.stringify(item.answer)}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          ))}
          {content.totalMarks ? (
            <div className="text-xs text-muted-foreground pt-2 border-t">
              Total marks: <strong>{content.totalMarks}</strong>
            </div>
          ) : null}
        </div>
      );
    }

    // Fallback — best-effort raw JSON view
    return (
      <div
        className="rounded-xl border border-slate-200/70 dark:border-slate-800 p-5 bg-white dark:bg-slate-900"
        data-testid="content-preview-raw"
      >
        {content.title && <div className="text-lg font-heading font-semibold mb-2">{content.title}</div>}
        <pre className="text-xs whitespace-pre-wrap break-words text-muted-foreground max-h-96 overflow-y-auto">
          {JSON.stringify(content, null, 2)}
        </pre>
      </div>
    );
  }

  // PPT tab (lesson kit) — keep existing
  if (tabId === 'ppt') {
    const slides = lessonKit?.components?.slideCount
      ? Array.from({ length: lessonKit.components.slideCount }, (_, i) => ({ title: `Slide ${i + 1}`, content: ['Generated by ' + (lessonKit.components.pptProvider || 'engine')] }))
      : null;
    if (!lessonKit || !slides) return <PreviewPlaceholder label={label} />;
    return (
      <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 p-5 bg-white dark:bg-slate-900" data-testid="ppt-preview">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">{slides.length} slides</div>
          <Badge variant="secondary" className="text-[10px]">
            {lessonKit.components.pptProvider === 'presenton' ? 'Presenton AI' : 'Internal Fallback'}
          </Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {slides.map((s, i) => (
            <div key={i} className="aspect-video rounded-lg border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 p-3 flex flex-col justify-between">
              <div className="text-[11px] font-semibold truncate">{s.title}</div>
              <div className="text-[9px] text-muted-foreground">#{i + 1}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Teaching Flow tab (lesson kit)
  if (tabId === 'flow') {
    const flow = lessonKit?.components?.teachingFlow;
    if (!flow) return <PreviewPlaceholder label={label} />;
    return (
      <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 p-5 bg-white dark:bg-slate-900 space-y-3" data-testid="flow-preview">
        {(flow.phases || []).map((p, i) => (
          <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
            <div className="text-xs font-mono text-slate-500 w-16 flex-shrink-0">{p.from}-{p.to}m</div>
            <div>
              <div className="text-sm font-semibold">{p.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{p.activity}</div>
              {p.tips && <div className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">💡 {p.tips}</div>}
            </div>
          </div>
        ))}
        {flow.materials && flow.materials.length > 0 && (
          <div className="text-xs text-muted-foreground pt-2 border-t">
            Materials: {flow.materials.join(', ')}
          </div>
        )}
      </div>
    );
  }

  // Assessment tab (lesson kit)
  if (tabId === 'assessment') {
    if (!lessonKit?.components?.assessmentAssetId) return <PreviewPlaceholder label={label} />;
    return (
      <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 p-5 bg-white dark:bg-slate-900 text-sm" data-testid="assessment-preview">
        <div className="font-semibold mb-1">Assessment bundled</div>
        <div className="text-xs text-muted-foreground">
          Asset ID: <span className="font-mono">{lessonKit.components.assessmentAssetId}</span>
        </div>
      </div>
    );
  }

  return <PreviewPlaceholder label={label} />;
}

// ─────────────────────────────────────────────────────────────
// TierBadge — small, non-intrusive subscription tier indicator
// ─────────────────────────────────────────────────────────────
function TierBadge({ access }) {
  if (!access) return null;
  const tier = access.tier || 'free';
  const isSchool = access.isSchoolTeacher;
  const label = isSchool ? 'SCHOOL • PRO' : tier.toUpperCase();
  const tone = isSchool
    ? 'bg-ae-cyan/15 text-ae-cyan ring-1 ring-ae-cyan/30'
    : tier === 'elite'
    ? 'bg-ae-warning text-ae-navy'
    : tier === 'pro'
    ? 'bg-ae-blue text-white'
    : tier === 'starter'
    ? 'bg-white text-ae-navy'
    : 'bg-white/10 text-slate-200 ring-1 ring-white/15';

  // Compute an at-a-glance usage quota if any limit is finite
  const limits = access.limits || {};
  const usage = access.usage || {};
  const usageBits = [
    ['Question papers', usage.questionPapersGenerated, limits.questionPapersPerMonth],
    ['Worksheets', usage.worksheetsGenerated, limits.worksheetsPerMonth],
    ['PPTs', usage.pptGenerated, limits.pptPerMonth],
    ['Lesson kits', usage.lessonKitsGenerated, limits.lessonKitsPerMonth],
  ].filter(([, , limit]) => typeof limit === 'number' && limit >= 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="tier-badge"
          className={cn(
            'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wide shadow-soft hover:shadow-soft-lg transition-shadow',
            tone
          )}
        >
          {isSchool ? <ShieldCheck className="h-3 w-3" /> : <Crown className="h-3 w-3" />}
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3" data-testid="tier-popover">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
          {isSchool ? 'School subscription covers you' : 'Monthly usage'}
        </div>
        {isSchool ? (
          <div className="text-sm">
            You have full <strong>PRO</strong>-level access through your school. No usage limits.
          </div>
        ) : usageBits.length === 0 ? (
          <div className="text-sm text-muted-foreground">Unlimited on current plan.</div>
        ) : (
          <div className="space-y-2">
            {usageBits.map(([label, used, limit]) => {
              const pct = limit === 0 ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
              return (
                <div key={label}>
                  <div className="flex items-center justify-between text-xs">
                    <span>{label}</span>
                    <span className="text-muted-foreground">{used} / {limit}</span>
                  </div>
                  <div className="h-1.5 mt-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full',
                        pct >= 90 ? 'bg-rose-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {access.watermark && (
          <div className="mt-3 pt-3 border-t text-[11px] text-muted-foreground flex items-center gap-1.5">
            <Lock className="h-3 w-3" />
            Exports are watermarked on your plan.
          </div>
        )}
        {!isSchool && (
          <Button
            data-testid="tier-upgrade-cta"
            size="sm"
            className="w-full mt-3 gap-1.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
            onClick={() => window.dispatchEvent(new CustomEvent('ce:upgrade-required'))}
          >
            <Crown className="h-3.5 w-3.5" />
            {tier === 'elite' ? 'Manage Plan' : 'Upgrade Plan'}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}


// ─────────────────────────────────────────────────────────────
// Step 5 — Evaluate (Outcomes, submissions, analytics)
// ─────────────────────────────────────────────────────────────
function Step5Evaluate({
  evalTab, setEvalTab, boardLabel, classId, subject, chapter, selectedType,
  evaluation, evaluationLoading, assignmentId,
}) {
  // Use backend data when available, otherwise deterministic local preview
  const ev = evaluation;
  const submissions = ev?.submissions || { assigned: 42, submitted: 31, pending: 11, late: 3 };
  const submitPct = submissions.assigned
    ? Math.round((submissions.submitted / submissions.assigned) * 100)
    : 0;

  const distribution = ev?.scoreDistribution || [
    { band: '90-100', count: 4, tone: 'bg-emerald-500' },
    { band: '75-89', count: 9, tone: 'bg-emerald-400' },
    { band: '60-74', count: 11, tone: 'bg-amber-400' },
    { band: '40-59', count: 5, tone: 'bg-orange-400' },
    { band: '0-39', count: 2, tone: 'bg-rose-500' },
  ];
  const maxCount = Math.max(...distribution.map((d) => d.count), 1);

  const questions = ev?.questions?.length ? ev.questions : [
    { id: 'Q1', topic: 'Basic recall', difficulty: 'Easy', avgScore: 92, discrimination: 0.18 },
    { id: 'Q2', topic: 'Formula application', difficulty: 'Medium', avgScore: 74, discrimination: 0.42 },
    { id: 'Q3', topic: 'Word problem', difficulty: 'Medium', avgScore: 58, discrimination: 0.55 },
    { id: 'Q4', topic: 'Proof / reasoning', difficulty: 'Hard', avgScore: 41, discrimination: 0.62 },
    { id: 'Q5', topic: 'Case study', difficulty: 'Hard', avgScore: 36, discrimination: 0.48 },
  ];

  const misconceptions = ev?.misconceptions?.length ? ev.misconceptions : [
    { topic: 'Sign errors in quadratics', severity: 'high', affected: 18 },
    { topic: 'Confusing AP vs GP formula', severity: 'medium', affected: 11 },
    { topic: 'Unit conversion in word problems', severity: 'medium', affected: 9 },
  ];

  const strugglers = ev?.strugglers?.length ? ev.strugglers : [
    { name: 'Aarav Patel', score: 38, trend: 'down' },
    { name: 'Ishita Roy', score: 42, trend: 'down' },
    { name: 'Kabir Singh', score: 45, trend: 'flat' },
    { name: 'Meera Nair', score: 47, trend: 'up' },
  ];

  const classAvg = ev?.classAverage ?? 68;
  const avgTime = ev?.avgTimeMin ?? 38;
  const difficultyIdx = ev?.difficultyIndex ?? 0.63;
  const discrimination = ev?.discrimination ?? 0.45;
  const topScorer = ev?.topScorer || 'Riya Sharma • 98%';
  const medianScore = ev?.medianScore ?? 71;
  const onTime = ev?.onTimeCount ?? (submissions.submitted - submissions.late);

  return (
    <div className="space-y-6" data-testid="step-5-evaluate">
      {/* Context strip */}
      <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/60 dark:border-slate-800">
        <div className="flex items-center gap-2 text-sm">
          <FileBarChart className="h-4 w-4 text-slate-500" />
          <span className="font-semibold">{selectedType || 'Assignment'}</span>
          <span className="text-muted-foreground">
            • {boardLabel} • Class {classId} • {subject}{chapter ? ` • ${chapter}` : ''}
          </span>
          {evaluationLoading && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground ml-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading live data…
            </span>
          )}
          {!assignmentId && (
            <Badge variant="secondary" className="ml-2 text-[10px]">
              Preview data — assign first to see live
            </Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => toast.info('Report export queued.')}>
          <Download className="h-3.5 w-3.5 mr-1.5" /> Export Report
        </Button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="eval-stats">
        <StatTile
          label="Assigned"
          value={submissions.assigned}
          icon={Users}
          tone="slate"
          testId="stat-assigned"
        />
        <StatTile
          label="Submitted"
          value={submissions.submitted}
          sub={`${submitPct}% response`}
          icon={CheckCircle2}
          tone="emerald"
          testId="stat-submitted"
        />
        <StatTile
          label="Pending"
          value={submissions.pending}
          icon={Clock}
          tone="amber"
          testId="stat-pending"
        />
        <StatTile
          label="Late"
          value={submissions.late}
          icon={AlertTriangle}
          tone="rose"
          testId="stat-late"
        />
      </div>

      {/* Tabbed analytics */}
      <SectionCard
        title="Outcome Analytics"
        subtitle="Understand how learners performed"
        icon={BarChart3}
      >
        <Tabs value={evalTab} onValueChange={setEvalTab}>
          <TabsList className="bg-slate-100 dark:bg-slate-800 mb-4">
            <TabsTrigger value="overview" data-testid="eval-tab-overview" className="gap-1.5">
              <LineChart className="h-3.5 w-3.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="questions" data-testid="eval-tab-questions" className="gap-1.5">
              <ListChecks className="h-3.5 w-3.5" /> Questions
            </TabsTrigger>
            <TabsTrigger value="misconceptions" data-testid="eval-tab-misconceptions" className="gap-1.5">
              <Flame className="h-3.5 w-3.5" /> Misconceptions
            </TabsTrigger>
            <TabsTrigger value="students" data-testid="eval-tab-students" className="gap-1.5">
              <Users className="h-3.5 w-3.5" /> Students
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold">Score Distribution</div>
                  <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">
                    Class avg: {classAvg}%
                  </Badge>
                </div>
                <div className="grid grid-cols-5 gap-3">
                  {distribution.map((d) => (
                    <div key={d.band} className="text-center">
                      <div className="h-28 flex items-end justify-center">
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: `${(d.count / maxCount) * 100}%` }}
                          transition={{ duration: 0.6 }}
                          className={cn('w-8 rounded-t-md', d.tone)}
                        />
                      </div>
                      <div className="text-xs font-semibold mt-2">{d.count}</div>
                      <div className="text-[10px] text-muted-foreground">{d.band}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <MiniMetric
                  label="Avg Time Taken"
                  value={`${avgTime} min`}
                  hint="vs 45 min planned"
                  icon={Clock}
                />
                <MiniMetric
                  label="Difficulty Index"
                  value={difficultyIdx.toFixed(2)}
                  hint="Balanced — ideal 0.55-0.70"
                  icon={Gauge}
                />
                <MiniMetric
                  label="Discrimination"
                  value={discrimination.toFixed(2)}
                  hint="Healthy separation"
                  icon={Activity}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="questions">
            <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 overflow-hidden overflow-x-auto" data-testid="question-analytics-table">
              <div className="grid grid-cols-[60px_1fr_100px_120px_140px] min-w-[640px] px-4 py-2.5 text-[10px] uppercase tracking-wide text-muted-foreground bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200/70 dark:border-slate-800">
                <div>Q.</div>
                <div>Topic</div>
                <div>Difficulty</div>
                <div>Avg Score</div>
                <div>Discrimination</div>
              </div>
              {questions.map((q) => (
                <div
                  key={q.id}
                  className="grid grid-cols-[60px_1fr_100px_120px_140px] min-w-[640px] px-4 py-3 border-b last:border-b-0 border-slate-200/60 dark:border-slate-800 text-sm items-center"
                >
                  <div className="font-mono font-semibold">{q.id}</div>
                  <div>{q.topic}</div>
                  <div>
                    <Badge
                      variant="secondary"
                      className={cn(
                        'text-[10px] border-0',
                        q.difficulty === 'Easy' && 'bg-emerald-100 text-emerald-800',
                        q.difficulty === 'Medium' && 'bg-amber-100 text-amber-800',
                        q.difficulty === 'Hard' && 'bg-rose-100 text-rose-800'
                      )}
                    >
                      {q.difficulty}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full',
                          q.avgScore >= 70 ? 'bg-emerald-500' : q.avgScore >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                        )}
                        style={{ width: `${q.avgScore}%` }}
                      />
                    </div>
                    <span className="text-xs font-semibold w-9 text-right">{q.avgScore}%</span>
                  </div>
                  <div className="text-xs font-mono text-muted-foreground">{q.discrimination.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="misconceptions">
            <div className="space-y-2.5" data-testid="misconceptions-list">
              {misconceptions.map((m) => (
                <div
                  key={m.topic}
                  className="flex items-center justify-between p-3 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        'h-9 w-9 rounded-lg flex items-center justify-center',
                        m.severity === 'high' && 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
                        m.severity === 'medium' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      )}
                    >
                      <Flame className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{m.topic}</div>
                      <div className="text-xs text-muted-foreground">
                        Affected {m.affected} of {submissions.submitted} students
                      </div>
                    </div>
                  </div>
                  <Badge
                    className={cn(
                      'text-[10px] border-0 uppercase tracking-wide',
                      m.severity === 'high' && 'bg-rose-500 text-white',
                      m.severity === 'medium' && 'bg-amber-500 text-white'
                    )}
                  >
                    {m.severity}
                  </Badge>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="students">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="students-list">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                  <TrendingDown className="h-3.5 w-3.5 text-rose-500" />
                  Needs Attention
                </div>
                <div className="space-y-2">
                  {strugglers.map((s) => {
                    const TrendIcon = s.trend === 'up' ? TrendingUp : s.trend === 'down' ? TrendingDown : Activity;
                    return (
                      <div
                        key={s.name}
                        className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900"
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="h-7 w-7 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold">
                            {s.name.split(' ').map((x) => x[0]).join('').slice(0, 2)}
                          </div>
                          <span className="text-sm">{s.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-rose-600 dark:text-rose-400">{s.score}%</span>
                          <TrendIcon
                            className={cn(
                              'h-3.5 w-3.5',
                              s.trend === 'down' && 'text-rose-500',
                              s.trend === 'up' && 'text-emerald-500',
                              s.trend === 'flat' && 'text-slate-400'
                            )}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                  Class Snapshot
                </div>
                <div className="p-4 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-3">
                  <Row label="Top scorer" value={topScorer} />
                  <Row label="Median score" value={`${medianScore}%`} />
                  <Row label="Participation" value={`${submitPct}%`} />
                  <Row label="On-time submissions" value={`${onTime} of ${submissions.submitted}`} />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </SectionCard>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 6 — Improve (Remediate & iterate)
// ─────────────────────────────────────────────────────────────
function Step6Improve({
  improveActions, toggleImprove, boardLabel, classId, subject, chapter,
  recommendations, recommendationsLoading, assignmentId,
}) {
  // Map icon name strings from backend → lucide components
  const ICON_MAP = {
    Flame, Home, PlayCircle, Gauge, Users, Sparkles, Wand2,
  };
  const fallback = [
    { id: 'remedial-quadratics', priority: 'high', icon: 'Flame', title: 'Generate Remedial Pack — Sign errors in quadratics', description: '18 students affected. Auto-build a 5-question recap with step-by-step worked solutions.', impact: 'High impact' },
    { id: 'revision-homework', priority: 'high', icon: 'Home', title: 'Create Revision Homework for bottom quartile', description: 'Target: 8 students scoring <50%. Lighter difficulty mix (60/30/10 E/M/H).', impact: 'Direct outcome' },
    { id: 're-teach-flow', priority: 'medium', icon: 'PlayCircle', title: 'Re-teach Flow: AP vs GP formulas', description: 'Short 15-min micro-lesson with compare-contrast slides + 3 practice items.', impact: 'Concept repair' },
    { id: 'next-lesson-adjust', priority: 'medium', icon: 'Gauge', title: 'Adjust next lesson difficulty', description: 'Current mix was too hard. Suggest 40/40/20 next time.', impact: 'Planning delta' },
    { id: 'peer-learning', priority: 'low', icon: 'Users', title: 'Pair top-scorers with strugglers', description: 'Peer-learning grouping based on score dispersion. Suggested pairs: 4.', impact: 'Class cohesion' },
  ];
  const recs = (recommendations && recommendations.length ? recommendations : fallback).map((r) => ({
    ...r,
    iconComponent: ICON_MAP[r.icon] || Flame,
  }));

  const priorityTone = {
    high: 'border-rose-200 bg-rose-50/40 dark:bg-rose-900/10 dark:border-rose-900/40',
    medium: 'border-amber-200 bg-amber-50/40 dark:bg-amber-900/10 dark:border-amber-900/40',
    low: 'border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800',
  };
  const priorityBadge = {
    high: 'bg-rose-500 text-white',
    medium: 'bg-amber-500 text-white',
    low: 'bg-slate-300 text-slate-800',
  };

  return (
    <div className="space-y-6" data-testid="step-6-improve">
      <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200/60 dark:border-emerald-900/40">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-white dark:bg-slate-900 flex items-center justify-center shadow-soft">
            <Sparkles className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <div className="text-sm font-semibold">AI-Generated Improvement Plan</div>
            <div className="text-xs text-muted-foreground">
              Based on evaluation insights for {subject} • Class {classId}{chapter ? ` • ${chapter}` : ''}
            </div>
          </div>
        </div>
        <Badge className="bg-emerald-600 text-white border-0">
          {improveActions.length} of {recs.length} selected
        </Badge>
      </div>

      <SectionCard
        title="Recommended Actions"
        subtitle="Select one or more — each closes the loop on a specific gap"
        icon={Wand2}
      >
        <div className="space-y-2.5" data-testid="improve-recommendations">
          {recommendationsLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground p-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating recommendations…
            </div>
          )}
          {recs.map((r) => {
            const Icon = r.iconComponent;
            const selected = improveActions.includes(r.id);
            return (
              <button
                key={r.id}
                data-testid={`improve-${r.id}`}
                onClick={() => toggleImprove(r.id)}
                className={cn(
                  'w-full flex items-start gap-3 p-4 rounded-xl border text-left transition-all',
                  priorityTone[r.priority],
                  selected && 'ring-2 ring-emerald-500 border-transparent'
                )}
              >
                <div
                  className={cn(
                    'h-5 w-5 rounded-md border-2 flex items-center justify-center mt-0.5 flex-shrink-0',
                    selected ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300 dark:border-slate-600'
                  )}
                >
                  {selected && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="h-9 w-9 rounded-lg bg-white/70 dark:bg-slate-900/70 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{r.title}</span>
                    <span className={cn('text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide', priorityBadge[r.priority])}>
                      {r.priority}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{r.description}</div>
                  <div className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-1.5 flex items-center gap-1">
                    <ArrowUpRight className="h-3 w-3" />
                    {r.impact}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard
          title="Feed Back into Next Lesson"
          subtitle="Auto-tune planning for the next round"
          icon={Repeat}
        >
          <div className="space-y-2.5">
            {[
              { id: 'auto-tune-difficulty', label: 'Auto-tune difficulty distribution for next lesson' },
              { id: 'update-curriculum-pace', label: 'Adjust curriculum pacing (chapter is behind target)' },
              { id: 'save-as-template', label: 'Save this insight as a class template' },
            ].map((o) => {
              const selected = improveActions.includes(o.id);
              return (
                <label
                  key={o.id}
                  data-testid={`improve-${o.id}`}
                  className={cn(
                    'flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors',
                    selected
                      ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/20'
                      : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400'
                  )}
                >
                  <Switch checked={selected} onCheckedChange={() => toggleImprove(o.id)} />
                  <span className="text-sm">{o.label}</span>
                </label>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard
          title="Share Insights"
          subtitle="Keep stakeholders in the loop"
          icon={Send}
        >
          <div className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start"
              data-testid="share-parents"
              onClick={() => toast.info('Parent digest queued (preview).')}
            >
              <Users className="h-4 w-4 mr-2" /> Send digest to parents of strugglers
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              data-testid="share-admin"
              onClick={() => toast.info('Shared with academic admin (preview).')}
            >
              <ShieldCheck className="h-4 w-4 mr-2" /> Share with academic admin
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              data-testid="export-learning-log"
              onClick={() => toast.info('Learning log exported (preview).')}
            >
              <Download className="h-4 w-4 mr-2" /> Export learning log (PDF)
            </Button>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

// Small primitives used in Evaluate/Improve
function StatTile({ label, value, sub, icon: Icon, tone = 'slate', testId }) {
  const toneMap = {
    slate: 'from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900 text-slate-700 dark:text-slate-300',
    emerald: 'from-emerald-100 to-emerald-50 dark:from-emerald-900/40 dark:to-emerald-900/10 text-emerald-700 dark:text-emerald-300',
    amber: 'from-amber-100 to-amber-50 dark:from-amber-900/40 dark:to-amber-900/10 text-amber-700 dark:text-amber-300',
    rose: 'from-rose-100 to-rose-50 dark:from-rose-900/40 dark:to-rose-900/10 text-rose-700 dark:text-rose-300',
  };
  return (
    <div
      data-testid={testId}
      className="relative rounded-xl border border-slate-200/60 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 overflow-hidden shadow-soft"
    >
      <div className={cn('absolute inset-0 bg-gradient-to-br opacity-50', toneMap[tone])} />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
            {label}
          </div>
          <Icon className="h-4 w-4 opacity-60" />
        </div>
        <div className="text-2xl font-bold mt-2">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function MiniMetric({ label, value, hint, icon: Icon }) {
  return (
    <div className="p-3 rounded-lg border border-slate-200/60 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-lg font-bold mt-1">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Bottom action bar
// ─────────────────────────────────────────────────────────────
function ActionBar({
  currentStep, totalSteps, goBack, goNext, handleGenerate, handleApplyImprovements,
  improveCount, isGenerating, isApplying, hasGenerated, mode,
}) {
  const isIndividual = mode === 'individual';
  const isLast = currentStep === totalSteps;
  const isAssignStep = currentStep === 4;
  const nextDisabled = isGenerating || isIndividual;
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-auto max-w-[calc(100%-1rem)] pointer-events-none">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="pointer-events-auto"
        data-testid="engine-action-bar"
      >
        <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white/95 dark:bg-ae-navy/95 backdrop-blur-xl shadow-soft-lg px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-center gap-2 sm:gap-3">
        <Button
          variant="ghost"
          onClick={goBack}
          disabled={currentStep === 1 || isGenerating || isApplying}
          data-testid="btn-back"
        >
          Back
        </Button>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">Step {currentStep}</span> of {totalSteps}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => toast.info('Draft saved locally.')}
            data-testid="btn-save-draft"
            className="hidden sm:inline-flex"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save
          </Button>
          {isAssignStep && (
            <Button
              onClick={handleGenerate}
              disabled={isGenerating}
              data-testid="btn-generate"
              className="gap-1.5"
            >
              {isGenerating ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
              ) : (
                <>{hasGenerated ? <RefreshCw className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
                  {hasGenerated ? 'Re-generate' : 'Generate'}</>
              )}
            </Button>
          )}
          {!isLast && (
            <Button
              onClick={goNext}
              data-testid="btn-next"
              className="gap-1.5"
              disabled={nextDisabled}
              title={isIndividual ? 'Steps 2–6 are available in School Mode' : undefined}
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          )}
          {isLast && (
            <Button
              onClick={handleApplyImprovements}
              disabled={isApplying}
              data-testid="btn-apply-improvements"
              className="gap-1.5 bg-ae-success hover:bg-ae-success/90 text-white"
            >
              {isApplying ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Applying…</>
              ) : (
                <><CheckCircle2 className="h-4 w-4" /> Apply Improvements
                  {improveCount > 0 && (
                    <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded-full bg-white/20">
                      {improveCount}
                    </span>
                  )}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      </motion.div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 1 — Two Independent Generators (Section A + Section B)
// ─────────────────────────────────────────────────────────────
const TASK_DOC_MAP = {
  'Assignment': ['assessment'],
  'Test': ['assessment'],
  'Exam': ['assessment'],
  'Homework': ['assessment'],
  'Holiday Homework': ['assessment'],
  'Summary': ['summary'],
  'Book / Chapter Task': ['summary', 'lesson_plan'],
  'Play / Skit': ['lesson_plan', 'teaching_flow'],
  'Debate': ['lesson_plan'],
  'Custom': ['summary'],
};

// Maps each generated doc-type to the (used, limit) counter on `access`.
// Mirrors backend FEATURE_MAP in /app/backend/content_engine/access_control.py.
const DOC_USAGE_KEY = {
  assessment:    { used: 'questionPapersGenerated', limit: 'questionPapersPerMonth', label: 'Question papers' },
  summary:       { used: 'worksheetsGenerated',     limit: 'worksheetsPerMonth',     label: 'Worksheets' },
  lesson_plan:   { used: 'worksheetsGenerated',     limit: 'worksheetsPerMonth',     label: 'Worksheets' },
  teaching_flow: { used: 'worksheetsGenerated',     limit: 'worksheetsPerMonth',     label: 'Worksheets' },
  ppt:           { used: 'pptGenerated',            limit: 'pptPerMonth',            label: 'Presentations' },
};

/**
 * Compute usage status for the given doc-types against the access record.
 * Returns an array of unique counters [{label, used, limit, atCap}], plus
 * `allAtCap` flag — true when EVERY counter required by the selected docs
 * is at-or-above the monthly limit. School/Pro/Elite tiers (limit=null/undefined)
 * always report `atCap=false`.
 */
function getDocTypeUsageStatus(docTypes, access) {
  const usage = access?.usage || {};
  const limits = access?.limits || {};
  const seen = new Set();
  const counters = [];
  for (const dt of (docTypes || [])) {
    const map = DOC_USAGE_KEY[dt];
    if (!map) continue;
    const sig = `${map.used}|${map.limit}`;
    if (seen.has(sig)) continue;
    seen.add(sig);
    const used = Number(usage[map.used] || 0);
    const lim  = limits[map.limit];
    // Unlimited = null/undefined/-1
    const isUnlimited = lim == null || Number(lim) < 0;
    const limit = isUnlimited ? null : Number(lim);
    const atCap = !isUnlimited && used >= limit;
    counters.push({ key: sig, label: map.label, used, limit, atCap, isUnlimited });
  }
  // ── Iter 68 — Total monthly generations cap (Free=5 / Starter=100). ──
  // Always show this when the tier has a finite total cap, so the user knows
  // exactly how many generations remain in their month.
  const totalCap = access?.totalGenerationsPerMonth;
  if (totalCap != null && Number(totalCap) > 0) {
    const totalUsed = Number(usage.totalGenerated || 0);
    counters.unshift({
      key: 'total',
      label: 'Generations',
      used: totalUsed,
      limit: Number(totalCap),
      atCap: totalUsed >= Number(totalCap),
      isUnlimited: false,
    });
  }
  const allAtCap = counters.length > 0 && counters.every((c) => c.atCap);
  const anyAtCap = counters.some((c) => c.atCap);
  return { counters, allAtCap, anyAtCap };
}

/**
 * Defensive payload sanitiser — strips any non-JSON-cloneable values from
 * the payload before sending. Prevents the Chromium "Request object cannot
 * be cloned" error caused by stray DOM/Function references in deep state.
 */
function _safeClonePayload(value) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (e) {
    return value;
  }
}

function describeCurriculum(board, classId, subject) {
  const boardLabel = BOARDS.find((b) => b.id === board)?.label;
  return [boardLabel, classId && `Class ${classId}`, subject].filter(Boolean).join(' · ');
}

function describeSource(source, topic) {
  if (source.sourceMode === 'topic') return topic ? `Topic — ${topic}` : '';
  if (!source.selectedBookId) return '';
  const n = source.selectedChapterNumbers.length;
  const book = source.selectedBook?.title || (source.sourceMode === 'upload' ? 'Uploaded book' : 'Library book');
  const multi = n > 1 ? ` (${source.multiChapterMode === 'combined' ? 'combined' : 'per chapter'})` : '';
  return `${book} · ${n} chapter${n === 1 ? '' : 's'}${multi}`;
}

function describeConfig(cfg, skip = []) {
  return Object.entries(cfg || {})
    .filter(([k, v]) => !skip.includes(k) && v !== '' && v != null && v !== false && typeof v !== 'object')
    .map(([k, v]) => `${k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}: ${v === true ? 'On' : v}`)
    .join(' · ');
}

function GenerateCta({ testIdPrefix, message, usage, keepPrevious, setKeepPrevious, keepLabel, children }) {
  const limited = usage.counters.filter((c) => !c.isUnlimited);
  return (
    <div
      data-testid={`${testIdPrefix}-generate-cta`}
      className="relative overflow-hidden rounded-2xl bg-ae-navy text-white p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4"
    >
      <div aria-hidden className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full bg-ae-blue/30 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-20 left-1/3 h-40 w-40 rounded-full bg-ae-cyan/20 blur-3xl" />
      <div className="relative text-sm flex items-start gap-3 min-w-0">
        <span className="h-9 w-9 rounded-xl bg-white/10 text-ae-cyan flex items-center justify-center shrink-0"><Sparkles className="h-4 w-4" /></span>
        <div className="min-w-0 space-y-2">
          <div className="text-slate-300 [&_strong]:text-white">{message}</div>
          {limited.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-usage-badges`}>
              {limited.map((c) => (
                <span
                  key={c.key}
                  data-testid={`${testIdPrefix}-usage-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    'text-[10px] px-2 py-0.5 rounded-full font-semibold border',
                    c.atCap ? 'bg-rose-500/15 text-rose-200 border-rose-400/30' : 'bg-ae-success/20 text-emerald-200 border-emerald-400/30',
                  )}
                  title={c.atCap ? 'Monthly limit reached — Upgrade to continue' : `${c.limit - c.used} remaining this month`}
                >
                  {c.label}: {c.used}/{c.limit}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="relative flex items-center gap-4 shrink-0 flex-wrap">
        <label
          data-testid={`${testIdPrefix}-keep-previous`}
          className="flex items-center gap-2 text-[11px] text-slate-300 select-none cursor-pointer"
          title="Stash the current batch before regenerating instead of overwriting it"
        >
          <input
            type="checkbox"
            checked={keepPrevious}
            onChange={(e) => setKeepPrevious(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-500 accent-[#20D5E8]"
          />
          {keepLabel}
        </label>
        {children}
      </div>
    </div>
  );
}

function ResetRow({ testId, label, hint, onReset }) {
  return (
    <div className="mt-6 pt-4 border-t border-slate-200/80 dark:border-slate-800 flex items-center justify-between gap-3 flex-wrap">
      <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
        <Eraser className="h-3.5 w-3.5" />
        {hint}
      </div>
      <Button type="button" variant="outline" size="sm" data-testid={testId} onClick={onReset} title={label} className="gap-1.5">
        <RefreshCw className="h-3.5 w-3.5" />
        {label}
      </Button>
    </div>
  );
}

const GENERATOR_MODES = [
  { id: 'quick', letter: 'A', icon: Zap, title: 'Quick Task', description: 'Worksheets, tests, homework, debates & more — in three quick steps.' },
  { id: 'plan', letter: 'B', icon: Target, title: 'Plan-based Kit', description: 'Goal-driven lesson plans, summaries, questionnaires & slides together.' },
];

function GeneratorModeSwitch({ generator, setGenerator }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="tablist" data-testid="generator-mode-switch">
      {GENERATOR_MODES.map((g) => {
        const active = generator === g.id;
        const Icon = g.icon;
        return (
          <button
            key={g.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => setGenerator(g.id)}
            data-testid={`generator-mode-${g.id}`}
            className={cn(
              'group relative flex items-start gap-3 rounded-2xl p-4 text-left transition-[background-color,border-color,box-shadow] duration-200',
              active
                ? 'bg-ae-navy text-white border border-ae-navy shadow-soft-lg'
                : 'bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 hover:border-ae-blue/40'
            )}
          >
            <span className={cn('h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-105',
              active ? 'bg-ae-blue text-white shadow-blue' : 'bg-ae-bg dark:bg-slate-800 text-ae-ink dark:text-slate-200')}>
              <Icon className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="flex items-center gap-2">
                <span className={cn('text-sm font-heading font-bold', active ? 'text-white' : 'text-ae-ink dark:text-white')}>{g.title}</span>
                <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded-md', active ? 'bg-ae-cyan text-ae-navy' : 'bg-slate-100 dark:bg-slate-800 text-ae-slate')}>Section {g.letter}</span>
              </span>
              <span className={cn('mt-0.5 block text-xs leading-snug', active ? 'text-slate-300' : 'text-muted-foreground')}>{g.description}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Step1Sections({
  access, docTypeCaps, mode, isGenerating, setIsGenerating,
  setBatchResult, setGeneratedAsset, setCurrentStep, refreshAccess,
  generator = 'quick', setGenerator = () => {},
}) {
  // Lifted blueprint state — shared across Section A AND Section B so a single
  // upload influences whichever generator the user picks. Rendered at the TOP
  // so it's visible immediately on page load.
  const [blueprint, setBlueprint] = useState(null);
  const [bpUploading, setBpUploading] = useState(false);
  const tier = (access?.tier || 'free').toLowerCase();
  const isSchool = !!(access?.schoolId || access?.isSchoolTeacher);
  const blueprintAllowed = isSchool || tier === 'pro' || tier === 'elite';
  return (
    <div className="space-y-5" data-testid="step-1-two-sections">
      <GeneratorModeSwitch generator={generator} setGenerator={setGenerator} />
      {/* OPTIONAL Blueprint Layer — progressive disclosure. Pro/Elite/School only.
          When set, every subsequent generate call (Section A or B) automatically
          attaches `blueprintId`. Backend silently falls back on any error. */}
      <Disclosure
        testId="step1-blueprint-disclosure"
        icon={FileText}
        title={blueprint ? 'Blueprint applied' : 'Match your own paper format (optional)'}
        subtitle={blueprint ? blueprint.filename : 'Upload a .pdf or .docx blueprint — applies to both generators.'}
        defaultOpen={false}
      >
      <div
        data-testid="step1-blueprint"
        className={cn(
          'rounded-xl border p-3 flex items-center gap-3 flex-wrap',
          blueprintAllowed
            ? 'border-ae-blue/20 bg-ae-blue/[0.04] dark:bg-ae-blue/10'
            : 'border-slate-200/70 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 opacity-70'
        )}
      >
        <div className="text-[11px] uppercase tracking-wide font-semibold text-ae-blue flex items-center gap-1.5 shrink-0">
          <FileText className="h-3.5 w-3.5" /> Upload Blueprint
          <span className="opacity-60 normal-case font-normal">(Optional · .pdf or .docx · applies to both generators)</span>
        </div>
        {blueprint ? (
          <>
            <span data-testid="step1-blueprint-status" className="text-[11px] text-emerald-700 dark:text-emerald-300 font-semibold flex items-center gap-1">
              <Check className="h-3.5 w-3.5" /> Blueprint applied
            </span>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="step1-blueprint-chip"
                  className="text-[10px] text-muted-foreground hover:text-indigo-700 dark:hover:text-indigo-300 underline decoration-dotted underline-offset-2 truncate max-w-[280px] text-left"
                  title="Click to view full detected structure"
                >
                  {blueprint.filename}
                  {blueprint.summary?.distribution?.length > 0 && (
                    <> · {blueprint.summary.distribution.map((d) => `${d.count} ${d.type}`).join(', ')}</>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                data-testid="step1-blueprint-popover"
                align="start"
                className="w-80 text-xs p-0"
              >
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-indigo-50/40 dark:bg-indigo-900/10 rounded-t-md">
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-indigo-700 dark:text-indigo-300 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" /> Detected structure
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 truncate" title={blueprint.filename}>
                    {blueprint.filename}
                  </div>
                </div>
                <div className="px-4 py-3 space-y-2.5">
                  {/* Top-line metadata */}
                  {(blueprint.summary?.totalMarks || blueprint.summary?.duration || blueprint.summary?.numSlides) && (
                    <div className="flex flex-wrap gap-1.5">
                      {blueprint.summary?.totalMarks && (
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[10px] font-medium">
                          {blueprint.summary.totalMarks} marks
                        </span>
                      )}
                      {blueprint.summary?.duration && (
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[10px] font-medium">
                          {blueprint.summary.duration} min
                        </span>
                      )}
                      {blueprint.summary?.numSlides && (
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[10px] font-medium">
                          {blueprint.summary.numSlides} slides
                        </span>
                      )}
                    </div>
                  )}
                  {/* Distribution */}
                  {blueprint.summary?.distribution?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">
                        Question Distribution
                      </div>
                      <ul className="space-y-0.5">
                        {blueprint.summary.distribution.map((d, i) => (
                          <li key={i} className="text-[11px] flex items-center justify-between gap-2">
                            <span className="font-medium">{d.type}</span>
                            <span className="text-muted-foreground">
                              {d.count}{d.marksEach ? ` × ${d.marksEach} mark${d.marksEach === 1 ? '' : 's'}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Sections */}
                  {blueprint.summary?.sections?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">
                        Sections / Headings
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {blueprint.summary.sections.slice(0, 8).map((s, i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-[10px]">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Intent flags — derived from raw text. Surfaces "HOTS · Case-based · Inquiry" tags. */}
                  {blueprint.summary?.intent && Object.keys(blueprint.summary.intent).length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">
                        Intent flags
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {Object.keys(blueprint.summary.intent).map((k, i) => (
                          <span
                            key={i}
                            className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-[10px] font-medium"
                            title="Detected from raw blueprint text — propagates to every content type"
                          >
                            {k.replace(/^emphasize/, '').replace(/^prefer/, '').replace(/Signaled$/, '').trim() || k}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Empty state */}
                  {!blueprint.summary?.distribution?.length
                    && !blueprint.summary?.sections?.length
                    && !blueprint.summary?.totalMarks
                    && !blueprint.summary?.duration
                    && !blueprint.summary?.numSlides && (
                    <div className="text-[11px] text-muted-foreground italic">
                      No structured signal detected — blueprint will be passed as raw context.
                      Generation will still run normally.
                    </div>
                  )}
                  <div className="pt-1 border-t border-slate-100 dark:border-slate-800">
                    <div className="text-[10px] text-muted-foreground italic leading-relaxed">
                      Curriculum (board) alignment remains the primary authority — blueprint constraints adjust to fit the board pattern when there's a strong conflict.
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Button
              data-testid="step1-blueprint-remove"
              variant="ghost" size="sm"
              className="h-7 px-2 text-[11px] text-rose-600"
              onClick={async () => {
                try { await contentEngineApi.deleteBlueprint(blueprint.blueprintId); } catch (_) { /* ignore */ }
                setBlueprint(null);
                toast.info('Blueprint removed.');
              }}
            >
              Remove
            </Button>
          </>
        ) : (
          <>
            <span data-testid="step1-blueprint-status" className="text-[11px] text-muted-foreground">
              No blueprint applied
            </span>
            <input
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              disabled={!blueprintAllowed || bpUploading}
              data-testid="step1-blueprint-input"
              className="text-[11px] file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-ae-blue file:text-white file:cursor-pointer file:text-[10px] disabled:opacity-50 disabled:cursor-not-allowed"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                setBpUploading(true);
                try {
                  const res = await contentEngineApi.uploadBlueprint(file);
                  setBlueprint(res);
                  toast.success('Blueprint applied — will guide both Section A and Section B generations.');
                } catch (err) {
                  toast.error(err.message || 'Blueprint upload failed.');
                } finally {
                  setBpUploading(false);
                }
              }}
            />
            <span
              data-testid="step1-blueprint-accept"
              className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[10px] font-mono text-slate-700 dark:text-slate-300"
              title="Only PDF and DOCX blueprints are accepted"
            >
              .pdf · .docx
            </span>
            {!blueprintAllowed && (
              <span className="text-[10px] text-amber-700 dark:text-amber-300 font-semibold">
                Available on PRO / ELITE / school plans.
              </span>
            )}
            {bpUploading && <span className="text-[10px] text-ae-blue animate-pulse">Parsing…</span>}
          </>
        )}
      </div>
      </Disclosure>
      <div className={cn(generator !== 'quick' && 'hidden')} data-testid="generator-panel-quick">
      <SectionA
        access={access}
        docTypeCaps={docTypeCaps}
        mode={mode}
        isGenerating={isGenerating}
        setIsGenerating={setIsGenerating}
        setBatchResult={setBatchResult}
        setGeneratedAsset={setGeneratedAsset}
        setCurrentStep={setCurrentStep}
        refreshAccess={refreshAccess}
        blueprint={blueprint}
      />
      </div>
      <div className={cn(generator !== 'plan' && 'hidden')} data-testid="generator-panel-plan">
      <SectionB
        access={access}
        docTypeCaps={docTypeCaps}
        mode={mode}
        isGenerating={isGenerating}
        setIsGenerating={setIsGenerating}
        setBatchResult={setBatchResult}
        setGeneratedAsset={setGeneratedAsset}
        setCurrentStep={setCurrentStep}
        refreshAccess={refreshAccess}
        blueprint={blueprint}
      />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// RecentStrip — "Last 5 generations" quick-reuse strip
// Fetches a user's recent successful generations (per section) and
// pre-fills the parent section's inputs on click.
// ─────────────────────────────────────────────────────────────
function RecentStrip({ section, onPick, refreshKey, accent = 'amber', accessTier = 'free', mode = 'school' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduleItem, setScheduleItem] = useState(null);
  // Schedule fan-out is school-only (individual users have no assigned classes).
  const showSchedule = mode === 'school';
  const canSchedule = showSchedule && accessTier !== 'free';

  const accentMap = {
    amber: {
      hover: 'hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20',
      icon: 'text-amber-600',
      pill: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    },
    emerald: {
      hover: 'hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
      icon: 'text-emerald-600',
      pill: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    },
    blue: {
      hover: 'hover:border-ae-blue/50 hover:bg-ae-blue/5 dark:hover:bg-ae-blue/10',
      icon: 'text-ae-blue',
      pill: 'bg-ae-blue/10 text-ae-blue dark:bg-ae-blue/20',
    },
    cyan: {
      hover: 'hover:border-ae-cyan/60 hover:bg-ae-cyan/5 dark:hover:bg-ae-cyan/10',
      icon: 'text-cyan-600 dark:text-ae-cyan',
      pill: 'bg-ae-cyan/15 text-cyan-800 dark:text-ae-cyan',
    },
  };
  const tone = accentMap[accent] || accentMap.amber;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    contentEngineApi
      .recentGenerations(section, 5)
      .then((r) => !cancelled && setItems(Array.isArray(r?.items) ? r.items : []))
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [section, refreshKey]);

  if (!loading && items.length === 0) return null;

  return (
    <div data-testid={`section-${section}-recent-strip`} className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <RefreshCw className={cn('h-3.5 w-3.5', tone.icon)} />
          <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">
            Recent generations — one-click reuse
          </span>
        </div>
        <Badge variant="secondary" className="text-[9px]" data-testid={`section-${section}-recent-count`}>
          {items.length}
        </Badge>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {loading && items.length === 0 ? (
          <div className="text-[11px] text-muted-foreground px-1">Loading…</div>
        ) : (
          items.map((it, idx) => {
            const src = it.source || {};
            const cfg = src.config || {};
            const subtitle = [src.board?.toUpperCase(), src.classId && `Cl ${src.classId}`, src.subject]
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={it.batchId || idx}
                className={cn(
                  'relative min-w-[180px] max-w-[260px] flex-shrink-0 rounded-lg border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 p-2.5 transition-colors group',
                  tone.hover
                )}
              >
                <button
                  data-testid={`section-${section}-recent-item-${idx}`}
                  onClick={() => onPick && onPick(it)}
                  title={`Reuse: ${it.label} — ${it.topic || src.topic || ''}`}
                  className="w-full text-left"
                >
                  <div className="flex items-center gap-1.5 mb-1 pr-6">
                    <Sparkles className={cn('h-3 w-3', tone.icon)} />
                    <span className="text-[10px] font-semibold uppercase tracking-wide truncate">
                      {it.label || 'Generation'}
                    </span>
                  </div>
                  <div className="text-xs font-medium truncate">
                    {it.topic || src.topic || '—'}
                  </div>
                  {subtitle && (
                    <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{subtitle}</div>
                  )}
                  <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                    {(it.docTypes || []).slice(0, 3).map((dt) => (
                      <span
                        key={dt}
                        className={cn('text-[9px] px-1.5 py-0.5 rounded-full font-mono', tone.pill)}
                      >
                        {dt}
                      </span>
                    ))}
                    {(cfg.difficulty || cfg.maxMarks) && (
                      <span className="text-[9px] text-muted-foreground">
                        {cfg.difficulty || ''}{cfg.difficulty && cfg.maxMarks ? ' · ' : ''}{cfg.maxMarks ? `${cfg.maxMarks}m` : ''}
                      </span>
                    )}
                  </div>
                </button>
                {/* Schedule weekly fan-out — paid SCHOOL tiers only (individual users have no classes) */}
                {showSchedule && (
                <button
                  data-testid={`section-${section}-recent-schedule-${idx}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canSchedule) {
                      window.dispatchEvent(new CustomEvent('ce:upgrade-required', {
                        detail: { code: 'UPGRADE_REQUIRED', message: 'Recurring weekly schedules are a Starter+ feature.', currentTier: accessTier, suggestedTier: 'starter' },
                      }));
                      return;
                    }
                    setScheduleItem(it);
                  }}
                  title={canSchedule
                    ? 'Schedule this as a weekly recurring fan-out'
                    : 'Upgrade to Starter+ to schedule recurring weekly fan-outs'}
                  className={cn(
                    'absolute top-1.5 right-1.5 h-6 w-6 rounded-md flex items-center justify-center transition-colors border',
                    canSchedule
                      ? 'border-slate-200/70 dark:border-slate-700 hover:border-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300'
                      : 'border-dashed border-slate-200 dark:border-slate-800 text-slate-300 dark:text-slate-600 opacity-60 cursor-not-allowed hover:opacity-80'
                  )}
                >
                  <Calendar className="h-3.5 w-3.5" />
                </button>
                )}
              </div>
            );
          })
        )}
      </div>
      {scheduleItem && (
        <ScheduleWeeklyDialog
          item={scheduleItem}
          onClose={() => setScheduleItem(null)}
          onCreated={() => setScheduleItem(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ScheduleWeeklyDialog — convert a recent generation into a recurring
// weekly fan-out (paid tiers only — backend enforces 402).
// ─────────────────────────────────────────────────────────────
const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function ScheduleWeeklyDialog({ item, onClose, onCreated }) {
  const [classes, setClasses] = useState([]);
  const defaultAssign = item?.source?.classId
    ? `Class ${item.source.classId} - A`
    : '';
  const [assignTo, setAssignTo] = useState(defaultAssign);
  const [dayOfWeek, setDayOfWeek] = useState(0); // Mon
  const [hour, setHour] = useState(8);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    contentEngineApi
      .listClassSections()
      .then((r) => !cancelled && setClasses(Array.isArray(r?.items) ? r.items : []))
      .catch(() => !cancelled && setClasses([]));
    return () => { cancelled = true; };
  }, []);

  const submit = async () => {
    if (!assignTo) {
      toast.error('Pick a class/section to fan out to.');
      return;
    }
    setSubmitting(true);
    try {
      await contentEngineApi.createSchedule({
        section: item.section,
        label: item.label,
        topic: item.topic,
        docTypes: item.docTypes,
        source: item.source,
        assignTo,
        assignmentType: 'homework',
        dayOfWeek,
        hour,
      });
      toast.success(`Weekly schedule created — every ${DOW_LABELS[dayOfWeek]} at ${hour}:00.`);
      onCreated && onCreated();
    } catch (err) {
      if (err.code === 'UPGRADE_REQUIRED') {
        window.dispatchEvent(new CustomEvent('ce:upgrade-required', { detail: err.detail }));
        onClose && onClose();
      } else {
        toast.error(err.message || 'Failed to create schedule.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="schedule-weekly-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="h-5 w-5 text-emerald-600" />
          <div>
            <div className="text-base font-semibold">Schedule weekly fan-out</div>
            <div className="text-xs text-muted-foreground">
              {item.label} — {item.topic || item.source?.topic || ''}
            </div>
          </div>
        </div>

        <div className="space-y-3 mt-2">
          <div>
            <label className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">
              Fan-out to
            </label>
            <select
              data-testid="schedule-assign-to"
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
              className="mt-1 w-full h-9 px-2 text-sm rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
            >
              <option value="">Select class & section…</option>
              {defaultAssign && !classes.find((c) => c.label === defaultAssign) && (
                <option value={defaultAssign}>{defaultAssign} (from generation)</option>
              )}
              {classes.map((c) => (
                <option key={c.label} value={c.label}>
                  {c.label} ({c.studentCount || 0} students)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">
              Day of week
            </label>
            <div className="grid grid-cols-7 gap-1 mt-1" data-testid="schedule-day-picker">
              {DOW_LABELS.map((label, i) => (
                <button
                  key={label}
                  data-testid={`schedule-day-${i}`}
                  onClick={() => setDayOfWeek(i)}
                  className={cn(
                    'h-11 sm:h-9 rounded-md text-xs font-medium border transition-colors',
                    dayOfWeek === i
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                      : 'border-slate-200 dark:border-slate-800 hover:border-slate-400 bg-white dark:bg-slate-900'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">
              Time of day (24h)
            </label>
            <Input
              data-testid="schedule-hour"
              type="number" min={0} max={23}
              value={hour}
              onChange={(e) => setHour(Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
              className="mt-1 h-9 text-sm"
            />
          </div>

          <div className="rounded-md border border-dashed border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-900/10 p-2.5 text-[11px] text-emerald-800 dark:text-emerald-300">
            <strong>Auto-pilot:</strong> every {DOW_LABELS[dayOfWeek]} at {hour}:00, the engine will regenerate this {item.label?.toLowerCase()} and assign it to {assignTo || 'the selected class'}.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button
            data-testid="schedule-cancel-btn"
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            data-testid="schedule-confirm-btn"
            onClick={submit}
            disabled={submitting || !assignTo}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scheduling…</> : 'Schedule weekly'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TaskConfigPanel — dynamic per-task configuration UI
// Renders the inputs declared in TASK_TYPE_CONFIG[taskType].inputs,
// plus a universal `dueDate` field. Writes back to `config` via setConfig.
// ─────────────────────────────────────────────────────────────
function TaskConfigPanel({ taskType, config, setConfig, testIdPrefix = 'task-config' }) {
  const cfg = TASK_TYPE_CONFIG[taskType];
  if (!cfg || !Array.isArray(cfg.inputs)) return null;

  const update = (key, value) => setConfig((prev) => ({ ...prev, [key]: value }));
  const tid = (key) => `${testIdPrefix}-config-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;

  return (
    <div
      data-testid={`${testIdPrefix}-config-panel`}
      className="rounded-xl border border-slate-200/70 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">
            {taskType} Configuration
          </div>
          {cfg.description && (
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">{cfg.description}</div>
          )}
        </div>
        <Badge variant="secondary" className="text-[9px]">
          {cfg.inputs.length + 1} fields
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {cfg.inputs.map((field) => {
          const value = config?.[field.key] ?? (field.default ?? '');
          if (field.type === 'select') {
            return (
              <div key={field.key} className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-700 dark:text-slate-300">
                  {field.label}
                </label>
                <select
                  data-testid={tid(field.key)}
                  value={value}
                  onChange={(e) => update(field.key, e.target.value)}
                  className="h-9 px-2.5 text-xs rounded-lg border focus:outline-none focus:ring-2 focus:ring-ae-blue/30 focus:border-ae-blue border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                >
                  <option value="">Select…</option>
                  {field.options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            );
          }
          if (field.type === 'number') {
            return (
              <div key={field.key} className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-700 dark:text-slate-300">
                  {field.label}
                </label>
                <Input
                  data-testid={tid(field.key)}
                  type="number"
                  min={0}
                  value={value}
                  onChange={(e) => update(field.key, e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder={field.default !== undefined ? String(field.default) : ''}
                  className="h-9 text-xs"
                />
              </div>
            );
          }
          if (field.type === 'textarea') {
            return (
              <div key={field.key} className="flex flex-col gap-1 md:col-span-2 lg:col-span-3">
                <label className="text-[11px] font-medium text-slate-700 dark:text-slate-300">
                  {field.label}
                </label>
                <textarea
                  data-testid={tid(field.key)}
                  value={value}
                  onChange={(e) => update(field.key, e.target.value)}
                  placeholder={field.placeholder || ''}
                  rows={3}
                  className="text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 resize-y"
                />
              </div>
            );
          }
          if (field.type === 'toggle') {
            return (
              <label
                key={field.key}
                className="flex items-center gap-2 cursor-pointer text-[11px] font-medium text-slate-700 dark:text-slate-300"
              >
                <input
                  data-testid={tid(field.key)}
                  type="checkbox"
                  checked={Boolean(value)}
                  onChange={(e) => update(field.key, e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300 accent-amber-600"
                />
                {field.label}
              </label>
            );
          }
          // default = text
          return (
            <div key={field.key} className="flex flex-col gap-1">
              <label className="text-[11px] font-medium text-slate-700 dark:text-slate-300">
                {field.label}
              </label>
              <Input
                data-testid={tid(field.key)}
                type="text"
                value={value}
                onChange={(e) => update(field.key, e.target.value)}
                placeholder={field.placeholder || ''}
                className="h-9 text-xs"
              />
            </div>
          );
        })}

        {/* Universal due date field */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-slate-700 dark:text-slate-300">
            Due Date
          </label>
          <Input
            data-testid={tid('dueDate')}
            type="date"
            value={config?.dueDate || ''}
            onChange={(e) => update('dueDate', e.target.value)}
            className="h-9 text-xs"
          />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Section A — Task-Type Quick Generator
// ─────────────────────────────────────────────────────────────
function SectionA(props) {
  const { t } = useTranslation();
  const { access, mode, isGenerating, setIsGenerating, setBatchResult, setGeneratedAsset, setCurrentStep, refreshAccess, blueprint } = props;

  const [taskType, setTaskType] = useState('');
  const [board, setBoard] = useState('');
  const [classId, setClassId] = useState('');
  const [subject, setSubject] = useState('');
  const [topic, setTopic] = useState('');
  const [batch, setBatch] = useState(null);
  const [taskConfig, setTaskConfig] = useState({});
  const [keepPrevious, setKeepPrevious] = useState(false);
  const [previousBatches, setPreviousBatches] = useState([]);
  const [wiz, setWiz] = useState(1);
  const [wizMax, setWizMax] = useState(1);
  const goWiz = (n) => { setWiz(n); setWizMax((m) => Math.max(m, n)); };

  // C1 — Reset all Section A selections to defaults.
  const resetSectionA = () => {
    setTaskType('');
    setBoard('');
    setClassId('');
    setSubject('');
    setTopic('');
    setBatch(null);
    setTaskConfig({});
    setKeepPrevious(false);
    setWiz(1);
    setWizMax(1);
    // Clear source/library state
    try {
      source.setSourceMode?.('topic');
      source.setSelectedBookId?.('');
      source.setSelectedChapterNumbers?.([]);
      source.setMultiChapterMode?.('per_chapter');
      source.setLibBoard?.('');
      source.setLibClass?.('');
      source.setLibSubject?.('');
      source.setLibrarySearch?.('');
    } catch (_e) { /* noop */ }
    toast.success('Section A reset.');
  };

  const source = useSourceState();
  const docTypes = TASK_DOC_MAP[taskType] || [];
  const docTypeAccess = access?.docTypeAccess || {};
  const lockedDocs = docTypes.filter((d) => docTypeAccess[d] === false);
  const usageStatus = getDocTypeUsageStatus(docTypes, access);

  // Reset config when task type changes
  useEffect(() => { setTaskConfig({}); }, [taskType]);

  // Library reader hand-off (worksheet / test from a chapter)
  useEffect(() => {
    const p = takeReaderPrefill('quick');
    if (!p?.book) return;
    const subj = SUBJECTS.concat(['English Literature']).includes(p.subject) ? p.subject : '';
    setTaskType(p.taskType || 'Assignment');
    setBoard(p.board || '');
    setClassId(CLASSES.includes(p.classId) ? p.classId : '');
    setSubject(subj);
    source.applyImportedBook(p.book, p.chapterNumber);
    goWiz(p.board && CLASSES.includes(p.classId) && subj ? 3 : 2);
    toast.success(`"${p.book.title}" chapter loaded — review and generate.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ready =
    taskType && board && classId && subject &&
    docTypes.length > 0 && source.isReady(topic) && !usageStatus.allAtCap;
  const step1Ok = Boolean(taskType);
  const step2Ok = Boolean(board && classId && subject && source.isReady(topic));

  const onGenerate = async () => {
    if (usageStatus.allAtCap) {
      const labels = usageStatus.counters.map((c) => c.label.toLowerCase()).join(' & ');
      toast.error(`Monthly ${labels} limit reached on ${(access?.tier || 'Free').toUpperCase()}. Upgrade to continue.`, {
        action: {
          label: 'Upgrade',
          onClick: () => window.dispatchEvent(new CustomEvent('ce:upgrade-required', {
            detail: { code: 'USAGE_LIMIT_REACHED', requiredTier: 'starter', currentTier: access?.tier || 'free' },
          })),
        },
      });
      window.dispatchEvent(new CustomEvent('ce:upgrade-required', {
        detail: { code: 'USAGE_LIMIT_REACHED', requiredTier: 'starter', currentTier: access?.tier || 'free' },
      }));
      return;
    }
    if (!ready) {
      toast.error('Complete task type, curriculum & source to generate.');
      return;
    }
    setIsGenerating(true);
    try {
      const payload = _safeClonePayload({
        docTypes,
        source: {
          mode: source.sourceMode,
          bookId: source.sourceMode === 'topic' ? null : source.selectedBookId,
          chapterNumbers: source.sourceMode === 'topic' ? null : source.selectedChapterNumbers,
          multiChapterMode: source.sourceMode === 'topic' ? null : source.multiChapterMode,
          topic: topic || taskType,
          board, classId, subject,
          duration: parseInt(taskConfig.duration, 10) || 45,
          config: { taskType, ...taskConfig },
          // OPTIONAL — silently ignored server-side if missing/invalid.
          ...(blueprint?.blueprintId ? { blueprintId: blueprint.blueprintId } : {}),
        },
      });
      const res = await contentEngineApi.generateMulti(payload, {
        onProgress: ({ status, elapsedMs }) => {
          const secs = Math.round(elapsedMs / 1000);
          if (status === 'queued') toast.loading('Generating… queued', { id: 'sa-gen' });
          else if (status === 'running') toast.loading(`Generating… (${secs}s)`, { id: 'sa-gen' });
        },
      });
      toast.dismiss('sa-gen');
      if (keepPrevious && batch) {
        setPreviousBatches((prev) => [batch, ...prev].slice(0, 5));
      } else {
        setPreviousBatches([]);
      }
      setBatch(res);
      setBatchResult(res);
      const primary = res.results[0];
      if (primary) {
        setGeneratedAsset({
          assetId: primary.assetId,
          docType: primary.docType,
          title: primary.title,
          content: primary.content,
        });
      }
      toast.success(`${taskType} generated — ${res.results.length} document${res.results.length === 1 ? '' : 's'}.`);
      // Surface any auto-reconciliation notes (e.g. assessment marks were adjusted to match maxMarks)
      const allNotes = (res.results || [])
        .flatMap((r) => r?.content?._reconciliationNotes || []);
      if (allNotes.length > 0) {
        toast.info(allNotes.join(' '), { duration: 6000 });
      }
      // Surface any answer-key issues so the teacher can review before sending out
      const keyIssues = (res.results || [])
        .flatMap((r) => r?.content?._answerKeyIssues || []);
      if (keyIssues.length > 0) {
        toast.warning(`Answer-key check flagged ${keyIssues.length} item${keyIssues.length === 1 ? '' : 's'} — review before assigning.`, {
          description: keyIssues.slice(0, 3).join('; ') + (keyIssues.length > 3 ? ` (+${keyIssues.length - 3} more)` : ''),
          duration: 9000,
        });
      }
      if (mode === 'school') setCurrentStep(4);
      refreshAccess();
    } catch (err) {
      // Defensive: never let a non-cloneable Error reference leak into
      // CustomEvent or React state — extract a plain-JSON snapshot first.
      const code = err?.code;
      const message = String(err?.message || 'Generation failed.');
      let safeDetail = null;
      try { safeDetail = err?.detail ? JSON.parse(JSON.stringify(err.detail)) : null; }
      catch (_e) { safeDetail = null; }
      console.error('[generate] error:', code, message, safeDetail);
      if (code === 'UPGRADE_REQUIRED') {
        window.dispatchEvent(new CustomEvent('ce:upgrade-required', { detail: safeDetail || {} }));
      } else {
        toast.error(message);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const pickRecent = (item) => {
    const src = item?.source || {};
    const cfg = src.config || {};
    if (cfg.taskType) setTaskType(cfg.taskType);
    if (src.board) setBoard(src.board);
    if (src.classId) setClassId(src.classId);
    if (src.subject) setSubject(src.subject);
    if (src.topic) setTopic(src.topic);
    setTaskConfig(cfg);
    if (src.mode) source.setSourceMode(src.mode);
    if (src.bookId) source.setSelectedBookId(src.bookId);
    if (Array.isArray(src.chapterNumbers)) source.setSelectedChapterNumbers(src.chapterNumbers);
    toast.success(`Loaded "${item.label || 'previous task'}" — adjust & regenerate.`);
  };

  return (
    <SectionCard
      title={t('teacher.sectionA.title')}
      subtitle={t('teacher.sectionA.subtitle')}
      icon={Zap}
      tone="blue"
      sectionLetter="A"
      actions={
        <Badge className="bg-ae-blue/10 text-ae-blue dark:bg-ae-blue/20 border-0 text-[10px]" data-testid="section-a-badge">
          {t('teacher.sectionA.badge')}
        </Badge>
      }
    >
      <div data-testid="section-a">
        <WizardStepper step={wiz} maxStep={!step1Ok ? 1 : !step2Ok ? Math.min(wizMax, 2) : wizMax} onJump={setWiz} testIdPrefix="section-a" />

        {/* ── Wizard Step 1 — Choose task ── */}
        <div className={cn(wiz !== 1 && 'hidden')} data-testid="section-a-wizard-panel-1">
          <WizardPanelHeading step={1} title="What would you like to create?" description="Pick a task — the right document format is matched automatically." />
          <div className="ae-eyebrow mb-2.5">{t('teacher.labels.taskType')}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2.5" data-testid="section-a-task-cards">
            {TASK_TYPES_LIST.map((taskName) => {
              const cfg = TASK_TYPE_CONFIG[taskName];
              return (
                <SelectionCard
                  key={taskName}
                  testId={`section-a-task-${taskName.toLowerCase().replace(/[\s/]+/g, '-')}`}
                  active={taskType === taskName}
                  onClick={() => setTaskType(taskName)}
                  icon={TASK_ICON_MAP[cfg.icon] || FileText}
                  title={t(`teacher.taskTypes.${taskName}`, { defaultValue: taskName })}
                  description={cfg.description}
                />
              );
            })}
          </div>
          <div className="mt-6">
            <RecentStrip
              section="a"
              onPick={(item) => { pickRecent(item); setWizMax(3); setWiz(3); }}
              refreshKey={batch?.batchId || ''}
              accent="blue"
              accessTier={access?.tier || 'free'}
              mode={mode}
            />
          </div>
        </div>

        {/* ── Wizard Step 2 — Add context ── */}
        <div className={cn('space-y-6', wiz !== 2 && 'hidden')} data-testid="section-a-wizard-panel-2">
          <WizardPanelHeading step={2} title="Add your teaching context" description="Choose the curriculum and the material to build from." />
          <div>
            <div className="ae-eyebrow mb-2.5">{t('teacher.labels.curriculumContext')}</div>
            <CurriculumRow
              board={board} setBoard={setBoard}
              classId={classId} setClassId={setClassId}
              subject={subject} setSubject={setSubject}
              testIdPrefix="section-a"
            />
          </div>
          <div>
            <div className="ae-eyebrow mb-2.5">{t('teacher.labels.sourceMaterial')}</div>
            <SourcePicker
              source={source}
              board={board} classId={classId} subject={subject}
              topic={topic} setTopic={setTopic}
              testIdPrefix="section-a"
            />
          </div>
          {taskType && (
            <Disclosure
              testId="section-a-advanced"
              title="Fine-tune options"
              subtitle={`Optional ${taskType} settings — smart defaults apply if left blank.`}
            >
              <TaskConfigPanel
                taskType={taskType}
                config={taskConfig}
                setConfig={setTaskConfig}
                testIdPrefix="section-a"
              />
            </Disclosure>
          )}
        </div>

        {/* ── Wizard Step 3 — Review & generate ── */}
        <div className={cn('space-y-5', wiz !== 3 && 'hidden')} data-testid="section-a-wizard-panel-3">
          <WizardPanelHeading step={3} title="Review & generate" description="Everything looks right? Generate your documents in one click." />
          <ReviewSummary
            title="Your request"
            testIdPrefix="section-a"
            onEdit={setWiz}
            rows={[
              { key: 'task', label: 'Task', value: taskType, step: 1 },
              { key: 'documents', label: 'Documents', value: docTypes.map((d) => t(`teacher.docTypes.${d}`, { defaultValue: d })).join(' + '), step: 1 },
              { key: 'curriculum', label: 'Curriculum', value: describeCurriculum(board, classId, subject), step: 2 },
              { key: 'source', label: 'Source', value: describeSource(source, topic), step: 2 },
              { key: 'options', label: 'Options', value: describeConfig(taskConfig, ['taskType']) || 'Smart defaults', step: 2 },
              ...(blueprint ? [{ key: 'blueprint', label: 'Blueprint', value: blueprint.filename }] : []),
            ]}
          />
          <GenerateCta
            testIdPrefix="section-a"
            message={taskType && docTypes.length > 0
              ? <>Will generate <strong className="text-ae-ink dark:text-white">{docTypes.length} {docTypes.join(' + ')}</strong> document{docTypes.length === 1 ? '' : 's'}.</>
              : 'Pick a task type, curriculum & source to enable.'}
            usage={usageStatus}
            keepPrevious={keepPrevious}
            setKeepPrevious={setKeepPrevious}
            keepLabel={t('teacher.labels.keepPrevious')}
          >
            <Button
              data-testid="section-a-generate-btn"
              onClick={onGenerate}
              disabled={!ready || isGenerating}
              title={usageStatus.allAtCap ? `Monthly limit reached on ${(access?.tier || 'Free').toUpperCase()} — Upgrade to continue` : ''}
              size="lg"
              className="gap-2"
            >
              {isGenerating ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> {t('teacher.cta.generating')}</>
              ) : usageStatus.allAtCap ? (
                <><Crown className="h-4 w-4" /> {t('teacher.cta.limitReached')}</>
              ) : (
                <><Sparkles className="h-4 w-4" /> {taskType ? `${t('teacher.cta.generatePrefix')} ${taskType}` : t('teacher.cta.generateNow')}</>
              )}
            </Button>
          </GenerateCta>
        </div>

        <WizardNav
          step={wiz}
          onBack={() => setWiz((s) => Math.max(1, s - 1))}
          onNext={() => goWiz(wiz + 1)}
          canNext={wiz === 1 ? step1Ok : step2Ok}
          nextLabel={wiz === 1 ? 'Add context' : 'Review'}
          blockedHint={wiz === 1 ? 'Select a task to continue' : 'Set board, class, subject & source'}
          testIdPrefix="section-a"
        />

        {/* Inline batch results */}
        {batch && batch.results?.length > 0 && (
          <BatchResultList batch={batch} testIdPrefix="section-a" access={access} />
        )}
        {previousBatches.length > 0 && (
          <div className="mt-4 pt-3 border-t border-dashed border-slate-300 dark:border-slate-700">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
              Earlier batches ({previousBatches.length})
            </div>
            {previousBatches.map((b, i) => (
              <div key={i} className="opacity-70">
                <BatchResultList batch={b} testIdPrefix={`section-a-prev-${i}`} access={access} />
              </div>
            ))}
          </div>
        )}
        <ResetRow testId="section-a-reset-btn" label="Reset Section A" hint="Start fresh — clear all Section A selections." onReset={resetSectionA} />
      </div>
    </SectionCard>
  );
}

// ─────────────────────────────────────────────────────────────
// Section B — Plan-based Generator
// ─────────────────────────────────────────────────────────────
function SectionB(props) {
  const { t } = useTranslation();
  const { access, docTypeCaps, mode, isGenerating, setIsGenerating, setBatchResult, setGeneratedAsset, setCurrentStep, refreshAccess, blueprint } = props;

  const [teachingGoal, setTeachingGoal] = useState('');
  const [planningScope, setPlanningScope] = useState('single_class');
  const [board, setBoard] = useState('');
  const [classId, setClassId] = useState('');
  const [subject, setSubject] = useState('');
  const [topic, setTopic] = useState('');
  const [docTypes, setDocTypes] = useState(['summary']);
  const [batch, setBatch] = useState(null);
  const [planConfig, setPlanConfig] = useState({});
  const [keepPrevious, setKeepPrevious] = useState(false);
  const [previousBatches, setPreviousBatches] = useState([]);
  const [wiz, setWiz] = useState(1);
  const [wizMax, setWizMax] = useState(1);
  const goWiz = (n) => { setWiz(n); setWizMax((m) => Math.max(m, n)); };

  // C1 — Reset all Section B selections to defaults.
  const resetSectionB = () => {
    setTeachingGoal('');
    setPlanningScope('single_class');
    setBoard('');
    setClassId('');
    setSubject('');
    setTopic('');
    setDocTypes(['summary']);
    setBatch(null);
    setPlanConfig({});
    setKeepPrevious(false);
    setWiz(1);
    setWizMax(1);
    // Clear source/library state
    try {
      source.setSourceMode?.('topic');
      source.setSelectedBookId?.('');
      source.setSelectedChapterNumbers?.([]);
      source.setMultiChapterMode?.('per_chapter');
      source.setLibBoard?.('');
      source.setLibClass?.('');
      source.setLibSubject?.('');
      source.setLibrarySearch?.('');
    } catch (_e) { /* noop */ }
    toast.success('Section B reset.');
  };

  const source = useSourceState();
  const docTypeAccess = access?.docTypeAccess || {};
  const tierName = (access?.tier || 'free').toUpperCase();
  const sectionBUsage = getDocTypeUsageStatus(docTypes, access);

  // Planning Scope = Lesson Kit auto-selects the 4 non-PPT doc types.
  // PPT is intentionally excluded — it's a standalone scope (different pipeline).
  useEffect(() => {
    if (planningScope === 'lesson_kit') {
      const nonPpt = ['summary', 'lesson_plan', 'assessment', 'teaching_flow'];
      setDocTypes(nonPpt.slice(0, nonPpt.length));
    }
  }, [planningScope]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Library reader hand-off (summary / lesson plan / slides from a chapter)
  useEffect(() => {
    const p = takeReaderPrefill('plan');
    if (!p?.book) return;
    const subj = SUBJECTS.concat(['English Literature']).includes(p.subject) ? p.subject : '';
    setTeachingGoal('syllabus_completion');
    setPlanningScope('single_class');
    setDocTypes([p.docType || 'summary']);
    setBoard(p.board || '');
    setClassId(CLASSES.includes(p.classId) ? p.classId : '');
    setSubject(subj);
    source.applyImportedBook(p.book, p.chapterNumber);
    goWiz(p.board && CLASSES.includes(p.classId) && subj ? 3 : 2);
    toast.success(`"${p.book.title}" chapter loaded — review and generate.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDocType = (dt) => {
    // UI parity — all cards remain selectable regardless of tier.
    // Backend enforces access via 402 UPGRADE_REQUIRED on generate.
    // Mutual exclusivity — PPT Slides is standalone (different generation pipeline).
    const isPpt = dt === 'ppt';
    const hasPpt = docTypes.includes('ppt');
    const hasNonPpt = docTypes.some((x) => x !== 'ppt');
    if (docTypes.includes(dt)) { setDocTypes(docTypes.filter((x) => x !== dt)); return; }
    if (isPpt && hasNonPpt) {
      toast.info('PPT Slides works standalone — switched selection to PPT only.', { id: 'ppt-exclusive' });
      setDocTypes(['ppt']);
      return;
    }
    if (!isPpt && hasPpt) {
      toast.info('PPT Slides works standalone — switched selection to docs only.', { id: 'ppt-exclusive' });
      setDocTypes([dt]);
      return;
    }
    setDocTypes([...docTypes, dt]);
  };

  const ready =
    teachingGoal && planningScope && board && classId && subject &&
    docTypes.length > 0 && source.isReady(topic) && !sectionBUsage.allAtCap;
  const step1Ok = Boolean(teachingGoal && planningScope && docTypes.length > 0);
  const step2Ok = Boolean(board && classId && subject && source.isReady(topic));

  const onGenerate = async () => {
    if (sectionBUsage.allAtCap) {
      const labels = sectionBUsage.counters.map((c) => c.label.toLowerCase()).join(' & ');
      toast.error(`Monthly ${labels} limit reached on ${tierName}. Upgrade to continue.`, {
        action: {
          label: 'Upgrade',
          onClick: () => window.dispatchEvent(new CustomEvent('ce:upgrade-required', {
            detail: { code: 'USAGE_LIMIT_REACHED', requiredTier: 'starter', currentTier: access?.tier || 'free' },
          })),
        },
      });
      window.dispatchEvent(new CustomEvent('ce:upgrade-required', {
        detail: { code: 'USAGE_LIMIT_REACHED', requiredTier: 'starter', currentTier: access?.tier || 'free' },
      }));
      return;
    }
    if (!ready) {
      toast.error('Complete goal, scope, curriculum, source & doc types.');
      return;
    }
    setIsGenerating(true);
    try {
      const payload = _safeClonePayload({
        docTypes,
        source: {
          mode: source.sourceMode,
          bookId: source.sourceMode === 'topic' ? null : source.selectedBookId,
          chapterNumbers: source.sourceMode === 'topic' ? null : source.selectedChapterNumbers,
          multiChapterMode: source.sourceMode === 'topic' ? null : source.multiChapterMode,
          topic: topic || subject,
          board, classId, subject,
          duration: parseInt(planConfig.duration, 10) || 45,
          config: { teachingGoal, planningScope, ...planConfig },
          // OPTIONAL — only sent when a blueprint was successfully uploaded.
          // Backend silently ignores if missing/invalid; never blocks generation.
          ...(blueprint?.blueprintId ? { blueprintId: blueprint.blueprintId } : {}),
        },
      });
      const res = await contentEngineApi.generateMulti(payload, {
        onProgress: ({ status, elapsedMs }) => {
          const secs = Math.round(elapsedMs / 1000);
          if (status === 'queued') toast.loading('Generating… queued', { id: 'sb-gen' });
          else if (status === 'running') toast.loading(`Generating… (${secs}s)`, { id: 'sb-gen' });
        },
      });
      toast.dismiss('sb-gen');
      if (keepPrevious && batch) {
        setPreviousBatches((prev) => [batch, ...prev].slice(0, 5));
      } else {
        setPreviousBatches([]);
      }
      setBatch(res);
      setBatchResult(res);
      const primary = res.results[0];
      if (primary) {
        setGeneratedAsset({
          assetId: primary.assetId,
          docType: primary.docType,
          title: primary.title,
          content: primary.content,
        });
      }
      toast.success(`Generated ${res.results.length} document${res.results.length === 1 ? '' : 's'} aligned to your plan.`);
      if (mode === 'school') setCurrentStep(4);
      refreshAccess();
    } catch (err) {
      // Defensive: never let a non-cloneable Error reference leak into
      // CustomEvent or React state — extract a plain-JSON snapshot first.
      const code = err?.code;
      const message = String(err?.message || 'Generation failed.');
      let safeDetail = null;
      try { safeDetail = err?.detail ? JSON.parse(JSON.stringify(err.detail)) : null; }
      catch (_e) { safeDetail = null; }
      console.error('[generate] error:', code, message, safeDetail);
      if (code === 'UPGRADE_REQUIRED') {
        window.dispatchEvent(new CustomEvent('ce:upgrade-required', { detail: safeDetail || {} }));
      } else {
        toast.error(message);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const pickRecent = (item) => {
    const src = item?.source || {};
    const cfg = src.config || {};
    if (cfg.teachingGoal) setTeachingGoal(cfg.teachingGoal);
    if (cfg.planningScope) setPlanningScope(cfg.planningScope);
    if (src.board) setBoard(src.board);
    if (src.classId) setClassId(src.classId);
    if (src.subject) setSubject(src.subject);
    if (src.topic) setTopic(src.topic);
    if (Array.isArray(item.docTypes) && item.docTypes.length > 0) setDocTypes(item.docTypes);
    setPlanConfig(cfg);
    if (src.mode) source.setSourceMode(src.mode);
    if (src.bookId) source.setSelectedBookId(src.bookId);
    if (Array.isArray(src.chapterNumbers)) source.setSelectedChapterNumbers(src.chapterNumbers);
    toast.success(`Loaded "${item.label || 'previous plan'}" — adjust & regenerate.`);
  };

  return (
    <SectionCard
      title={t('teacher.sectionB.title')}
      subtitle={t('teacher.sectionB.subtitle')}
      icon={Target}
      tone="cyan"
      sectionLetter="B"
      actions={
        <Badge className="bg-ae-cyan/15 text-cyan-800 dark:text-ae-cyan border-0 text-[10px]" data-testid="section-b-badge">
          {t('teacher.sectionB.badge')}
        </Badge>
      }
    >
      <div data-testid="section-b">
        <WizardStepper step={wiz} maxStep={!step1Ok ? 1 : !step2Ok ? Math.min(wizMax, 2) : wizMax} onJump={setWiz} testIdPrefix="section-b" />

        {/* ── Wizard Step 1 — Choose task (goal, scope, documents) ── */}
        <div className={cn('space-y-6', wiz !== 1 && 'hidden')} data-testid="section-b-wizard-panel-1">
          <WizardPanelHeading step={1} title="Plan your lesson outcome" description="Set the goal and scope, then choose which documents to create." />
          <div>
            <div className="ae-eyebrow mb-2.5">{t('teacher.labels.teachingGoal')}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="section-b-goals">
              {TEACHING_GOALS.map((g) => (
                <SelectionCard
                  key={g.id}
                  compact
                  testId={`section-b-goal-${g.id}`}
                  active={teachingGoal === g.id}
                  onClick={() => setTeachingGoal(g.id)}
                  icon={GOAL_ICON_MAP[g.icon] || Sparkles}
                  title={g.label}
                />
              ))}
            </div>
          </div>
          <div>
            <div className="ae-eyebrow mb-2.5">{t('teacher.labels.planningScope')}</div>
            <div className="inline-flex flex-wrap gap-1 p-1 rounded-xl bg-ae-bg dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-800" data-testid="section-b-scopes">
              {PLANNING_SCOPES.map((p) => {
                const active = planningScope === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`section-b-scope-${p.id}`}
                    onClick={() => setPlanningScope(p.id)}
                    aria-pressed={active}
                    className={cn(
                      'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-[background-color,color,box-shadow] duration-200',
                      active ? 'bg-white dark:bg-slate-900 text-ae-ink dark:text-white shadow-soft' : 'text-ae-slate hover:text-ae-ink dark:hover:text-white'
                    )}
                  >
                    {p.label}
                    {p.id === 'lesson_kit' && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-ae-warning/15 text-amber-700 dark:text-amber-300 font-bold">Full Kit</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <div className="ae-eyebrow">{t('teacher.labels.documentsToCreate')}</div>
              <Badge variant="secondary" className="text-[10px]" data-testid="section-b-cap">
                {t('teacher.labels.selectedCount', { count: docTypes.length })}
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5" data-testid="section-b-doctypes">
              {[
                { id: 'summary', icon: AlignLeft, fmt: '.docx + .pdf' },
                { id: 'lesson_plan', icon: BookOpen, fmt: '.docx + .pdf' },
                { id: 'assessment', icon: ListChecks, fmt: '.docx + .pdf' },
                { id: 'teaching_flow', icon: PlayCircle, fmt: '.docx + .pdf' },
                { id: 'ppt', icon: Presentation, fmt: '.pptx' },
              ].map((dt) => {
                const selected = docTypes.includes(dt.id);
                // Mutual exclusivity — PPT Slides is standalone. Grey out cross-type cards.
                const pptSelected = docTypes.includes('ppt');
                const nonPptSelected = docTypes.some((x) => x !== 'ppt');
                const crossExcluded = !selected && (
                  (dt.id === 'ppt' && nonPptSelected) ||
                  (dt.id !== 'ppt' && pptSelected)
                );
                return (
                  <div
                    key={dt.id}
                    className={cn(crossExcluded && 'opacity-40')}
                    title={crossExcluded
                      ? (dt.id === 'ppt'
                          ? 'PPT Slides works standalone — deselect the other docs first.'
                          : 'PPT Slides is selected — deselect it to mix with other docs.')
                      : undefined}
                  >
                    <SelectionCard
                      compact
                      testId={`section-b-doctype-${dt.id}`}
                      active={selected}
                      onClick={() => toggleDocType(dt.id)}
                      icon={dt.icon}
                      title={t(`teacher.docTypes.${dt.id}`)}
                      description={dt.fmt}
                    />
                  </div>
                );
              })}
            </div>
            <p
              data-testid="section-b-doctype-standalone-note"
              className="mt-2.5 text-[11px] leading-relaxed text-muted-foreground"
            >
              <strong className="text-ae-ink dark:text-slate-200">PPT Slides works standalone.</strong> Pick PPT on its own (designed slide deck) <em>or</em> any mix of Summary / Lesson Plan / Questionnaire / Teaching Flow (.docx&nbsp;+&nbsp;.pdf).
            </p>
          </div>
          <RecentStrip
            section="b"
            onPick={(item) => { pickRecent(item); setWizMax(3); setWiz(3); }}
            refreshKey={batch?.batchId || ''}
            accent="cyan"
            accessTier={access?.tier || 'free'}
            mode={mode}
          />
        </div>

        {/* ── Wizard Step 2 — Add context ── */}
        <div className={cn('space-y-6', wiz !== 2 && 'hidden')} data-testid="section-b-wizard-panel-2">
          <WizardPanelHeading step={2} title="Add your teaching context" description="Choose the curriculum and content source for this plan." />
          <div>
            <div className="ae-eyebrow mb-2.5">{t('teacher.labels.curriculumContext')}</div>
            <CurriculumRow
              board={board} setBoard={setBoard}
              classId={classId} setClassId={setClassId}
              subject={subject} setSubject={setSubject}
              testIdPrefix="section-b"
            />
          </div>
          <div>
            <div className="ae-eyebrow mb-2.5">{t('teacher.labels.contentSource')}</div>
            <SourcePicker
              source={source}
              board={board} classId={classId} subject={subject}
              topic={topic} setTopic={setTopic}
              testIdPrefix="section-b"
            />
          </div>
          <Disclosure
            testId="section-b-advanced"
            title={t('teacher.labels.planConfiguration')}
            subtitle="Optional — duration, difficulty, detailing, day-wise planner & due date."
          >
            <div data-testid="section-b-plan-config" className="pt-1">
              {(() => {
                // Difficulty applies to Assessments only.
                // Detailing Level applies to Summary / Lesson Plan / Teaching Flow.
                // When all four are selected, both fields render.
                const hasAssessment = docTypes.includes('assessment');
                const hasNonAssessmentDoc = docTypes.some((d) => ['summary', 'lesson_plan', 'teaching_flow'].includes(d));
                const hasLessonPlan = docTypes.includes('lesson_plan');
                const showDifficulty = hasAssessment;
                const showDetailing = hasNonAssessmentDoc;
                const greyDifficulty = !showDifficulty;
                const greyDetailing = !showDetailing;
                const daywiseOn = !!planConfig.daywiseEnabled && hasLessonPlan;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-medium text-slate-700 dark:text-slate-300">Duration (mins)</label>
                      <Input
                        data-testid="section-b-config-duration"
                        type="number" min={5}
                        value={planConfig.duration ?? ''}
                        onChange={(e) => setPlanConfig((p) => ({ ...p, duration: e.target.value === '' ? '' : Number(e.target.value) }))}
                        placeholder="45"
                        className="h-9 text-xs"
                      />
                    </div>
                    <div className="flex flex-col gap-1" data-testid="section-b-config-difficulty-wrap">
                      <label className={cn(
                        'text-[11px] font-medium',
                        greyDifficulty ? 'text-slate-400 dark:text-slate-600' : 'text-slate-700 dark:text-slate-300'
                      )}>
                        Difficulty {greyDifficulty && <span className="text-[9px]">· Questionnaire-only</span>}
                      </label>
                      <select
                        data-testid="section-b-config-difficulty"
                        value={greyDifficulty ? '' : (planConfig.difficulty || '')}
                        disabled={greyDifficulty}
                        title={greyDifficulty ? 'Difficulty applies only when Questionnaire is selected.' : undefined}
                        onChange={(e) => setPlanConfig((p) => ({ ...p, difficulty: e.target.value }))}
                        className={cn(
                          'h-9 px-2.5 text-xs rounded-lg border focus:outline-none focus:ring-2 focus:ring-ae-blue/30 focus:border-ae-blue bg-white dark:bg-slate-900',
                          greyDifficulty ? 'border-slate-200 dark:border-slate-800 opacity-50 cursor-not-allowed' : 'border-slate-200 dark:border-slate-700'
                        )}
                      >
                        <option value="">Select…</option>
                        <option value="Easy">Easy</option>
                        <option value="Medium">Medium</option>
                        <option value="Hard">Hard</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1" data-testid="section-b-config-detailing-wrap">
                      <label className={cn(
                        'text-[11px] font-medium',
                        greyDetailing ? 'text-slate-400 dark:text-slate-600' : 'text-slate-700 dark:text-slate-300'
                      )}>
                        Detailing Level {greyDetailing && <span className="text-[9px]">· Docs-only</span>}
                      </label>
                      <select
                        data-testid="section-b-config-detailing-level"
                        value={greyDetailing ? '' : (planConfig.detailingLevel || '')}
                        disabled={greyDetailing}
                        title={greyDetailing ? 'Detailing Level applies to Summary / Lesson Plan / Teaching Flow.' : undefined}
                        onChange={(e) => setPlanConfig((p) => ({ ...p, detailingLevel: e.target.value }))}
                        className={cn(
                          'h-9 px-2.5 text-xs rounded-lg border focus:outline-none focus:ring-2 focus:ring-ae-blue/30 focus:border-ae-blue bg-white dark:bg-slate-900',
                          greyDetailing ? 'border-slate-200 dark:border-slate-800 opacity-50 cursor-not-allowed' : 'border-slate-200 dark:border-slate-700'
                        )}
                      >
                        <option value="">Select…</option>
                        <option value="Concise">Concise</option>
                        <option value="Standard">Standard</option>
                        <option value="Detailed">Detailed</option>
                      </select>
                    </div>
                    {/* ── Day-wise Planner (lesson_plan only — feature-flagged, additive) ── */}
                    {hasLessonPlan && (
                      <div
                        className="flex flex-col gap-1 lg:col-span-2 rounded-md border border-dashed border-emerald-300 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-900/10 px-2 py-1.5"
                        data-testid="section-b-config-daywise-wrap"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <label className="text-[11px] font-medium text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            Day-wise Planner
                            <span className="text-[9px] text-emerald-700/70 dark:text-emerald-400/70 font-normal">· Lesson-Plan only</span>
                          </label>
                          <label className="inline-flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              data-testid="section-b-config-daywise-toggle"
                              className="h-3.5 w-3.5 accent-emerald-600"
                              checked={!!planConfig.daywiseEnabled}
                              onChange={(e) => setPlanConfig((p) => ({
                                ...p,
                                daywiseEnabled: e.target.checked,
                                teachingDays: e.target.checked ? (p.teachingDays || 5) : p.teachingDays,
                                autoDistributeTopics: p.autoDistributeTopics ?? true,
                              }))}
                            />
                            <span className="text-[10px] text-emerald-800 dark:text-emerald-300 font-medium">{planConfig.daywiseEnabled ? 'On' : 'Off'}</span>
                          </label>
                        </div>
                        {daywiseOn && (
                          <div className="flex items-center gap-2 mt-1">
                            <label className="text-[10px] text-slate-600 dark:text-slate-400 whitespace-nowrap">Teaching Days</label>
                            <Input
                              data-testid="section-b-config-teaching-days"
                              type="number"
                              min={1}
                              max={60}
                              value={planConfig.teachingDays ?? 5}
                              onChange={(e) => {
                                const v = e.target.value === '' ? '' : Math.max(1, Math.min(60, Number(e.target.value)));
                                setPlanConfig((p) => ({ ...p, teachingDays: v }));
                              }}
                              className="h-7 w-16 text-xs"
                            />
                            <label className="inline-flex items-center gap-1 cursor-pointer ml-1" title="Auto-distribute chapter subtopics evenly across days">
                              <input
                                type="checkbox"
                                data-testid="section-b-config-auto-distribute"
                                className="h-3 w-3 accent-emerald-600"
                                checked={planConfig.autoDistributeTopics !== false}
                                onChange={(e) => setPlanConfig((p) => ({ ...p, autoDistributeTopics: e.target.checked }))}
                              />
                              <span className="text-[10px] text-slate-600 dark:text-slate-400">Auto-distribute</span>
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Due Date — school users only. Individual teachers don't assign class-level deadlines. */}
                    {(access?.schoolId || access?.isSchoolTeacher) && (
                      <div className="flex flex-col gap-1" data-testid="section-b-config-due-date-wrap">
                        <label className="text-[11px] font-medium text-slate-700 dark:text-slate-300">Due Date</label>
                        <Input
                          data-testid="section-b-config-due-date"
                          type="date"
                          value={planConfig.dueDate || ''}
                          onChange={(e) => setPlanConfig((p) => ({ ...p, dueDate: e.target.value }))}
                          className="h-9 text-xs"
                        />
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </Disclosure>
        </div>

        {/* ── Wizard Step 3 — Review & generate ── */}
        <div className={cn('space-y-5', wiz !== 3 && 'hidden')} data-testid="section-b-wizard-panel-3">
          <WizardPanelHeading step={3} title="Review & generate" description="Confirm your plan and generate every aligned document together." />
          <ReviewSummary
            title="Your plan"
            testIdPrefix="section-b"
            onEdit={setWiz}
            rows={[
              { key: 'goal', label: 'Teaching goal', value: TEACHING_GOALS.find((g) => g.id === teachingGoal)?.label, step: 1 },
              { key: 'scope', label: 'Planning scope', value: PLANNING_SCOPES.find((p) => p.id === planningScope)?.label, step: 1 },
              { key: 'documents', label: 'Documents', value: docTypes.map((d) => t(`teacher.docTypes.${d}`, { defaultValue: d })).join(' + '), step: 1 },
              { key: 'curriculum', label: 'Curriculum', value: describeCurriculum(board, classId, subject), step: 2 },
              { key: 'source', label: 'Source', value: describeSource(source, topic), step: 2 },
              { key: 'options', label: 'Plan settings', value: describeConfig(planConfig, ['teachingGoal', 'planningScope']) || 'Smart defaults', step: 2 },
              ...(blueprint ? [{ key: 'blueprint', label: 'Blueprint', value: blueprint.filename }] : []),
            ]}
          />
          <GenerateCta
            testIdPrefix="section-b"
            message={ready
              ? <>Ready — will produce <strong className="text-ae-ink dark:text-white">{docTypes.length}</strong> aligned document{docTypes.length === 1 ? '' : 's'}.</>
              : sectionBUsage.allAtCap
                ? `Monthly limit reached on ${tierName} — Upgrade to continue.`
                : 'Set goal, scope, curriculum, source & at least one document type.'}
            usage={sectionBUsage}
            keepPrevious={keepPrevious}
            setKeepPrevious={setKeepPrevious}
            keepLabel={t('teacher.labels.keepPrevious')}
          >
            <Button
              data-testid="section-b-generate-btn"
              onClick={onGenerate}
              disabled={!ready || isGenerating}
              title={sectionBUsage.allAtCap ? `Monthly limit reached on ${tierName} — Upgrade to continue` : ''}
              size="lg"
              className="gap-2"
            >
              {isGenerating ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> {t('teacher.cta.generating')}</>
              ) : sectionBUsage.allAtCap ? (
                <><Crown className="h-4 w-4" /> {t('teacher.cta.limitReached')}</>
              ) : (
                <><Sparkles className="h-4 w-4" /> {t('teacher.cta.generateNow')}</>
              )}
            </Button>
          </GenerateCta>
        </div>

        <WizardNav
          step={wiz}
          onBack={() => setWiz((s) => Math.max(1, s - 1))}
          onNext={() => goWiz(wiz + 1)}
          canNext={wiz === 1 ? step1Ok : step2Ok}
          nextLabel={wiz === 1 ? 'Add context' : 'Review'}
          blockedHint={wiz === 1 ? 'Pick a goal and at least one document' : 'Set board, class, subject & source'}
          testIdPrefix="section-b"
        />

        {batch && batch.results?.length > 0 && (
          <BatchResultList batch={batch} testIdPrefix="section-b" access={access} />
        )}
        {previousBatches.length > 0 && (
          <div className="mt-4 pt-3 border-t border-dashed border-slate-300 dark:border-slate-700">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
              Earlier batches ({previousBatches.length})
            </div>
            {previousBatches.map((b, i) => (
              <div key={i} className="opacity-70">
                <BatchResultList batch={b} testIdPrefix={`section-b-prev-${i}`} access={access} />
              </div>
            ))}
          </div>
        )}
        <ResetRow testId="section-b-reset-btn" label="Reset Section B" hint="Start fresh — clear all Section B selections." onReset={resetSectionB} />
      </div>
    </SectionCard>
  );
}

// ─────────────────────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────────────────────
function useSourceState() {
  const [sourceMode, setSourceMode] = useState('topic');
  const [libBoard, setLibBoard] = useState('');
  const [libClass, setLibClass] = useState('');
  const [libSubject, setLibSubject] = useState('');
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryBooks, setLibraryBooks] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState('');
  // Multi-chapter selection — combined into one source per spec (4a)
  const [selectedChapterNumbers, setSelectedChapterNumbers] = useState([]);
  // Task A2 — output mode when >1 chapters selected ('combined' | 'per_chapter')
  const [multiChapterMode, setMultiChapterMode] = useState('per_chapter');
  const [uploadedBook, setUploadedBook] = useState(null);
  const [uploading, setUploading] = useState(false);
  const pendingChapterRef = useRef(null);

  // Fetch library books when in library mode
  useEffect(() => {
    let cancelled = false;
    if (sourceMode !== 'library') return;
    contentEngineApi
      .listBooks(libBoard || undefined, libClass || undefined, libSubject || undefined)
      .then((r) => !cancelled && setLibraryBooks(r.books || []))
      .catch(() => !cancelled && setLibraryBooks([]));
    return () => { cancelled = true; };
  }, [sourceMode, libBoard, libClass, libSubject]);

  const selectedBook = useMemo(() => {
    if (uploadedBook && selectedBookId === uploadedBook.bookId) return uploadedBook;
    return libraryBooks.find((b) => b.bookId === selectedBookId) || null;
  }, [selectedBookId, libraryBooks, uploadedBook]);

  // When the book changes, clear chapter selections (or apply the reader hand-off chapter)
  useEffect(() => {
    const pending = pendingChapterRef.current;
    // Only consume the hand-off chapter once the matching book is selected (StrictMode re-runs effects)
    if (pending && pending.bookId === selectedBookId) {
      pendingChapterRef.current = null;
      setSelectedChapterNumbers(pending.n ? [pending.n] : []);
      return;
    }
    setSelectedChapterNumbers([]);
  }, [selectedBookId]);

  const applyImportedBook = (book, chapterNumber) => {
    const n = parseInt(chapterNumber, 10) || book.chapters?.[0]?.number || null;
    pendingChapterRef.current = { bookId: book.bookId, n };
    setUploadedBook(book);
    setSourceMode('upload');
    setSelectedBookId(book.bookId);
  };

  const toggleChapter = (num) => {
    const n = parseInt(num, 10);
    if (Number.isNaN(n)) return;
    setSelectedChapterNumbers((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].sort((a, b) => a - b)
    );
  };
  const setSingleChapter = (num) => {
    const n = parseInt(num, 10);
    if (Number.isNaN(n)) return;
    setSelectedChapterNumbers([n]);
  };

  const handleUploadBook = async (file, ctx = {}) => {
    if (!file) return;
    setUploading(true);
    try {
      const book = await contentEngineApi.uploadBook(file, ctx);
      setUploadedBook(book);
      setSelectedBookId(book.bookId);
      if (book.chapters?.length) setSelectedChapterNumbers([book.chapters[0].number]);
      toast.success(`Uploaded • ${book.chapters?.length || 0} chapter${book.chapters?.length === 1 ? '' : 's'} detected.`);
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const isReady = (topic) => {
    if (sourceMode === 'topic') return Boolean(topic);
    if (sourceMode === 'library') return Boolean(selectedBookId && selectedChapterNumbers.length > 0);
    if (sourceMode === 'upload') return Boolean(uploadedBook && selectedChapterNumbers.length > 0);
    return false;
  };

  return {
    sourceMode, setSourceMode,
    libBoard, setLibBoard, libClass, setLibClass, libSubject, setLibSubject,
    librarySearch, setLibrarySearch,
    libraryBooks, selectedBookId, setSelectedBookId,
    selectedChapterNumbers, setSelectedChapterNumbers,
    multiChapterMode, setMultiChapterMode,
    toggleChapter, setSingleChapter,
    uploadedBook, uploading,
    selectedBook,
    handleUploadBook,
    applyImportedBook,
    isReady,
  };
}

function CurriculumRow({ board, setBoard, classId, setClassId, subject, setSubject, testIdPrefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
      <Select value={board} onValueChange={setBoard}>
        <SelectTrigger data-testid={`${testIdPrefix}-board`} className="h-9 text-sm">
          <SelectValue placeholder="Select board" />
        </SelectTrigger>
        <SelectContent>
          {BOARDS.map((b) => (
            <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={classId} onValueChange={setClassId}>
        <SelectTrigger data-testid={`${testIdPrefix}-class`} className="h-9 text-sm">
          <SelectValue placeholder="Select class" />
        </SelectTrigger>
        <SelectContent>
          {CLASSES.map((c) => (
            <SelectItem key={c} value={c}>Class {c}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={subject} onValueChange={setSubject}>
        <SelectTrigger data-testid={`${testIdPrefix}-subject`} className="h-9 text-sm">
          <SelectValue placeholder="Select subject" />
        </SelectTrigger>
        <SelectContent>
          {SUBJECTS.concat(['English Literature']).map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ChapterMultiSelect — multi-select chapter list with select-all/clear
// ─────────────────────────────────────────────────────────────
function ChapterMultiSelect({ chapters, selected, onToggle, onSelectAll, onClear, multiMode, setMultiMode, testId = 'chapter-multi' }) {
  if (!Array.isArray(chapters) || chapters.length === 0) return null;
  const selectedSet = new Set((selected || []).map((n) => Number(n)));
  const allSelected = selectedSet.size === chapters.length;
  const showModeToggle = selectedSet.size > 1 && typeof setMultiMode === 'function';

  return (
    <div data-testid={testId} className="rounded-lg border border-slate-200/70 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 p-2.5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Layers className="h-3.5 w-3.5 text-slate-500" />
          <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">
            Chapters
          </span>
          <Badge variant="secondary" className="text-[9px]" data-testid={`${testId}-count`}>
            {selectedSet.size} / {chapters.length}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`${testId}-select-all`}
            onClick={allSelected ? onClear : onSelectAll}
            className="text-[10px] font-medium text-slate-600 dark:text-slate-300 hover:underline"
          >
            {allSelected ? 'Clear all' : 'Select all'}
          </button>
        </div>
      </div>
      {showModeToggle && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-md border border-amber-300/60 bg-amber-50/60 dark:bg-amber-900/15 dark:border-amber-800/60 px-2 py-1.5" data-testid={`${testId}-mode`}>
          <div className="text-[10px] text-amber-800 dark:text-amber-200 leading-tight">
            <span className="font-semibold">Multi-chapter output:</span>{' '}
            {multiMode === 'per_chapter'
              ? 'Generates a separate doc per chapter.'
              : 'Merges all selected chapters into a single doc.'}
          </div>
          <div className="inline-flex rounded-md border border-amber-300/60 dark:border-amber-800/60 overflow-hidden">
            <button
              type="button"
              data-testid={`${testId}-mode-per-chapter`}
              onClick={() => setMultiMode('per_chapter')}
              className={cn(
                'px-2 py-0.5 text-[10px] font-semibold transition-colors',
                multiMode === 'per_chapter'
                  ? 'bg-amber-500 text-white'
                  : 'bg-white dark:bg-slate-900 text-amber-700 dark:text-amber-300 hover:bg-amber-100/60'
              )}
            >
              Per chapter
            </button>
            <button
              type="button"
              data-testid={`${testId}-mode-combined`}
              onClick={() => setMultiMode('combined')}
              className={cn(
                'px-2 py-0.5 text-[10px] font-semibold transition-colors border-l border-amber-300/60 dark:border-amber-800/60',
                multiMode === 'combined'
                  ? 'bg-amber-500 text-white'
                  : 'bg-white dark:bg-slate-900 text-amber-700 dark:text-amber-300 hover:bg-amber-100/60'
              )}
            >
              Combined
            </button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-[260px] overflow-y-auto pr-1">
        {chapters.map((ch) => {
          const num = Number(ch.number);
          const active = selectedSet.has(num);
          return (
            <button
              key={num}
              type="button"
              data-testid={`${testId}-chapter-${num}`}
              onClick={() => onToggle(num)}
              className={cn(
                'flex items-start gap-2 p-2 rounded-md border text-left transition-colors',
                active
                  ? 'border-slate-900 dark:border-white bg-slate-50 dark:bg-slate-800'
                  : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 bg-white dark:bg-slate-900'
              )}
            >
              <div className={cn(
                'h-4 w-4 rounded border flex-shrink-0 mt-0.5 flex items-center justify-center',
                active
                  ? 'bg-slate-900 dark:bg-white border-slate-900 dark:border-white'
                  : 'border-slate-300 dark:border-slate-600'
              )}>
                {active && <Check className="h-3 w-3 text-white dark:text-slate-900" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">
                  Ch {num}: {ch.title || `Chapter ${num}`}
                </div>
                {ch.summary && (
                  <div className="text-[10px] text-muted-foreground truncate">
                    {ch.summary}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SourcePicker({ source, board, classId, subject, topic, setTopic, testIdPrefix }) {
  const { t } = useTranslation();
  // BOSCO Capture (Phase 1) — controls 4th source card behaviour.
  const captureAccess = useBoscoCaptureAccess({ enabled: true });
  const [captureUpsellOpen, setCaptureUpsellOpen] = useState(false);
  const filteredBooks = useMemo(() => {
    const needle = (source.librarySearch || '').trim().toLowerCase();
    if (!needle) return source.libraryBooks;
    return source.libraryBooks.filter((b) =>
      [b.title, b.author, b.publisher, b.subject, b.board]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [source.libraryBooks, source.librarySearch]);

  const chapters = source.selectedBook?.chapters || [];

  return (
    <div className="space-y-3">
      {/* Mode tabs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid={`${testIdPrefix}-source-modes`}>
        {[
          { id: 'library', label: t('teacher.sourceModes.libraryLabel'), icon: BookOpen },
          { id: 'upload', label: t('teacher.sourceModes.uploadLabel'), icon: ArrowUp },
          { id: 'topic', label: t('teacher.sourceModes.topicLabel'), icon: Wand2 },
          { id: 'capture', label: 'BOSCO Capture™', icon: Camera, isCapture: true },
        ].map((m) => {
          const Icon = m.icon;
          const active = source.sourceMode === m.id;

          // BOSCO Capture (Phase 1) — opens new flow or upsell
          if (m.isCapture) {
            const enabled = captureAccess?.data?.enabled;
            return (
              <button
                key={m.id}
                data-testid={`${testIdPrefix}-source-capture`}
                onClick={() => {
                  if (captureAccess.loading) return;
                  if (enabled) {
                    trackClarityEvent?.('bosco_capture_open_from_source', { from: 'quickgen-source' });
                    if (typeof window !== 'undefined') window.location.assign('/bosco-capture');
                  } else {
                    trackClarityEvent?.('bosco_capture_upsell_open', { tier: captureAccess?.data?.tier, from: 'quickgen-source' });
                    setCaptureUpsellOpen(true);
                  }
                }}
                className="relative flex items-center gap-2 p-2 rounded-lg border border-emerald-300/70 dark:border-emerald-700/50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/30 dark:to-slate-900 text-xs font-medium hover:border-emerald-500 transition-all overflow-hidden"
              >
                <span
                  data-testid={`${testIdPrefix}-source-capture-beta-crown`}
                  className="absolute -top-1 -right-1 inline-flex items-center gap-0.5 px-1 py-0.5 rounded-bl-md rounded-tr-md bg-amber-400 text-amber-950 text-[8px] font-bold uppercase tracking-wider"
                >
                  <Crown className="h-2 w-2" /> Beta
                </span>
                <Icon className="h-3.5 w-3.5 text-emerald-600" />
                <span className="truncate">
                  BOSCO Capture™
                  {!enabled && !captureAccess.loading && <Lock className="inline-block h-3 w-3 ml-1 text-slate-400" />}
                </span>
              </button>
            );
          }

          return (
            <button
              key={m.id}
              data-testid={`${testIdPrefix}-source-${m.id}`}
              onClick={() => source.setSourceMode(m.id)}
              className={cn(
                'flex items-center gap-2 p-2 rounded-lg border text-xs font-medium transition-all',
                active
                  ? 'border-slate-900 dark:border-white bg-slate-50 dark:bg-slate-800'
                  : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 bg-white dark:bg-slate-900'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {m.label}
            </button>
          );
        })}
      </div>

      {/* BOSCO Capture upsell modal (Phase 1) */}
      <BoscoCaptureUpsell
        open={captureUpsellOpen}
        onClose={() => setCaptureUpsellOpen(false)}
        tier={captureAccess?.data?.tier}
        isIndividual={captureAccess?.data?.isIndividual ?? true}
      />

      {source.sourceMode === 'library' && (
        <div className="space-y-2.5" data-testid={`${testIdPrefix}-library-panel`}>
          {/* Task C2 — Category quick-filter tabs (ALL / NCERT / INTERNATIONAL) with live counts */}
          <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testIdPrefix}-lib-category-tabs`}>
            {[
              { id: 'all',           label: 'ALL BOOKS',     match: () => true },
              { id: 'ncert',         label: 'NCERT',         match: (b) => b.board === 'cbse' },
              { id: 'international', label: 'INTERNATIONAL', match: (b) => ['icse', 'igcse', 'ib'].includes(b.board) || (b.publisher || '').toLowerCase().includes('gutenberg') },
            ].map((cat) => {
              const count = (source.libraryBooks || []).filter(cat.match).length;
              const isActive =
                cat.id === 'all' ? source.libBoard === '' :
                cat.id === 'ncert' ? source.libBoard === 'cbse' :
                ['icse', 'igcse', 'ib'].includes(source.libBoard);
              return (
                <button
                  key={cat.id}
                  data-testid={`${testIdPrefix}-lib-cat-${cat.id}`}
                  onClick={() => {
                    if (cat.id === 'all') source.setLibBoard('');
                    else if (cat.id === 'ncert') source.setLibBoard('cbse');
                    else source.setLibBoard('igcse');
                  }}
                  className={cn(
                    'h-7 px-2.5 rounded-full text-[10px] font-bold tracking-wider border transition-colors inline-flex items-center gap-1.5',
                    isActive
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'
                      : 'border-slate-200 dark:border-slate-800 hover:border-slate-400 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300'
                  )}
                >
                  {cat.label}
                  <span className={cn(
                    'inline-flex items-center justify-center min-w-[20px] h-4 px-1 rounded-full text-[9px] font-bold',
                    isActive
                      ? 'bg-white/20 dark:bg-slate-900/30 text-white dark:text-slate-900'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                  )} data-testid={`${testIdPrefix}-lib-cat-${cat.id}-count`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Select value={source.libBoard || 'all'} onValueChange={(v) => source.setLibBoard(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid={`${testIdPrefix}-lib-board`} className="h-8 text-xs"><SelectValue placeholder="Board" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All boards</SelectItem>
                {BOARDS.map((b) => <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={source.libClass || 'all'} onValueChange={(v) => source.setLibClass(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid={`${testIdPrefix}-lib-class`} className="h-8 text-xs"><SelectValue placeholder="Class" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classes</SelectItem>
                {CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={source.libSubject || 'all'} onValueChange={(v) => source.setLibSubject(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid={`${testIdPrefix}-lib-subject`} className="h-8 text-xs"><SelectValue placeholder="Subject" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {SUBJECTS.concat(['English Literature']).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              data-testid={`${testIdPrefix}-lib-search`}
              placeholder="Search title / author…"
              value={source.librarySearch}
              onChange={(e) => source.setLibrarySearch(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div className="text-[10px] text-muted-foreground">
            {filteredBooks.length} of {source.libraryBooks.length} books
            {(source.libBoard || source.libClass || source.libSubject || source.librarySearch) && (
              <button
                onClick={() => { source.setLibBoard(''); source.setLibClass(''); source.setLibSubject(''); source.setLibrarySearch(''); }}
                className="ml-2 underline hover:text-foreground"
              >
                Clear
              </button>
            )}
          </div>
          <div
            data-testid={`${testIdPrefix}-book-grid`}
            className="max-h-64 overflow-y-auto pr-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
          >
            {filteredBooks.length === 0 ? (
              <div className="col-span-full text-[11px] text-muted-foreground italic p-4 text-center border border-dashed rounded-lg">
                No books match — try clearing filters.
              </div>
            ) : (
              filteredBooks.map((b) => {
                const active = b.bookId === source.selectedBookId;
                return (
                  <button
                    key={b.bookId}
                    data-testid={`${testIdPrefix}-book-${b.bookId}`}
                    onClick={() => { source.setSelectedBookId(b.bookId); source.setSelectedChapterNumbers([]); }}
                    className={cn(
                      'text-left p-2.5 rounded-lg border transition-all',
                      active
                        ? 'border-emerald-500 bg-emerald-50/40 ring-2 ring-emerald-500/30'
                        : 'border-slate-200/70 dark:border-slate-800 hover:border-slate-400 bg-white dark:bg-slate-900'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[11px] font-semibold line-clamp-2 leading-snug">{b.title}</div>
                      {active && <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />}
                    </div>
                    <div className="text-[9px] text-muted-foreground">{b.author || b.publisher || '—'}</div>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <Badge variant="secondary" className="text-[9px] px-1 py-0">{b.board?.toUpperCase()}</Badge>
                      <Badge variant="secondary" className="text-[9px] px-1 py-0">Cls {b.classId}</Badge>
                      {b.source_type === 'NCERT' && <Badge className="bg-emerald-100 text-emerald-800 text-[9px] px-1 py-0 border-0">NCERT</Badge>}
                      {b.source_type === 'Gutenberg' && <Badge className="bg-amber-100 text-amber-800 text-[9px] px-1 py-0 border-0">Classic</Badge>}
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {source.selectedBook && chapters.length > 0 && (
            <ChapterMultiSelect
              chapters={chapters}
              selected={source.selectedChapterNumbers}
              onToggle={source.toggleChapter}
              onSelectAll={() => source.setSelectedChapterNumbers(chapters.map((c) => c.number))}
              onClear={() => source.setSelectedChapterNumbers([])}
              multiMode={source.multiChapterMode}
              setMultiMode={source.setMultiChapterMode}
              testId={`${testIdPrefix}-chapter-multi`}
            />
          )}
        </div>
      )}

      {source.sourceMode === 'upload' && (
        <div className="space-y-2.5" data-testid={`${testIdPrefix}-upload-panel`}>
          <label className="flex flex-col items-center justify-center gap-2 p-6 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-slate-400 cursor-pointer transition-colors">
            <input
              data-testid={`${testIdPrefix}-upload-input`}
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && source.handleUploadBook(e.target.files[0], { board, classId, subject })}
              disabled={source.uploading}
            />
            {source.uploading ? (
              <><Loader2 className="h-5 w-5 animate-spin" /><span className="text-sm">Extracting chapters…</span></>
            ) : source.uploadedBook ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <span className="text-sm font-semibold">{source.uploadedBook.title}</span>
                <span className="text-xs text-muted-foreground">
                  {source.uploadedBook.chapters?.length || 0} chapter(s){source.uploadedBook.pageCount ? ` • ${source.uploadedBook.pageCount} pages` : ''}
                </span>
                <span className="text-[11px] text-slate-500 underline">Click to replace</span>
              </>
            ) : (
              <>
                <ArrowUp className="h-5 w-5 text-slate-500" />
                <span className="text-sm font-semibold">Upload PDF</span>
                <span className="text-[11px] text-muted-foreground">Max 25 MB</span>
                <span className="text-[11px] text-amber-700 dark:text-amber-400 font-medium">💡 For best results, upload one chapter at a time.</span>
              </>
            )}
          </label>
          {source.uploadedBook?.chapters?.length > 0 && (
            <ChapterMultiSelect
              chapters={source.uploadedBook.chapters}
              selected={source.selectedChapterNumbers}
              onToggle={source.toggleChapter}
              onSelectAll={() => source.setSelectedChapterNumbers(source.uploadedBook.chapters.map((c) => c.number))}
              onClear={() => source.setSelectedChapterNumbers([])}
              multiMode={source.multiChapterMode}
              setMultiMode={source.setMultiChapterMode}
              testId={`${testIdPrefix}-upload-chapter-multi`}
            />
          )}
        </div>
      )}

      {source.sourceMode === 'topic' && (
        <Input
          data-testid={`${testIdPrefix}-topic-input`}
          placeholder="Enter chapter / topic name (e.g. Real Numbers, Photosynthesis)"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className="h-9 text-sm"
        />
      )}
    </div>
  );
}

function BatchResultList({ batch, testIdPrefix, access }) {
  // Task A2 — Group results by chapter when backend ran in per-chapter mode.
  const perChapter = batch.multiChapterMode === 'per_chapter';
  const partialFailures = Array.isArray(batch.partialFailures) ? batch.partialFailures : [];
  // Iter 68 — Hide .pptx download for tiers without pptxExport.
  const pptxAllowed = access?.featureFlags?.pptxExport !== false;
  const groups = perChapter
    ? batch.results.reduce((acc, r) => {
        const key = r.chapterNumber != null ? `Ch ${r.chapterNumber}: ${r.chapterTitle || ''}` : 'Other';
        (acc[key] = acc[key] || []).push(r);
        return acc;
      }, {})
    : null;

  const renderItem = (r) => (
    <div
      key={r.assetId}
      className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900"
    >
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold truncate capitalize">{r.docType.replace('_', ' ')}</div>
        <div className="text-[10px] text-muted-foreground truncate">{r.title}</div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {r.downloadFormats.filter((fmt) => fmt !== 'pptx' || pptxAllowed).map((fmt) => (
          <button
            key={fmt}
            data-testid={`${testIdPrefix}-dl-${r.docType}-${fmt}${r.chapterNumber != null ? `-ch${r.chapterNumber}` : ''}`}
            onClick={async () => {
              try {
                await contentEngineApi.downloadAsset(fmt, r.assetId, `${r.docType}.${fmt}`);
              } catch (err) {
                toast.error(err.message || 'Download failed');
              }
            }}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-900 text-white hover:bg-slate-700 dark:bg-white dark:text-slate-900 transition-colors"
          >
            <Download className="h-2.5 w-2.5" /> .{fmt}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800"
    >
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
        {batch.results.length} document{batch.results.length === 1 ? '' : 's'} ready
        {perChapter && (
          <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider">
            Per chapter
          </span>
        )}
      </div>
      {partialFailures.length > 0 && (
        <div
          data-testid={`${testIdPrefix}-partial-failures`}
          className="mb-3 rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/50 p-2.5"
        >
          <div className="text-[11px] font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" />
            {partialFailures.length} item{partialFailures.length === 1 ? '' : 's'} failed (others succeeded)
          </div>
          <ul className="mt-1.5 ml-5 list-disc text-[10px] text-amber-700 dark:text-amber-300/80 space-y-0.5">
            {partialFailures.slice(0, 4).map((f, i) => (
              <li key={i}>
                {f.chapterNumber != null ? `Ch ${f.chapterNumber} · ` : ''}
                {f.docType}: {String(f.error || 'Unknown error').slice(0, 90)}
              </li>
            ))}
          </ul>
          <div className="mt-1.5 text-[10px] text-amber-700/80 dark:text-amber-300/60">
            Tip: try fewer chapters at once, or switch to <strong>Combined</strong> mode.
          </div>
        </div>
      )}
      {groups ? (
        <div className="space-y-3" data-testid={`${testIdPrefix}-results`}>
          {Object.entries(groups).map(([chKey, items]) => (
            <div key={chKey}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">{chKey}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {items.map(renderItem)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`${testIdPrefix}-results`}>
          {batch.results.map(renderItem)}
        </div>
      )}
    </motion.div>
  );
}


// ─────────────────────────────────────────────────────────────
// UpgradeDialog — listens to global `ce:upgrade-required` events.
// Renders a 3-tier comparison + Razorpay checkout. Mock mode auto-completes.
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// ManageSubscriptionDialog — Auto-Renew toggle + Cancel Anytime
// (Razorpay-ready: backend already structured to call rzp.subscriptions.*)
// ─────────────────────────────────────────────────────────────
// Per-doc retail estimate (₹) used to compute the "you saved" ribbon — based on
// what comparable AI doc-generation tools charge per single-purchase document.
const RETAIL_PER_DOC_INR = 49;
const TIER_PRICE_INR = { free: 0, starter: 299, pro: 499, elite: 999 };

function ManageSubscriptionDialog() {
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState(null);
  const [access, setAccess] = useState(null);
  const [busy, setBusy] = useState(false);
  // True ONLY when Razorpay keys are not configured on backend — we pull the
  // truth from /billing/plans so the UI never lies about payment status.
  const [mockMode, setMockMode] = useState(false);

  useEffect(() => {
    const handler = () => {
      setOpen(true);
      contentEngineApi.getMySubscription().then(setSub).catch(() => setSub(null));
      contentEngineApi.getAccess().then(setAccess).catch(() => setAccess(null));
      contentEngineApi.listPlans().then((d) => setMockMode(Boolean(d?.mockMode))).catch(() => setMockMode(false));
    };
    window.addEventListener('ce:open-manage-subscription', handler);
    return () => window.removeEventListener('ce:open-manage-subscription', handler);
  }, []);

  const refresh = async () => {
    try { setSub(await contentEngineApi.getMySubscription()); } catch {/* */}
    try { setAccess(await contentEngineApi.getAccess()); } catch {/* */}
  };

  const handleToggleAutoRenew = async (next) => {
    setBusy(true);
    try {
      await contentEngineApi.toggleAutoRenew(next);
      toast.success(next ? 'Auto-renew enabled' : 'Auto-renew disabled');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel subscription at the end of the current billing period? You keep full access until then.')) return;
    setBusy(true);
    try {
      const res = await contentEngineApi.cancelSubscription();
      toast.success(res.message || 'Subscription cancelled at period end.');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async () => {
    setBusy(true);
    try {
      await contentEngineApi.reactivateSubscription();
      toast.success('Subscription reactivated.');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Reactivation failed');
    } finally {
      setBusy(false);
    }
  };

  if (!sub || sub.tier === 'free') {
    if (!open) return null;
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="manage-subscription-dialog">
          <DialogHeader>
            <DialogTitle>Manage subscription</DialogTitle>
            <DialogDescription>You are on the Free plan — nothing to manage.</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const isCancelled = sub.status === 'cancelled_at_period_end';
  const expiresHuman = (() => {
    if (!sub.expiresAt) return '—';
    try { return new Date(sub.expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return sub.expiresAt; }
  })();

  // "You saved ₹X by upgrading" — only show when meaningful (>0)
  const usage = access?.usage || {};
  const docsThisMonth =
    (usage.questionPapersGenerated || 0)
    + (usage.worksheetsGenerated || 0)
    + (usage.pptGenerated || 0)
    + (usage.lessonKitsGenerated || 0);
  const planPrice = TIER_PRICE_INR[sub.tier] ?? 0;
  const savings = Math.max(0, docsThisMonth * RETAIL_PER_DOC_INR - planPrice);
  const showSavings = savings > 0 && docsThisMonth > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="manage-subscription-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-500" /> Manage subscription
          </DialogTitle>
          <DialogDescription>
            You are on the <strong className="capitalize text-foreground">{sub.tier}</strong> plan.
            {isCancelled
              ? ` Pending cancellation — access until ${expiresHuman}.`
              : ` Renews on ${expiresHuman}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 space-y-4">
          {/* Savings ribbon — nudges retention by quantifying value vs pay-per-doc */}
          {showSavings && (
            <div
              data-testid="manage-savings-ribbon"
              className="relative overflow-hidden rounded-lg border border-emerald-200 dark:border-emerald-900/60 bg-gradient-to-r from-emerald-50 to-emerald-100/40 dark:from-emerald-950/40 dark:to-emerald-900/10 px-4 py-3"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                  <Crown className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                    You saved <span data-testid="manage-savings-amount">₹{savings.toLocaleString('en-IN')}</span> this month by being on {sub.tier.toUpperCase()}
                  </div>
                  <div className="mt-0.5 text-xs text-emerald-800/80 dark:text-emerald-300/80">
                    {docsThisMonth} doc{docsThisMonth === 1 ? '' : 's'} generated × ₹{RETAIL_PER_DOC_INR} pay-per-doc rate − ₹{planPrice} plan = <strong>₹{savings.toLocaleString('en-IN')} saved</strong>.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Auto-renew row */}
          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 p-3">
            <div>
              <div className="text-sm font-semibold">Auto-renew</div>
              <div className="text-xs text-muted-foreground">
                {sub.autoRenew ? 'Your plan will renew automatically.' : 'Your plan will not renew.'}
              </div>
            </div>
            <button
              data-testid="manage-auto-renew-toggle"
              onClick={() => handleToggleAutoRenew(!sub.autoRenew)}
              disabled={busy || isCancelled}
              role="switch"
              aria-checked={!!sub.autoRenew}
              className={cn(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                sub.autoRenew ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700',
                (busy || isCancelled) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span
                className={cn(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  sub.autoRenew ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </div>

          {/* Cancel / reactivate row */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
            {!isCancelled ? (
              <>
                <div className="text-sm font-semibold">Cancel anytime</div>
                <div className="text-xs text-muted-foreground mb-2">
                  You'll keep {sub.tier.toUpperCase()} access until {expiresHuman}. Reactivate any time before that.
                </div>
                <Button
                  data-testid="manage-cancel-btn"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={busy}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  Cancel subscription
                </Button>
              </>
            ) : (
              <>
                <div className="text-sm font-semibold text-amber-700 dark:text-amber-400">Cancellation pending</div>
                <div className="text-xs text-muted-foreground mb-2">
                  Your {sub.tier.toUpperCase()} access ends on {expiresHuman}. Change your mind?
                </div>
                <Button
                  data-testid="manage-reactivate-btn"
                  onClick={handleReactivate}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Reactivate
                </Button>
              </>
            )}
          </div>

          {mockMode ? (
            <div className="text-[10px] text-muted-foreground italic" data-testid="razorpay-mock-note">
              Razorpay keys aren't configured on this environment — toggles and cancellation are recorded in your account and will sync automatically when live payment keys are added.
            </div>
          ) : (
            <div className="text-[10px] text-emerald-700 dark:text-emerald-400 italic flex items-center gap-1" data-testid="razorpay-live-note">
              <Check className="h-3 w-3" /> Payments are live via Razorpay — any tier change triggers the secure checkout.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UpgradeDialog() {
  const { currentUser } = useApp();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const [plans, setPlans] = useState([]);
  const [currentTier, setCurrentTier] = useState('free');
  const [isSchool, setIsSchool] = useState(false);
  const [mockMode, setMockMode] = useState(true);
  const [razorpayKeyId, setRazorpayKeyId] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setReason(e.detail || null);
      setOpen(true);
      // If caller specified a plan (e.g. `plan: 'elite'` from BOSCO Capture
      // upsell), auto-launch that checkout — one click for the user.
      const targetPlan = e?.detail?.plan;
      if (targetPlan) {
        // Delay so plans have time to load before we launch checkout.
        setTimeout(() => {
          onUpgradeRef.current?.(targetPlan);
        }, 400);
      } else {
        setPreview(null);
        setCheckout(null);
      }
    };
    window.addEventListener('ce:upgrade-required', handler);

    // Also honour a URL query-param entry point — links from BOSCO Capture
    // (or anywhere else) can deep-link straight into Razorpay checkout for a
    // given plan without hunting through the Preferences UI.
    try {
      const params = new URLSearchParams(window.location.search);
      let urlPlan = params.get('upgrade');
      // Homepage pricing CTA → signup/login → here: carry the chosen plan into checkout.
      try {
        const pending = sessionStorage.getItem('ae_pending_plan');
        if (!urlPlan && pending) urlPlan = pending;
        sessionStorage.removeItem('ae_pending_plan');
      } catch { /* storage unavailable */ }
      if (params.get('plans') === '1' && !urlPlan) {
        setOpen(true);
        params.delete('plans');
        const q0 = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (q0 ? `?${q0}` : ''));
      }
      if (urlPlan && ['starter', 'pro', 'elite'].includes(urlPlan)) {
        setReason({ reason: params.get('reason') || 'deep-link', plan: urlPlan });
        setOpen(true);
        setTimeout(() => onUpgradeRef.current?.(urlPlan), 500);
        // Clean the URL so a page refresh doesn't re-trigger.
        params.delete('upgrade');
        params.delete('reason');
        const q = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
      }
    } catch { /* window/URLSearchParams unavailable — silent no-op */ }

    return () => window.removeEventListener('ce:upgrade-required', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ref so the event handler can call the latest onUpgrade without
  // stale-closure issues.
  const onUpgradeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    contentEngineApi
      .listPlans()
      .then((d) => {
        setPlans((d.plans || []).filter((p) => p.id !== 'free'));
        setCurrentTier(d.currentTier || 'free');
        setIsSchool(Boolean(d.isSchoolTeacher));
        setMockMode(Boolean(d.mockMode));
        setRazorpayKeyId(d.razorpayKeyId || null);
        setGstStates(d.gstStates || []);
        setBuyerState((prev) => prev || d.lastBuyer?.buyerState || '');
        setBusiness((prev) => (prev.gstin || prev.name ? prev : {
          enabled: Boolean(d.lastBuyer?.buyerGstin),
          gstin: d.lastBuyer?.buyerGstin || '',
          name: d.lastBuyer?.buyerBusinessName || '',
        }));
      })
      .catch(() => {});
  }, [open]);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const [preview, setPreview] = useState(null);
  const [gstStates, setGstStates] = useState([]);
  const [buyerState, setBuyerState] = useState('');
  const [business, setBusiness] = useState({ enabled: false, gstin: '', name: '' });
  const [checkout, setCheckout] = useState(null); // { status: 'verifying'|'success'|'error', ... }

  const onPlanSelected = async (planId) => {
    setBusyPlan(planId);
    setCheckout(null);
    try {
      setPreview(await contentEngineApi.previewPlanChange(planId));
    } catch (err) {
      toast.error(err.message || 'Could not load plan details');
    } finally {
      setBusyPlan(null);
    }
  };

  const activate = async (planId, order, resp) => {
    setCheckout({ status: 'verifying' });
    setOpen(true);
    try {
      const data = await contentEngineApi.verifyPayment({
        planId,
        razorpayOrderId: resp.razorpay_order_id,
        razorpayPaymentId: resp.razorpay_payment_id,
        razorpaySignature: resp.razorpay_signature,
      });
      if (!order.mockMode) {
        trackSubscriptionStarted({
          planId,
          amountPaise: order.amount,
          currency: order.currency || 'INR',
          txnId: resp.razorpay_payment_id,
        });
      }
      setCheckout({ status: 'success', data });
      setPreview(null);
      if (!data.scheduled) setCurrentTier(planId);
      window.dispatchEvent(new CustomEvent('ce:access-refresh'));
      window.dispatchEvent(new CustomEvent('ce:subscription-changed', { detail: { tier: data.scheduled ? currentTier : planId } }));
    } catch (err) {
      setCheckout({
        status: 'error',
        message: err.message || 'Verification failed',
        paymentId: resp.razorpay_payment_id,
        retry: () => activate(planId, order, resp),
      });
    }
  };

  const onUpgrade = async (planId) => {
    setBusyPlan(planId);
    try {
      const order = await contentEngineApi.createSubscription(planId, buyerState, business.enabled ? business : {});
      if (order.mockMode || (!razorpayKeyId && !window.Razorpay)) {
        await activate(planId, order, {
          razorpay_order_id: order.orderId,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: 'mock_signature',
        });
        return;
      }
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error('Payment gateway unavailable. Check your connection and try again.');
        return;
      }
      // Close our modal first — its focus-trap / pointer-events lock would
      // otherwise block every field inside the Razorpay checkout iframe.
      setOpen(false);
      await new Promise((r) => setTimeout(r, 350));
      document.body.style.pointerEvents = '';
      const rzp = new window.Razorpay({
        key: order.razorpayKeyId,
        amount: order.amount,
        currency: order.currency,
        name: 'Actanon Technologies',
        image: `${window.location.origin}/actanon-brand.png`,
        description: `ActanonEdu ${planId.charAt(0).toUpperCase() + planId.slice(1)} — monthly`,
        order_id: order.orderId,
        prefill: {
          name: currentUser?.name || '',
          email: currentUser?.email || '',
          contact: currentUser?.phone || currentUser?.contactNumber || '',
        },
        notes: { planId },
        handler: (resp) => activate(planId, order, resp),
        modal: {
          ondismiss: () => {
            toast.info('Checkout closed — no payment was taken.');
            setOpen(true);
          },
          confirm_close: true,
        },
        retry: { enabled: true, max_count: 3 },
        theme: { color: '#4568F5' },
      });
      rzp.on('payment.failed', (resp) => {
        toast.error(resp?.error?.description || 'Payment failed — please try another method.');
      });
      rzp.open();
    } catch (err) {
      toast.error(err.message || 'Could not start checkout');
    } finally {
      setBusyPlan(null);
    }
  };

  // Keep the ref up-to-date so the ce:upgrade-required listener can call
  // the latest onUpgrade without hitting a stale closure.
  useEffect(() => {
    onUpgradeRef.current = onPlanSelected;
  });

  // Determine plan ordering for directional Upgrade/Downgrade labels.
  const TIER_RANK = { free: 0, starter: 1, pro: 2, elite: 3 };
  const currentRank = TIER_RANK[currentTier] ?? 0;
  // Header copy — "Change plan" when user is already on a paid plan and not on elite.
  const isPaidNonElite = !isSchool && currentTier !== 'free' && currentTier !== 'elite';
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="upgrade-dialog">
        {checkout ? (
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>Checkout</DialogTitle>
              <DialogDescription>Payment status</DialogDescription>
            </DialogHeader>
            <CheckoutResult
              result={checkout}
              onRetry={() => checkout.retry?.()}
              onClose={() => { setOpen(false); setCheckout(null); }}
            />
          </>
        ) : preview ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Crown className="h-5 w-5 text-ae-warning" /> Review your plan change</DialogTitle>
              <DialogDescription>Here's exactly what changes before you pay.</DialogDescription>
            </DialogHeader>
            <PlanChangePreview
              preview={preview}
              busy={busyPlan === preview.toTier}
              onBack={() => setPreview(null)}
              onConfirm={() => onUpgrade(preview.toTier)}
              states={gstStates}
              buyerState={buyerState}
              setBuyerState={setBuyerState}
              business={business}
              setBusiness={setBusiness}
            />
          </>
        ) : (<>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            {isSchool
              ? 'Provided by your school'
              : currentTier === 'elite'
              ? "You're on the top plan"
              : isPaidNonElite
              ? 'Change plan'
              : 'Upgrade your plan'}
          </DialogTitle>
          <DialogDescription>
            {isSchool
              ? 'Your school provides Pro access — no upgrade needed.'
              : currentTier === 'elite'
              ? 'You already have access to every Content Engine feature. There is nothing higher to upgrade to — thanks for being on Elite!'
              : isPaidNonElite
              ? `You're currently on ${currentTier.toUpperCase()}. Switch to a higher or lower plan anytime — no lock-in.`
              : reason?.message || 'Get more out of the Content Engine — pick a plan.'}
          </DialogDescription>
        </DialogHeader>
        {currentTier === 'elite' && !isSchool && (
          <div data-testid="elite-notice" className="mt-2 rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-900/10 p-5 text-center">
            <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-amber-500/20 mb-2">
              <Crown className="h-5 w-5 text-amber-600" />
            </div>
            <div className="text-sm font-semibold mb-1">Elite plan — full access</div>
            <div className="text-xs text-muted-foreground max-w-md mx-auto">
              Bulk paper generation, syllabus planner, exam blueprint, parent communication and priority processing are all already enabled for you.
            </div>
            <div className="mt-3 text-[11px] text-amber-700 dark:text-amber-400 max-w-md mx-auto">
              Switch to Pro or Starter anytime — the new plan starts when your prepaid Elite period ends. Cancel from <strong>Manage subscription</strong> — no lock-in.
            </div>
            <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
              {['pro', 'starter'].map((pid) => (
                <Button
                  key={pid}
                  data-testid={`elite-switch-${pid}`}
                  variant="outline"
                  disabled={busyPlan === pid}
                  onClick={() => onPlanSelected(pid)}
                >
                  {busyPlan === pid ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                  Switch to {pid === 'pro' ? 'Pro' : 'Starter'}
                </Button>
              ))}
              <Button
                data-testid="elite-manage-btn"
                onClick={() => {
                  setOpen(false);
                  window.dispatchEvent(new CustomEvent('ce:open-manage-subscription'));
                }}
                variant="outline"
              >
                Manage subscription
              </Button>
              <Button
                data-testid="elite-close-btn"
                onClick={() => setOpen(false)}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                Got it
              </Button>
            </div>
          </div>
        )}
        {!isSchool && currentTier !== 'elite' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4" data-testid="upgrade-plans">
            {plans.map((p) => {
              const isCurrent = p.id === currentTier;
              const planRank = TIER_RANK[p.id] ?? 0;
              const isHigher = planRank > currentRank;
              const isLower = planRank < currentRank && p.id !== 'free';
              const recommended = reason?.suggestedTier === p.id || (!reason && p.id === 'pro' && currentTier === 'free');
              return (
                <div
                  key={p.id}
                  data-testid={`plan-card-${p.id}`}
                  className={cn(
                    'rounded-xl border p-4 flex flex-col bg-white dark:bg-slate-900 transition-all',
                    isCurrent
                      ? 'border-slate-400 ring-2 ring-slate-300/40'
                      : recommended
                      ? 'border-emerald-500 ring-2 ring-emerald-500/20 shadow-lg'
                      : 'border-slate-200/70 dark:border-slate-800',
                  )}
                >
                  {recommended && !isCurrent && (
                    <Badge className="mb-2 self-start bg-emerald-500 text-white border-0 text-[10px]">
                      Recommended
                    </Badge>
                  )}
                  {isCurrent && (
                    <Badge className="mb-2 self-start bg-slate-700 text-white border-0 text-[10px]">
                      Current Plan
                    </Badge>
                  )}
                  <div className="text-base font-heading font-semibold capitalize">{p.name}</div>
                  <div className="mt-1 mb-3">
                    <span className="text-2xl font-bold">₹{p.price}</span>
                    <span className="text-xs text-muted-foreground"> / {p.interval}</span>
                  </div>
                  <ul className="text-[11px] space-y-1 text-muted-foreground flex-1 mb-3">
                    {(p.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <Check className="h-3 w-3 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    data-testid={`plan-upgrade-${p.id}`}
                    onClick={() => onPlanSelected(p.id)}
                    disabled={isCurrent || busyPlan === p.id}
                    className={cn(
                      'w-full',
                      recommended && !isCurrent
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white'
                        : isLower
                        ? 'border-amber-400 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                        : '',
                    )}
                    variant={recommended && !isCurrent ? 'default' : 'outline'}
                  >
                    {busyPlan === p.id ? (
                      <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Processing…</>
                    ) : isCurrent ? (
                      'Current Plan'
                    ) : isHigher ? (
                      <>Upgrade to {p.name}</>
                    ) : (
                      <>Downgrade to {p.name}</>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        {/* Razorpay trust strip — only shown in LIVE mode, sits BELOW the plan
            grid so it never competes with the CTA. Single 11px line, no
            extra spacing for school users (they don't transact). */}
        {!isSchool && !mockMode && (
          <div
            className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[11px] text-muted-foreground"
            data-testid="razorpay-trust-strip"
          >
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3 w-3 text-emerald-600" />
              <span className="font-medium text-foreground/80">Secure checkout by Razorpay</span>
            </span>
            <span className="hidden sm:inline text-slate-300 dark:text-slate-700">•</span>
            <span className="inline-flex items-center gap-1.5">
              Cards · UPI · Netbanking · Wallets
            </span>
          </div>
        )}
        {isSchool && (
          <div className="text-center py-8" data-testid="school-notice">
            <Badge className="bg-emerald-100 text-emerald-800 border-0 text-xs">
              Provided by your school
            </Badge>
            <div className="text-sm mt-3 text-muted-foreground">
              All Pro features are unlocked for you. Contact your school admin for any questions.
            </div>
          </div>
        )}
        {mockMode && !isSchool && currentTier !== 'elite' && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-[11px] text-amber-800 dark:text-amber-300">
            <strong>Test mode:</strong> Razorpay keys aren't configured — clicking Upgrade auto-activates the plan for testing.
          </div>
        )}
        </>)}
      </DialogContent>
    </Dialog>
  );
}

